import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Check, X, Save, Users, Plus, Trash2 } from 'lucide-react';
import supabase from '../supabaseClient';
import ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';
import pptxgen from 'pptxgenjs';
import { toPng } from 'html-to-image';
import { apiPost } from '../../services/apiClient';
import { prepareChartData } from '../../functions/bayesianNPS';
import { useAuth, AuthContext } from '../AuthContext';

// Real chart components — rendered off-screen and captured via toPng
import NPSChart from '../NPSChart';
import PricingChart from '../PricingChart';
import PostAnalytics from '../PostAnalytics';
import PostLikesAnalytics from '../PostLikesAnalytics';
import LeaverJoinerPlot from '../LeaverJoinerPlot';
import SentimentAnalysisChart from '../SentimentAnalysisChart';
import LLMRecommendationsPage from '../LLMRecommendationsPage';
import LLMCompetitiveComparisonPage from '../LLMCompetitiveComparisonPage';
import LLMPositionChart from '../LLMPositionChart';
import LocationInsights, { locationDisplayName, locationStateOf } from '../LocationInsights';

// Recharts for sophisticated off-screen chart rendering
import {
    ComposedChart, Bar as RBar, BarChart as RBarChart, Line as RLine,
    XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend as RLegend,
    Cell, LabelList,
} from 'recharts';
import { format, parseISO } from 'date-fns';

// Chart.js — canvas-based fallback + NPS rendering
import {
    Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
    BarElement, BarController, LineController, DoughnutController, ArcElement,
    Title, Tooltip, Legend, Filler,
} from 'chart.js';
import '../../styles/ReportGenerator.css';

ChartJS.register(
    CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement,
    BarController, LineController, DoughnutController,
    Title, Tooltip, Legend, Filler,
);

// Brand colors — exact match to Scuttlebutt Research theme
const COLORS = {
    accent1: '1042C1',       // Primary brand blue (banners, highlights)
    overlay: '001848',       // Title slide dark overlay
    gray: 'ACACAC',          // Company label gray
    white: 'FFFFFF',
    black: '000000',
};

// Fetch an image from public/ and return raw base64 string
async function fetchImageAsBase64(url) {
    const resp = await fetch(url);
    const blob = await resp.blob();
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

// Fetch all brand assets (called once at start of generation)
async function fetchBrandAssets() {
    const [background, logoText, logoIcon] = await Promise.all([
        fetchImageAsBase64('/report-assets/background.jpg'),
        fetchImageAsBase64('/report-assets/logo-text.png'),
        fetchImageAsBase64('/report-assets/logo-icon.png'),
    ]);
    return { background, logoText, logoIcon };
}

// Available slide pages — each is an individual slide the user can include.
// Sentiment types (NPS, pricing, satisfaction_reason, etc.) are dynamic per topic
// and get expanded into separate matrix columns from topicTypes.
const AVAILABLE_SLIDES = [
    { id: 'executiveSummary', label: 'Executive Summary', group: 'Overview' },
    { id: 'npsCompetitive', label: 'NPS Competitive', group: 'NPS & Sentiment' },
    { id: 'sentimentBreakdown', label: 'Sentiment Breakdown', group: 'NPS & Sentiment', isDynamic: true },
    { id: 'leaverJoiner', label: 'Leaver / Joiner', group: 'Churn & Switching' },
    { id: 'llmRecommendations', label: 'LLM Analysis', group: 'LLM Recommendations' },
    { id: 'llmCompetitive', label: 'LLM Competitive Comparison', group: 'LLM Recommendations' },
    { id: 'locationMap', label: 'Location Map', group: 'Location Insights' },
    { id: 'locationStores', label: 'Top & Bottom Stores', group: 'Location Insights' },
    { id: 'postVolume', label: 'Post Volume', group: 'Engagement' },
    { id: 'postLikes', label: 'Post Likes', group: 'Engagement' },
    // Extras-driven deep-dive slides (deck-content `extras` contract). No
    // competitor/deep-dive mode config exists in REPORT_MODES, so these are
    // user-selectable but excluded from the default cell set (defaultOff).
    { id: 'billingTaxonomy', label: 'Billing Breakdown', group: 'Deep Dive', defaultOff: true },
    { id: 'locationExtremes', label: 'Top & Bottom Locations', group: 'Deep Dive', defaultOff: true },
    { id: 'volumeNpsOverlay', label: 'Volume × NPS', group: 'Deep Dive', defaultOff: true },
];

// Standardized analysis type labels for the matrix columns.
// Dynamic sentiment types get their label from this map; anything not here
// falls back to titleCase(type).
const ANALYSIS_TYPE_LABELS = {
    NPS: 'NPS',
    pricing: 'Pricing',
    satisfaction_reason: 'Satisfaction Reasons',
    dissatisfaction_reason: 'Dissatisfaction Reasons',
    usage_reason: 'Usage Reasons',
    joining: 'Joining Reasons',
    leaving: 'Leaving Reasons',
    interest_level: 'Interest Level',
    lead_source: 'Lead Source',
};

// Report timeframe modes — controls data queries, shift thresholds, and AI prompt focus
const REPORT_MODES = {
    weekly: {
        label: 'Weekly',
        description: 'Last 7 days',
        // Charts render with a 12-month window for context; analysis,
        // deltas, quotes, and the AI prompt remain focused on the 7-day
        // and 30-day picture as requested.
        npsMonthsBack: 12,
        sentimentDisplayMonths: 12,
        sentimentDataExtraMonths: 3,
        sentimentFrequency: 'day',
        postStartDaysBack: 30,
        // Quote candidates are constrained to the mode's reporting window so a
        // slide never shows quotes from outside the timeframe it represents.
        quotesWindowDays: 30,
        criticalMoveThreshold: 5,
        majorMoveThreshold: 3,
        deltaShortDays: 7,
        deltaLongDays: 30,
        deltaShortLabel: '7-Day',
        deltaLongLabel: '30-Day',
        promptSections: ['THIS WEEK', 'KEY CHANGES', 'EMERGING SIGNALS'],
        promptTimeframe: 'the past week',
        // LLM Recommendations slides default to a 10-day window in every mode:
        // the prompts are re-asked daily, so the current answer set is what
        // matters, not a long history.
        llmTimeRange: '10d',
        // Location slides aggregate monthly, so their window is in months and
        // scales with the mode (per-store ratings need volume to be stable).
        locationMonths: 3,
    },
    monthly: {
        label: 'Monthly',
        description: 'Last 30 days',
        npsMonthsBack: 6,
        sentimentDisplayMonths: 3,
        sentimentDataExtraMonths: 3,
        sentimentFrequency: 'day',
        postStartDaysBack: 90,
        quotesWindowDays: 30,
        criticalMoveThreshold: 8,
        majorMoveThreshold: 4,
        deltaShortDays: 7,
        deltaLongDays: 30,
        deltaShortLabel: '7-Day',
        deltaLongLabel: '30-Day',
        promptSections: ['MONTHLY TREND', 'KEY DEVELOPMENTS', 'RECENT MOVEMENT'],
        promptTimeframe: 'the past month',
        llmTimeRange: '10d',
        locationMonths: 6,
    },
    yearly: {
        label: 'Annual',
        description: 'Last 12 months',
        npsMonthsBack: 24,
        sentimentDisplayMonths: 12,
        sentimentDataExtraMonths: 12,
        sentimentFrequency: 'day',
        postStartDaysBack: 365,
        quotesWindowDays: 365,
        criticalMoveThreshold: 15,
        majorMoveThreshold: 5,
        deltaShortDays: 30,
        deltaLongDays: 365,
        deltaShortLabel: '30-Day',
        deltaLongLabel: '365-Day',
        promptSections: ['1-YEAR TREND', 'KEY DEVELOPMENTS', 'RECENT MOVEMENT'],
        promptTimeframe: 'the past year',
        llmTimeRange: '10d',
        locationMonths: 12,
    },
    twentyTwoMonth: {
        label: '22-Month',
        description: 'Last 22 months',
        // Matches the 22 Months option in CustomSentimentBreakdown so chart axes line up.
        npsMonthsBack: 30,
        sentimentDisplayMonths: 22,
        sentimentDataExtraMonths: 6,
        sentimentFrequency: 'day',
        postStartDaysBack: 670,             // ~22 * 30.4
        quotesWindowDays: 670,
        criticalMoveThreshold: 20,
        majorMoveThreshold: 8,
        deltaShortDays: 30,
        deltaLongDays: 670,
        deltaShortLabel: '30-Day',
        deltaLongLabel: '22-Month',
        promptSections: ['22-MONTH TREND', 'KEY DEVELOPMENTS', 'RECENT MOVEMENT'],
        promptTimeframe: 'the past 22 months',
        llmTimeRange: '10d',
        locationMonths: 22,
    },
};

// Day counts for the LLM pages' time-range keys (mirrors PERIOD_DAYS there).
const LLM_RANGE_DAYS = { '10d': 10, '30d': 30, '90d': 90, '6mo': 180, '1y': 365, all: 730 };

// Minimum scored answers (score slide) / open-answer opportunities (position
// slide) inside the chart window for an LLM slide to be worth shipping — a
// thin sample makes both the line and the exec numbers noise.
const MIN_LLM_SAMPLE = 100;

// Location slides: fewer qualifying stores than this and a top/bottom ranking
// (or a map) is meaningless, so the slides are skipped for the topic.
const MIN_LOCATION_STORES = 4;
// A store needs this many mappable peers before the map earns a slide
// (named-store-code topics have no coordinates at all).
const MIN_MAPPABLE_LOCATIONS = 3;
// Review excerpts sent to the LLM per store when writing highlight/lowlight
// lines, and the cap on each excerpt's length.
const STORE_EXCERPTS_PER_STORE = 8;
const STORE_EXCERPT_CHARS = 200;


// NPS chart colors
const NPS_CHART_COLORS = [
    '#0057FF', '#FF8A00', '#00A86B', '#7A1FFF', '#E63946', '#0891B2', '#CA8A04',
];

// Helper: title-case a snake_case string
function titleCase(str) {
    return str.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// Remove em dashes from generated copy — they read as machine-written.
// Replace with a comma and tidy the spacing. Hyphens (e.g. "13-point") and
// minus signs are left intact. Applied to every title, bullet, and narrative
// before it reaches a slide.
function stripEmDashes(text) {
    if (!text || typeof text !== 'string' || text.indexOf('—') === -1) {
        return text;
    }
    return text
        .replace(/\s*—\s*/g, ', ')
        .replace(/,\s*,/g, ', ')
        .replace(/\s+([,.;:!?])/g, '$1')
        .replace(/[ \t]{2,}/g, ' ')
        .trim();
}

// Helper: calculate quality metric score (0-100) from sentiment percentages
// Matches the formula in SentimentAnalysisChart / PricingChart
function calcQualityScore(point) {
    const promoters = (point.percentVeryPositive || 0) + ((point.percentModeratePositive || 0) * 0.5);
    const detractors = (point.percentVeryNegative || 0) + (point.percentModerateNegative || 0) + ((point.percentNeutral || 0) * 0.33);
    const rawNPS = promoters - detractors;
    return (rawNPS + 100) / 2;
}

// Helper: fetch NPS rows and normalise to { month, meanNPS, ... } format
function normaliseNpsRows(rows) {
    return (rows || []).map(row => ({
        month: row.month,
        meanNPS: row.nps,
        lowerCI: row.lower_ci,
        upperCI: row.upper_ci,
        count: row.sample_size,
        isForwardFilled: row.is_forward_filled,
    }));
}

// Aggregate daily sentiment-overview points (type=NPS) into ISO-week buckets.
// Input: [{ timestamp, totalPosts, percentVeryPositive, percentModeratePositive,
//           percentNeutral, percentModerateNegative, percentVeryNegative }]
// Output: [{ week: 'YYYY-MM-DD' (Monday), nps: -100..+100, sample_size }]
function aggregateNpsWeekly(daily) {
    if (!Array.isArray(daily) || daily.length === 0) return [];
    const byWeek = new Map();
    for (const d of daily) {
        const date = new Date(d.timestamp);
        const isoDay = date.getUTCDay() || 7; // Sun=0→7, Mon=1
        const monday = new Date(Date.UTC(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate() - (isoDay - 1),
        ));
        const key = monday.toISOString().slice(0, 10);
        if (!byWeek.has(key)) byWeek.set(key, []);
        byWeek.get(key).push(d);
    }
    return [...byWeek.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([week, points]) => {
            const avg = (k) => points.reduce((s, p) => s + (p[k] || 0), 0) / points.length;
            const vpos = avg('percentVeryPositive');
            const mpos = avg('percentModeratePositive');
            const neu = avg('percentNeutral');
            const mneg = avg('percentModerateNegative');
            const vneg = avg('percentVeryNegative');
            const promoters = vpos + mpos * 0.5;
            const detractors = vneg + mneg + neu * 0.33;
            const rawNPS = promoters - detractors;
            const totalCount = points.reduce((s, p) => s + (p.totalPosts || 0), 0);
            return { week, nps: rawNPS, sample_size: totalCount };
        });
}

// ------------------------------------------------------------------
// Render React element off-screen, wait, capture as PNG.
// Container uses opacity:0 so it's invisible but has real layout dimensions.
// For capture: canvas.toDataURL (Chart.js) or toPng with opacity override.
// ------------------------------------------------------------------
// Poll a container for a descendant matching `selector` that has real layout.
// Resolves the node, or null on timeout. Used to wait for a live component to
// finish fetching its data and render its chart before capture.
function waitForNode(container, selector, timeoutMs = 8000) {
    return new Promise(resolve => {
        const started = Date.now();
        const check = () => {
            const node = container.querySelector(selector);
            if (node && node.offsetWidth > 0 && node.offsetHeight > 0) {
                resolve(node);
                return;
            }
            if (Date.now() - started > timeoutMs) {
                resolve(node || null);
                return;
            }
            setTimeout(check, 200);
        };
        check();
    });
}

// Like waitForNode, but waits until at least `count` matching nodes have real
// layout — used when capturing a grid of charts that each fetch data async.
function waitForCount(container, selector, count, timeoutMs = 14000) {
    return new Promise(resolve => {
        const started = Date.now();
        const check = () => {
            let ready = 0;
            container.querySelectorAll(selector).forEach(n => {
                if (n.offsetWidth > 0 && n.offsetHeight > 0) ready++;
            });
            if (ready >= count || Date.now() - started > timeoutMs) {
                resolve(ready);
                return;
            }
            setTimeout(check, 250);
        };
        check();
    });
}

async function captureOffScreen(element, { width = 800, height = 450, delay = 2000, authValue = null, cropSelector = null, readySelector = null, readyCount = 0 } = {}) {
    const container = document.createElement('div');
    // opacity:0 keeps it invisible but browser computes real layout (unlike left:-9999px)
    container.style.cssText = `position:fixed;top:0;left:0;width:${width}px;height:${height}px;background:#fff;overflow:hidden;opacity:0;pointer-events:none;z-index:-1;`;
    document.body.appendChild(container);

    return new Promise((resolve, reject) => {
        const root = createRoot(container);
        const wrapped = authValue
            ? <AuthContext.Provider value={authValue}>{element}</AuthContext.Provider>
            : element;
        root.render(wrapped);

        setTimeout(async () => {
            try {
                // Capture via toPng for ALL chart types (Canvas + SVG)
                // Using toPng ensures consistent 800x450 output dimensions.
                // KEY FIX: toPng clones the DOM including styles — container has
                // opacity:0 so clone would be invisible. Set opacity:1 right before
                // capture so the clone renders visibly. User never sees it because
                // the capture is near-instant and container is behind page (z-index:-1).
                container.style.opacity = '1';

                // For a grid capture, wait until enough charts have rendered.
                if (readySelector && readyCount > 0) {
                    await waitForCount(container, readySelector, readyCount, 14000);
                }

                // When capturing a specific child (chart-only crop), wait for
                // that node to appear with real layout — live components fetch
                // their data async before the chart renders.
                let target = container;
                if (cropSelector) {
                    target = await waitForNode(container, cropSelector, 8000);
                    if (!target) {
                        root.unmount();
                        document.body.removeChild(container);
                        reject(new Error(`capture target "${cropSelector}" not found`));
                        return;
                    }
                }

                // A <canvas> (Chart.js) serializes most reliably via toDataURL.
                if (target.tagName === 'CANVAS') {
                    const canvasUrl = target.toDataURL('image/png', 1.0);
                    root.unmount();
                    document.body.removeChild(container);
                    if (canvasUrl && canvasUrl !== 'data:,' && canvasUrl.length > 500) {
                        resolve(canvasUrl);
                    } else {
                        reject(new Error('canvas.toDataURL returned blank image'));
                    }
                    return;
                }

                // html-to-image re-runs CSS animations on its DOM clone, so it
                // can rasterise an element mid-fade — e.g. a `fadeInUp` at its
                // opacity:0 start frame, leaving the chart/cards blank. Freeze
                // every animation + transition on the source subtree first.
                [target, ...target.querySelectorAll('*')].forEach(el => {
                    if (el && el.style) {
                        el.style.animation = 'none';
                        el.style.transition = 'none';
                    }
                });

                const toPngOpts = {
                    pixelRatio: 2,
                    backgroundColor: '#ffffff',
                    cacheBust: true,
                    // Skip cross-origin font embedding — prevents SecurityError
                    // from Google Fonts stylesheets. Chart fonts render fine as
                    // they're rasterized in the capture anyway.
                    skipFonts: true,
                };
                // No width/height passed to toPng on purpose: those options
                // resize the node, distorting its aspect ratio. We always
                // capture the node at its natural rendered size so images are
                // never stretched — the slide then scales them with
                // `sizing: contain`, which preserves aspect.

                // First call primes html-to-image's internal cache (known bug: first
                // call often returns blank). Result discarded.
                await toPng(target, toPngOpts).catch(() => {});

                // Second call produces the real image.
                const url = await toPng(target, toPngOpts);
                root.unmount();
                document.body.removeChild(container);

                if (url && url !== 'data:,' && url.length > 500) {
                    resolve(url);
                } else {
                    reject(new Error('toPng returned blank image'));
                }
            } catch (err) {
                try { root.unmount(); } catch (_) {}
                try { document.body.removeChild(container); } catch (_) {}
                reject(err);
            }
        }, delay);
    });
}

// ------------------------------------------------------------------
// Render a Chart.js config directly to canvas → base64 PNG.
// This is the PRIMARY rendering path for all non-NPS charts.
// canvas.toDataURL() is 100% reliable — no SVG capture issues.
// ------------------------------------------------------------------
function renderChartToBase64(config, width = 1920, height = 1080) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);

    const chart = new ChartJS(ctx, {
        ...config,
        options: {
            ...config.options,
            responsive: false,
            animation: false,
            devicePixelRatio: 1,
        },
        plugins: config.plugins || [],
    });

    const dataUrl = canvas.toDataURL('image/png', 1.0);
    chart.destroy();
    return toBase64(dataUrl);
}

// ------------------------------------------------------------------
// Chart.js config builders — professional, consultancy-style charts
// ------------------------------------------------------------------

function buildSentimentConfig(data) {
    // 5-category stacked area matching PricingChart colors
    const labels = data.map(d => {
        const date = new Date(d.timestamp);
        return `${date.toLocaleString('default', { month: 'short' })} ${date.getFullYear()}`;
    });
    return {
        type: 'line',
        data: {
            labels,
            datasets: [
                { label: 'Exceptional', data: data.map(d => d.percentVeryPositive), fill: true, backgroundColor: '#98D586', borderColor: '#669756', borderWidth: 1, tension: 0.3, pointRadius: 0 },
                { label: 'Good', data: data.map(d => d.percentModeratePositive), fill: true, backgroundColor: '#BED5BA', borderColor: '#849782', borderWidth: 1, tension: 0.3, pointRadius: 0 },
                { label: 'Average', data: data.map(d => d.percentNeutral), fill: true, backgroundColor: '#B7B7B7', borderColor: '#7D7D7D', borderWidth: 1, tension: 0.3, pointRadius: 0 },
                { label: 'Poor', data: data.map(d => d.percentModerateNegative), fill: true, backgroundColor: '#F6BBBA', borderColor: '#B68180', borderWidth: 1, tension: 0.3, pointRadius: 0 },
                { label: 'Very Bad', data: data.map(d => d.percentVeryNegative), fill: true, backgroundColor: '#F2857B', borderColor: '#B25147', borderWidth: 1, tension: 0.3, pointRadius: 0 },
            ],
        },
        options: {
            scales: {
                x: { grid: { color: '#F3F4F6' }, ticks: { maxTicksLimit: 8, font: { size: 12 }, color: '#6B7280' } },
                y: { stacked: true, max: 100, grid: { color: '#F3F4F6' }, ticks: { callback: v => v + '%', font: { size: 12 }, color: '#6B7280' } },
            },
            plugins: {
                legend: { position: 'top', labels: { font: { size: 12 }, usePointStyle: true, padding: 16 } },
            },
        },
    };
}

function buildPricingConfig(categories) {
    return {
        type: 'bar',
        data: {
            labels: categories.map(c => c.name),
            datasets: [{
                data: categories.map(c => c.value),
                backgroundColor: categories.map(c => c.fill),
                borderRadius: 4,
                barThickness: 40,
            }],
        },
        options: {
            indexAxis: 'y',
            layout: { padding: { right: 60 } },
            scales: {
                x: {
                    grid: { color: '#F3F4F6' },
                    ticks: { callback: v => v + '%', font: { size: 12 }, color: '#6B7280' },
                },
                y: {
                    grid: { display: false },
                    ticks: { font: { size: 14, weight: '500' }, color: '#374151' },
                },
            },
            plugins: {
                legend: { display: false },
            },
        },
        plugins: [{
            afterDatasetsDraw: (chart) => {
                const chartCtx = chart.ctx;
                const meta = chart.getDatasetMeta(0);
                meta.data.forEach((bar, index) => {
                    const value = chart.data.datasets[0].data[index];
                    chartCtx.fillStyle = '#374151';
                    chartCtx.font = '600 14px Calibri, sans-serif';
                    chartCtx.textAlign = 'left';
                    chartCtx.textBaseline = 'middle';
                    chartCtx.fillText(`${value}%`, bar.x + 8, bar.y);
                });
            },
        }],
    };
}

function buildLeaverJoinerConfig(data) {
    return {
        type: 'bar',
        data: {
            labels: data.map(d => d.month),
            datasets: [
                { label: 'Strong Joiners', data: data.map(d => d.strongJoiners), backgroundColor: '#059669', stack: 'stack', borderRadius: 2 },
                { label: 'Possible Joiners', data: data.map(d => d.possibleJoiners), backgroundColor: '#34D399', stack: 'stack' },
                { label: 'Possible Leavers', data: data.map(d => d.possibleLeavers), backgroundColor: '#F87171', stack: 'stack' },
                { label: 'Strong Leavers', data: data.map(d => d.strongLeavers), backgroundColor: '#DC2626', stack: 'stack', borderRadius: 2 },
                {
                    label: 'Net', data: data.map(d => d.net),
                    type: 'line', borderColor: '#1F2937', borderWidth: 2.5,
                    borderDash: [6, 3], pointRadius: 0, fill: false,
                    yAxisID: 'yNet',
                },
            ],
        },
        options: {
            scales: {
                x: { stacked: true, grid: { color: '#F3F4F6' }, ticks: { maxTicksLimit: 12, font: { size: 11 }, color: '#6B7280' } },
                y: { stacked: true, grid: { color: '#F3F4F6' }, ticks: { font: { size: 12 }, color: '#6B7280' } },
                yNet: { display: false, grid: { display: false } },
            },
            plugins: {
                legend: { position: 'top', labels: { font: { size: 12 }, usePointStyle: true, padding: 16 } },
            },
        },
    };
}

function buildBarConfig(data, dataKey, colorTop, colorBottom, yLabel) {
    return {
        type: 'bar',
        data: {
            labels: data.map(d => d.month),
            datasets: [{
                label: yLabel,
                data: data.map(d => d[dataKey]),
                backgroundColor: function(context) {
                    const chart = context.chart;
                    const { ctx: cCtx, chartArea } = chart;
                    if (!chartArea) return colorTop;
                    const gradient = cCtx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                    gradient.addColorStop(0, colorTop);
                    gradient.addColorStop(1, colorBottom);
                    return gradient;
                },
                borderRadius: 3,
            }],
        },
        options: {
            scales: {
                x: {
                    grid: { display: false },
                    ticks: { maxTicksLimit: 12, font: { size: 11 }, color: '#6B7280', maxRotation: 45 },
                },
                y: {
                    grid: { color: '#F3F4F6' },
                    ticks: { font: { size: 12 }, color: '#6B7280' },
                    title: { display: true, text: yLabel, font: { size: 13 }, color: '#9CA3AF' },
                },
            },
            plugins: {
                legend: { display: false },
            },
        },
    };
}

// Strip data URL prefix to get raw base64 for pptxgenjs
function toBase64(dataUrl) {
    return dataUrl.replace(/^data:image\/png;base64,/, '');
}

// ------------------------------------------------------------------
// Quote selection: pick representative, high-confidence quotes that
// explain chart movements.
//
// timeSeries (optional): [{month: "YYYY-MM", value: number}, ...]
//   When provided, detects 5+ point swings and grabs posts from those
//   months whose rating aligns with the movement direction.
// ------------------------------------------------------------------
function postToMonth(unixTime) {
    const d = new Date(unixTime * 1000);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// ISO-week Monday (YYYY-MM-DD) for a Unix-seconds timestamp.
function postToWeek(unixTime) {
    const d = new Date(unixTime * 1000);
    const isoDay = d.getUTCDay() || 7;
    const monday = new Date(Date.UTC(
        d.getUTCFullYear(),
        d.getUTCMonth(),
        d.getUTCDate() - (isoDay - 1),
    ));
    return monday.toISOString().slice(0, 10);
}

// Stop-word set used when reducing an aspect name (e.g., "Food Quality") to
// keyword tokens for sentence relevance scoring.
const ASPECT_STOPWORDS = new Set([
    'the','a','an','and','or','of','for','to','in','on','at','is','it',
    'this','that','with','by','as','about','from','our','your','their',
    'reason','reasons','rating','overall','general','customer','customers',
]);

// Given a review body and an aspect name (the topic / dimension this slide is
// about, e.g., "Food Quality"), extract the 1–2 sentences in the review that
// actually mention the aspect. Returns null if no aspect-relevant sentence is
// found so the caller can fall back to the original truncation.
function extractAspectExcerpt(text, aspectName, maxLen = 240) {
    if (!text || !aspectName) return null;

    const tokens = String(aspectName)
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .split(/\s+/)
        .filter(t => t.length > 2 && !ASPECT_STOPWORDS.has(t));
    if (tokens.length === 0) return null;

    const sentences = text
        .split(/(?<=[.!?])\s+/)
        .map(s => s.trim())
        .filter(s => s.length > 0);
    if (sentences.length === 0) return null;

    const scored = sentences.map((sentence, idx) => {
        const lower = sentence.toLowerCase();
        let score = 0;
        for (const tok of tokens) {
            const re = new RegExp(`\\b${tok.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g');
            const m = lower.match(re);
            if (m) score += m.length;
        }
        return { sentence, idx, score };
    });

    const maxScore = Math.max(...scored.map(s => s.score));
    if (maxScore === 0) return null;

    const bestIdx = scored.findIndex(s => s.score === maxScore);
    let excerpt = scored[bestIdx].sentence;

    // If room, attach the next sentence for context (often elaboration of the aspect).
    if (bestIdx + 1 < scored.length) {
        const combined = `${excerpt} ${scored[bestIdx + 1].sentence}`;
        if (combined.length <= maxLen) excerpt = combined;
    }
    // If we have headroom and a prior sentence sets up the matched one, prepend it.
    if (excerpt.length < maxLen * 0.6 && bestIdx > 0) {
        const combined = `${scored[bestIdx - 1].sentence} ${excerpt}`;
        if (combined.length <= maxLen) excerpt = combined;
    }

    if (excerpt.length > maxLen) excerpt = excerpt.substring(0, maxLen - 3) + '...';
    return excerpt;
}

function formatQuote(post, aspectName = null) {
    let text = post.text || post.title || '';
    // Strip markdown formatting (bold, links, etc.)
    text = text.replace(/\*\*/g, '').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1').replace(/\n+/g, ' ').trim();

    // When the slide is about a specific aspect (topic), pull the sentences
    // actually about that aspect rather than blindly taking the opener.
    if (aspectName) {
        const aspectExcerpt = extractAspectExcerpt(text, aspectName, 240);
        if (aspectExcerpt) text = aspectExcerpt;
    }

    if (text.length > 240) text = text.substring(0, 237) + '...';
    const date = post.time
        ? new Date(post.time * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
        : '';
    return { text, date, source: post.source_name || '' };
}

// Posts sourced from YouTube must never be used as report quotes. Checks the
// source fields a post may carry from the sentiment/posts API.
function isYouTubeSource(post) {
    const fields = [post?.source_name, post?.source_type, post?.source];
    return fields.some(v => typeof v === 'string' && v.toLowerCase().includes('youtube'));
}

function selectQuotes(posts, assessmentType, count = 4, timeSeries = null, granularity = 'month', aspectName = null) {
    if (!posts || !Array.isArray(posts) || posts.length === 0) return [];

    // Enrich posts with assessment rating + confidence
    // Posts may have flat structure (rating/confidence at top level from sentiment/posts API)
    // or nested structure (post_assessments[] from company data)
    let candidates = [];

    const extractRating = (post, type) => {
        // Flat structure (from topic/sentiment/posts API)
        if (post.rating != null) {
            return { rating: post.rating, confidence: post.confidence || 0 };
        }
        // Nested structure (from company data)
        if (type) {
            const a = post?.post_assessments?.find(x => x?.type === type && x?.rating != null);
            if (a) return { rating: a.rating, confidence: a.confidence || 0 };
        } else {
            const best = (post.post_assessments || []).reduce((b, a) =>
                a.rating != null && Math.abs(a.rating) > Math.abs(b?.rating || 0) ? a : b, null);
            if (best) return { rating: best.rating, confidence: best.confidence || 0 };
        }
        return null;
    };

    posts.forEach(p => {
        if (!(p?.text || p?.title)) return;
        if (isYouTubeSource(p)) return;   // #5: never quote YouTube-sourced content
        const r = extractRating(p, assessmentType);
        if (!r) return;
        candidates.push({
            ...p,
            _rating: r.rating,
            _confidence: r.confidence,
            _absRating: Math.abs(r.rating),
        });
    });

    if (candidates.length === 0) return [];

    const selected = [];
    const usedIds = new Set();

    const addPost = (post) => {
        const key = post.id || post.time;
        if (usedIds.has(key)) return false;
        usedIds.add(key);
        selected.push(post);
        return true;
    };

    // Phase 1: Movement-driven quotes (if time series provided)
    if (timeSeries && timeSeries.length >= 2) {
        const movements = [];
        for (let i = 1; i < timeSeries.length; i++) {
            const prev = timeSeries[i - 1];
            const curr = timeSeries[i];
            if (prev.value == null || curr.value == null) continue;
            const delta = curr.value - prev.value;
            if (Math.abs(delta) >= 5) {
                movements.push({ month: curr.month, delta });
            }
        }

        // Biggest movements first
        movements.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));

        for (const move of movements) {
            if (selected.length >= Math.ceil(count / 2)) break;

            // Posts from this bucket (month or ISO-week) matching the movement direction
            const bucketOf = granularity === 'week' ? postToWeek : postToMonth;
            const monthPosts = candidates
                .filter(p => p.time && bucketOf(p.time) === move.month)
                .filter(p => move.delta > 0 ? p._rating > 2 : p._rating < -2)
                .sort((a, b) => (b._confidence - a._confidence) || (b._absRating - a._absRating));

            if (monthPosts.length > 0) addPost(monthPosts[0]);
        }
    }

    // Phase 2: Fill remaining with high-confidence strong opinions
    const remaining = candidates
        .filter(c => !usedIds.has(c.id || c.time))
        .sort((a, b) => {
            // Primary: confidence
            const confDiff = (b._confidence || 0) - (a._confidence || 0);
            if (Math.abs(confDiff) > 0.1) return confDiff;
            // Secondary: strength of opinion
            return b._absRating - a._absRating;
        });

    for (const post of remaining) {
        if (selected.length >= count) break;
        addPost(post);
    }

    return selected.map(p => formatQuote(p, aspectName));
}

// ------------------------------------------------------------------
// Shift detection & evidence pipeline
// ------------------------------------------------------------------

/**
 * Analyze a time series to find the most important movements:
 *  - Long-term trend (start-of-period vs end-of-period direction)
 *  - Major moves (biggest month-over-month swings, up and down)
 *  - Recent micro-trends (last 2 months of direction changes)
 *
 * Returns an array of { type, month, delta, description } objects.
 * `month` is "YYYY-MM" format for fetching posts.
 */
// Minimum sample size for a month-over-month move to be considered high-confidence.
const MIN_SHIFT_SAMPLE = 8;

function detectKeyShifts(timeSeries, { criticalThreshold = 10, majorThreshold = 4 } = {}) {
    // timeSeries: [{ month, value, count? }]
    if (!timeSeries || timeSeries.length < 3) return [];

    const shifts = [];
    const validPoints = timeSeries.filter(p => p.value != null);
    if (validPoints.length < 3) return [];

    // --- Critical single-month moves (≥criticalThreshold pts on decent volume) ---
    // These are the sharpest signals — a specific event in a specific month.
    const criticalMoves = [];
    for (let i = 1; i < validPoints.length; i++) {
        const prev = validPoints[i - 1];
        const curr = validPoints[i];
        const delta = curr.value - prev.value;
        const n = curr.count || 0;
        if (Math.abs(delta) >= criticalThreshold && n >= MIN_SHIFT_SAMPLE) {
            criticalMoves.push({
                type: 'critical_move',
                month: curr.month,
                // single-month window — no prevMonth so backend scopes tightly
                delta,
                count: n,
                description: `${delta > 0 ? 'Spike' : 'Crash'} of ${Math.abs(delta).toFixed(1)} pts in ${curr.month} (n=${n})`,
            });
        }
    }

    // If we found critical single-month moves, use ONLY those — they're the event.
    if (criticalMoves.length > 0) {
        criticalMoves.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
        return criticalMoves.slice(0, 3);
    }

    // --- Major month-over-month moves (≥majorThreshold pts) ---
    const moves = [];
    for (let i = 1; i < validPoints.length; i++) {
        const prev = validPoints[i - 1];
        const curr = validPoints[i];
        const delta = curr.value - prev.value;
        if (Math.abs(delta) >= majorThreshold) {
            moves.push({ month: curr.month, prevMonth: prev.month, delta, absD: Math.abs(delta), count: curr.count || 0 });
        }
    }
    // Keep top 3 biggest moves
    moves.sort((a, b) => b.absD - a.absD);
    for (const m of moves.slice(0, 3)) {
        shifts.push({
            type: 'major_move',
            month: m.month,
            prevMonth: m.prevMonth,
            delta: m.delta,
            count: m.count,
            description: `${m.delta > 0 ? 'Surge' : 'Drop'} of ${Math.abs(m.delta).toFixed(1)} pts in ${m.month} (from ${m.prevMonth})`,
        });
    }

    // --- Recent micro-trends (last 3 data points) ---
    if (validPoints.length >= 3) {
        const recent = validPoints.slice(-3);
        const recentDelta = recent[recent.length - 1].value - recent[0].value;
        if (Math.abs(recentDelta) >= 2) {
            shifts.push({
                type: 'recent_micro',
                month: recent[recent.length - 1].month,
                startMonth: recent[0].month,
                delta: recentDelta,
                description: `Recent ${recentDelta > 0 ? 'uptick' : 'dip'} of ${Math.abs(recentDelta).toFixed(1)} pts over last 2 months`,
            });
        }
    }

    // --- Long-term trend (only if no major moves found) ---
    if (shifts.length === 0) {
        const first = validPoints[0];
        const last = validPoints[validPoints.length - 1];
        const totalDelta = last.value - first.value;
        if (Math.abs(totalDelta) >= 3) {
            shifts.push({
                type: 'long_term_trend',
                month: last.month,
                startMonth: first.month,
                delta: totalDelta,
                description: `Overall ${totalDelta > 0 ? 'improvement' : 'decline'} of ${Math.abs(totalDelta).toFixed(1)} pts from ${first.month} to ${last.month}`,
            });
        }
    }

    return shifts;
}

// ------------------------------------------------------------------
// AI Summary helpers
// ------------------------------------------------------------------

/**
 * Fetch an AI-generated slide summary from the Flask report-summary endpoint.
 * Sends metric data + detected shifts so the server can pull evidence quotes
 * from the DB and feed them to the LLM for causal explanations.
 */
async function fetchAISummary(flaskServerAddress, companyId, prompt, {
    topicId = null,
    assessmentType = null,
    timeSeries = null,
    shifts = null,
    granularity = 'month',
} = {}) {
    const payload = {
        question: prompt,
        strategy: 'hierarchical',
        max_posts: 200,
        // Evidence pipeline fields — Flask uses these to pull
        // high-confidence quotes from shift periods and inject
        // them into the LLM context before generating the summary.
        topic_id: topicId,
        assessment_type: assessmentType,
        time_series: timeSeries,
        time_series_granularity: granularity,
        shifts: shifts,
    };
    const url = `${flaskServerAddress}company-report-summary/${companyId}/`;
    console.log('[Report][Flask] POST', url, {
        topic_id: topicId,
        assessment_type: assessmentType,
        time_series_granularity: granularity,
        time_series_length: Array.isArray(timeSeries) ? timeSeries.length : 0,
        time_series_sample: Array.isArray(timeSeries) ? timeSeries.slice(0, 5) : null,
        time_series_last: Array.isArray(timeSeries) ? timeSeries.slice(-3) : null,
        shifts_count: Array.isArray(shifts) ? shifts.length : 0,
        shifts,
        prompt_preview: typeof prompt === 'string' ? prompt.slice(0, 400) : null,
    });
    console.log('[Report][Flask] FULL payload', payload);
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        if (!response.ok) {
            console.warn('[Report][Flask] non-OK response', response.status, response.statusText);
            return null;
        }
        const data = await response.json();
        console.log('[Report][Flask] response', {
            hasAnswer: !!data?.answer,
            answerLength: Array.isArray(data?.answer) ? data.answer.length : 0,
        });
        if (!data?.answer || !Array.isArray(data.answer)) return null;
        return data.answer.map(a => a.comment).join('\n\n');
    } catch (e) {
        console.warn('[Report][Flask] AI summary fetch failed:', e);
        return null;
    }
}

// Deck style rules (PE register) from the Flask `deck/style-rules/` endpoint.
// Fetched once per session and cached at module level; stays null when the
// endpoint is unavailable so the Tier-B fallback prompts behave exactly as
// they do today. Never throws.
let _deckStyleRules = null;

async function getStyleRules(flaskServerAddress) {
    if (_deckStyleRules) return _deckStyleRules;
    if (!flaskServerAddress) return null;
    try {
        const resp = await fetch(`${flaskServerAddress}deck/style-rules/`);
        if (!resp.ok) {
            console.warn('[Report][StyleRules] endpoint non-OK', resp.status, resp.statusText);
            return null;
        }
        const data = await resp.json();
        if (data && typeof data === 'object') _deckStyleRules = data;
    } catch (e) {
        console.warn('[Report][StyleRules] fetch failed (fallback prompts unchanged):', e);
    }
    return _deckStyleRules;
}

// Deck methodology entries ({ metric_key: { label, footnote, appendix } })
// from the Flask `deck/methodology/` endpoint. Same pattern as getStyleRules:
// fetched once per session, cached at module level, never throws. Feeds the
// per-slide footnotes for client-copy slides and the appendix Methodology page.
let _deckMethodology = null;

async function getMethodology(flaskServerAddress) {
    if (_deckMethodology) return _deckMethodology;
    if (!flaskServerAddress) return null;
    try {
        const resp = await fetch(`${flaskServerAddress}deck/methodology/`);
        if (!resp.ok) {
            console.warn('[Report][Methodology] endpoint non-OK', resp.status, resp.statusText);
            return null;
        }
        const data = await resp.json();
        if (data && typeof data === 'object') _deckMethodology = data;
    } catch (e) {
        console.warn('[Report][Methodology] fetch failed (appendix page skipped):', e);
    }
    return _deckMethodology;
}

// Extras requested once per topic from deck-content (contract C).
const DECK_EXTRA_KEYS = ['billing_taxonomy', 'location_extremes', 'volume_nps_series'];

function buildAnalystPreamble(sectionLabels = ['1-YEAR TREND', 'KEY DEVELOPMENTS', 'RECENT MOVEMENT'], timeframe = 'the past year') {
    const [s1, s2, s3] = sectionLabels;
    const base = `You are an elite equity research analyst at a top-tier investment bank writing a concise slide commentary for institutional investors. Your tone is authoritative, thesis-driven, and numbers-heavy — like a Goldman Sachs or Morgan Stanley research note.

RULES:
- Lead with the investment-relevant takeaway, not a description of the chart
- Every sentence must contain a specific number, date, or comparison
- When customer evidence is provided, weave it into causal explanations (e.g., "The 8pt NPS decline in July was driven by pricing backlash — one user noted 'my bill more than doubled'")
- Use precise language: "accelerated," "inflected," "compressed," "stabilized" — avoid vague words like "changed" or "went down"
- Be opinionated: state whether this is bullish, bearish, or neutral for the company
- Focus your analysis on ${timeframe}
- No bullet points. No filler. Every word earns its place.

Write exactly 3 labeled paragraphs:

${s1}:
[2-3 sentences. State the directional thesis first, then support with specific start/end values and the magnitude of change. Compare to competitors if data is available.]

${s2}:
[2-3 sentences. Identify the 1-2 most important inflection points with specific dates and magnitudes. Explain the root cause using customer evidence when available. Connect to business events (pricing changes, product launches, competitive moves).]

${s3}:
[2-3 sentences. Focus on the most recent data. Is the trend accelerating, decelerating, or reversing? What is the forward-looking implication for the business?]

`;
    // Append the server-provided PE style register when the style-rules fetch
    // succeeded (cached module-level by getStyleRules); otherwise the preamble
    // is byte-identical to before.
    const styleSnippet = typeof _deckStyleRules?.prompt_snippet === 'string' ? _deckStyleRules.prompt_snippet.trim() : '';
    return styleSnippet ? `${base}${styleSnippet}\n\n` : base;
}

// Headline KPIs as displayed in the on-slide metric boxes. The AI must use
// these numbers verbatim so the narrative matches the chart the user sees.
function buildKpisSection(kpis) {
    if (!kpis) return '';
    return (
        `HEADLINE KPIs (these are the exact values shown on the slide — use them verbatim, do not recompute):\n` +
        `- Current: ${kpis.currentNps}\n` +
        `- Peak: ${kpis.peakNps} (${kpis.peakDate})\n` +
        `- Low: ${kpis.lowNps} (${kpis.lowDate})\n` +
        `- Posts analyzed: ${kpis.postsAnalyzed}\n\n`
    );
}

function buildSentimentSummaryPrompt(topicName, type, chartData, stats, sectionLabels, timeframe, kpis = null) {
    const preamble = buildAnalystPreamble(sectionLabels, timeframe);
    const monthly = [];
    let lastMonth = '';
    for (const d of chartData) {
        const ts = new Date(d.timestamp);
        const m = `${ts.getFullYear()}-${String(ts.getMonth() + 1).padStart(2, '0')}`;
        if (m === lastMonth) continue;
        lastMonth = m;
        const score = calcQualityScore(d);
        monthly.push(`${m}: ${score.toFixed(0)}`);
    }
    const statsText = stats.map(s => `${s.label}: ${s.value}`).join(', ');
    return `${buildKpisSection(kpis)}${preamble}COMPANY/TOPIC: "${topicName}"\nMETRIC: ${titleCase(type)} quality score (0-100 scale; 50=neutral, >60=positive, <40=concerning)\n\nMONTHLY SCORES:\n${monthly.join('\n')}\n\nKPIs: ${statsText}\n\nWrite your analysis of this ${titleCase(type)} metric for ${topicName}. Reference specific score values and dates.`;
}

function buildNpsCompetitiveSummaryPrompt(topicName, npsById, competitorNames, allIds, sectionLabels, timeframe, npsMonthsBack = 12, kpis = null) {
    const preamble = buildAnalystPreamble(sectionLabels, timeframe);
    // Backend returns weekly buckets for the report generator; cap the series
    // at roughly npsMonthsBack months worth of weeks so the prompt stays bounded.
    const weeksCap = Math.max(4, Math.round(npsMonthsBack * 4.345));
    const lines = [];
    for (const id of allIds) {
        const rows = npsById[id] || [];
        const name = id === allIds[0] ? topicName : (competitorNames[allIds.indexOf(id) - 1] || id);
        const weekly = rows.slice(-weeksCap).map(r => `${r.month}: ${r.nps?.toFixed(0) ?? 'N/A'}`);
        if (weekly.length > 0) lines.push(`${name}: ${weekly.join(', ')}`);
    }
    return `${buildKpisSection(kpis)}${preamble}COMPANY/TOPIC: "${topicName}"\nMETRIC: NPS (Net Promoter Score) — competitive comparison\nCOMPETITORS: ${competitorNames.join(', ')}\n\nWEEKLY NPS (last ${weeksCap} weeks; each date is the week-start):\n${lines.join('\n')}\n\nWrite your competitive analysis. Focus on: which company is gaining/losing ground, NPS spread changes, and whether ${topicName}'s competitive moat is widening or narrowing.`;
}

function buildLeaverJoinerSummaryPrompt(topicName, monthlyData, stats, sectionLabels, timeframe) {
    const preamble = buildAnalystPreamble(sectionLabels, timeframe);
    const lines = monthlyData.labels.map((label, i) => {
        const d = monthlyData.data[i];
        return `${label}: net=${d.net}, joiners=${d.strongJoiners + d.possibleJoiners}, leavers=${Math.abs(d.strongLeavers + d.possibleLeavers)}`;
    });
    const statsText = stats.map(s => `${s.label}: ${s.value}`).join(', ');
    return `${preamble}COMPANY/TOPIC: "${topicName}"\nMETRIC: Leaver/Joiner — customer churn and acquisition signals from online discussions\nPositive net = more customers joining than leaving.\n\nMONTHLY DATA:\n${lines.join('\n')}\n\nKPIs: ${statsText}\n\nWrite your analysis of ${topicName}'s customer retention dynamics. Is the company gaining or hemorrhaging customers? What is driving churn?`;
}

function buildLlmRecommendationSummaryPrompt(topicName, rows, compScores, sectionLabels, timeframe, windowDays) {
    const preamble = buildAnalystPreamble(sectionLabels, timeframe);
    const lines = rows
        .filter(r => (r.count || 0) > 0)
        .map(r => `${r.day}: ${Math.round(r.mean_nps ?? 0)} (n=${r.count})`);
    const compText = compScores.length > 0
        ? `\n\nCOMPETITOR ${windowDays}-DAY SCORES (same metric, same scale):\n${compScores.map(c => `${c.name}: ${c.score}`).join('\n')}`
        : '';
    return `${preamble}COMPANY/TOPIC: "${topicName}"\nMETRIC: LLM Recommendation Score (0-100) — how favourably AI assistants (ChatGPT) rate the brand when it comes up in recommendation-style answers. Promoter/detractor banding of per-answer ratings, 7-day smoothed.\n\nDAILY SCORES:\n${lines.join('\n')}${compText}\n\nWrite your analysis of how AI assistants portray ${topicName}. Reference specific score levels and dates. AI answers increasingly shape purchase decisions, so treat this as an emerging recommendation channel: is the brand's AI standing improving or deteriorating, and how does it compare to rivals?`;
}

function buildLlmPositionSummaryPrompt(topicName, summaries, sectionLabels, timeframe) {
    const preamble = buildAnalystPreamble(sectionLabels, timeframe);
    const lines = summaries.map((s, i) =>
        `${i + 1}. ${s.name}: avg position ${s.position != null ? s.position.toFixed(2) : 'N/A'}, named in ${s.appearRate != null ? Math.round(s.appearRate * 100) : 0}% of ${s.opportunities} open-ended answers`);
    return `${preamble}COMPANY/TOPIC: "${topicName}"\nMETRIC: LLM answer position — where each brand lands when AI assistants (ChatGPT) answer open-ended category questions that name no brand. Position 1 = named first; a brand missing from a ranked answer is scored one place behind the last brand named. Positions are averaged as reciprocal ranks (harmonic mean), so the number reflects a brand's typical top-of-list placement rather than being dragged by rare deep placements.\n\nBRAND STANDINGS (best position first):\n${lines.join('\n')}\n\nWrite your competitive positioning analysis. Who wins the AI recommendation, and is ${topicName} the default answer or an afterthought? Weigh both rank and appearance rate — ranking well in rare appearances is weaker than being named reliably.`;
}

function buildPostLikesSummaryPrompt(topicName, monthlyLabels, monthlyValues, sectionLabels, timeframe) {
    const preamble = buildAnalystPreamble(sectionLabels, timeframe);
    const lines = monthlyLabels.map((label, i) => `${label}: ${monthlyValues[i]}`);
    const total = monthlyValues.reduce((a, b) => a + b, 0);
    const avg = Math.round(total / monthlyValues.length);
    return `${preamble}COMPANY/TOPIC: "${topicName}"\nMETRIC: Monthly post likes — proxy for content resonance and community engagement\nAverage: ${avg} likes/month | Total: ${total.toLocaleString()}\n\nMONTHLY LIKES:\n${lines.join('\n')}\n\nWrite your analysis. Like trends reflect how much the community cares about this topic. Is engagement intensifying or fading?`;
}

/**
 * Parse AI summary text into concise bullet points for the data slide.
 * Extracts the key sentences from each labeled section.
 */
function parseSummaryBullets(text, sectionLabels = ['1-YEAR TREND', 'KEY DEVELOPMENTS', 'RECENT MOVEMENT']) {
    if (!text) return [];
    const bullets = [];
    const labels = sectionLabels;
    const pattern = new RegExp(`(${labels.join('|')}):\\s*`, 'gi');
    const parts = text.split(pattern).filter(s => s.trim());

    let currentLabel = null;
    for (const part of parts) {
        const upper = part.trim().toUpperCase();
        if (labels.includes(upper)) {
            currentLabel = upper;
        } else if (currentLabel) {
            // Take first 1-2 sentences from each section
            const sentences = part.trim().split(/(?<=[.!?])\s+/).filter(s => s.trim().length > 10);
            if (sentences.length > 0) {
                bullets.push(sentences.slice(0, 2).join(' '));
            }
            currentLabel = null;
        }
    }

    // Fallback: if no labeled sections, split into sentences
    if (bullets.length === 0) {
        const sentences = text.trim().split(/(?<=[.!?])\s+/).filter(s => s.trim().length > 10);
        return sentences.slice(0, 3).map(stripEmDashes);
    }

    return bullets.map(stripEmDashes);
}

/**
 * Extract a headline from AI summary text — takes the first sentence from
 * the 1-YEAR TREND section (or first sentence overall) and strips the period.
 * This avoids a separate API call per slide.
 */
// Turn the lead sentence of a summary into a slide title.
// NEVER produces an ellipsis: a short sentence is used whole; a long one is
// cut back to its first clause (the part before a comma) when that clause
// reads as a complete title; failing that, trimmed at a word boundary.
function titleFromSentence(sentence) {
    const MAX = 120;
    const s = sentence.trim().replace(/\.+$/, '');
    if (s.length <= MAX) return stripEmDashes(s);
    // Prefer the first clause — the part before the first comma — when it
    // stands on its own as a title (the rest is usually the "driven by..."
    // explanation, which belongs in the bullets, not the title).
    const comma = s.indexOf(',');
    if (comma >= 40 && comma <= MAX) return stripEmDashes(s.slice(0, comma).trim());
    // Otherwise trim at the last word boundary within budget. No ellipsis.
    const cut = s.slice(0, MAX);
    const lastSpace = cut.lastIndexOf(' ');
    return stripEmDashes((lastSpace > 40 ? cut.slice(0, lastSpace) : cut).trim());
}

function deriveHeadline(summaryText, fallback, firstSectionLabel = '1-YEAR TREND') {
    if (!summaryText) return fallback;
    // Try to extract first sentence from the first section
    const escapedLabel = firstSectionLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const trendMatch = summaryText.match(new RegExp(escapedLabel + ':\\s*(.+?)(?:\\.|$)', 'i'));
    if (trendMatch) {
        return titleFromSentence(trendMatch[1]);
    }
    // Fallback: first sentence of the whole text
    const allLabelsPattern = new RegExp('^(' + ['1-YEAR TREND', 'KEY DEVELOPMENTS', 'RECENT MOVEMENT', 'THIS WEEK', 'KEY CHANGES', 'EMERGING SIGNALS', 'MONTHLY TREND'].join('|') + '):\\s*', 'gi');
    const firstSentence = summaryText.replace(allLabelsPattern, '')
        .split(/(?<=[.!?])\s+/)[0];
    if (firstSentence) {
        return titleFromSentence(firstSentence);
    }
    return fallback;
}

function parseSummaryParagraphs(text, sectionLabels = ['1-YEAR TREND', 'KEY DEVELOPMENTS', 'RECENT MOVEMENT']) {
    if (!text) return null;
    const sections = {};
    const labels = sectionLabels;
    const pattern = new RegExp(`(${labels.join('|')}):\\s*`, 'gi');
    const parts = text.split(pattern).filter(s => s.trim());

    let currentLabel = null;
    for (const part of parts) {
        const upper = part.trim().toUpperCase();
        if (labels.includes(upper)) {
            currentLabel = upper;
        } else if (currentLabel) {
            sections[currentLabel] = part.trim();
            currentLabel = null;
        }
    }

    if (Object.keys(sections).length === 0) {
        return { 'ANALYSIS': text.trim() };
    }
    return sections;
}

// ------------------------------------------------------------------
// Data processing helpers
// ------------------------------------------------------------------

/**
 * Compute the value N days ago from a time series.
 * timeSeries: [{ month: "YYYY-MM", value }]
 * Returns the value closest to N days ago, or null.
 */
function getValueDaysAgo(timeSeries, daysAgo) {
    if (!timeSeries || timeSeries.length === 0) return null;
    const target = new Date();
    target.setDate(target.getDate() - daysAgo);
    // YYYY-MM-DD compares correctly against both monthly (YYYY-MM) and weekly
    // (YYYY-MM-DD) bucket labels lexicographically.
    const targetDay = target.toISOString().slice(0, 10);

    let best = null;
    for (const pt of timeSeries) {
        if (pt.value == null) continue;
        if (pt.month <= targetDay) best = pt;
    }
    return best ? best.value : null;
}

/**
 * Compute 30-day and 365-day deltas from a time series.
 */
// For cumulative-count metrics (likes, post volume) where the last monthly
// bucket is partial — May 23rd means only 23/31 of May has elapsed — comparing
// last-bucket vs prior-bucket produces a fake delta. Roll the window across
// daily rows instead: current = last `shortDays` days, delta vs the preceding
// equivalent window (and the same window one year back).
function computeRollingDeltas(rawData, valueKey = 'num_posts', shortDays = 30, longDays = 365) {
    if (!Array.isArray(rawData) || rawData.length === 0) {
        return { current: null, delta30: null, delta365: null };
    }
    const dayMs = 24 * 60 * 60 * 1000;
    const nowMs = Date.now();
    const sumInRange = (startMs, endMs) => {
        let sum = 0, anyData = false;
        for (const item of rawData) {
            if (!item || !item.day) continue;
            const ts = new Date(item.day + 'T00:00:00Z').getTime();
            if (ts >= startMs && ts < endMs) {
                sum += Number(item[valueKey]) || 0;
                anyData = true;
            }
        }
        return { sum, anyData };
    };
    const recent = sumInRange(nowMs - shortDays * dayMs, nowMs);
    const priorShort = sumInRange(nowMs - 2 * shortDays * dayMs, nowMs - shortDays * dayMs);
    const yearAgo = sumInRange(nowMs - longDays * dayMs - shortDays * dayMs, nowMs - longDays * dayMs);
    return {
        current: recent.sum,
        delta30: priorShort.anyData ? Math.round(recent.sum - priorShort.sum) : null,
        delta365: yearAgo.anyData ? Math.round(recent.sum - yearAgo.sum) : null,
    };
}

function computeDeltas(timeSeries, shortDays = 30, longDays = 365) {
    if (!timeSeries || timeSeries.length === 0) return { current: null, delta30: null, delta365: null };
    const validPoints = timeSeries.filter(p => p.value != null);
    if (validPoints.length === 0) return { current: null, delta30: null, delta365: null };
    const current = validPoints[validPoints.length - 1].value;
    const valShort = getValueDaysAgo(validPoints, shortDays);
    const valLong = getValueDaysAgo(validPoints, longDays);
    return {
        current: Math.round(current * 10) / 10,
        delta30: valShort != null ? Math.round((current - valShort) * 10) / 10 : null,
        delta365: valLong != null ? Math.round((current - valLong) * 10) / 10 : null,
    };
}

// Aggregate daily data to monthly
function aggregateMonthly(rawData, valueKey = 'num_posts') {
    if (!Array.isArray(rawData) || rawData.length === 0) return { labels: [], values: [] };
    const monthly = {};
    rawData.forEach(item => {
        const month = item.day.substring(0, 7);
        if (!monthly[month]) monthly[month] = 0;
        monthly[month] += Number(item[valueKey]) || 0;
    });
    const sorted = Object.entries(monthly).sort((a, b) => a[0].localeCompare(b[0]));
    return {
        labels: sorted.map(([m]) => format(parseISO(m + '-01'), 'MMM yy')),
        values: sorted.map(([, v]) => v),
    };
}

// Aggregate daily {day:'YYYY-MM-DD', <valueKey>} rows into ISO-week buckets
// (week start = Monday). Returns [{ weekStart:'YYYY-MM-DD', value }] sorted.
function aggregateDailyToWeekly(rawData, valueKey = 'num_posts') {
    const byWeek = new Map();
    (rawData || []).forEach(item => {
        if (!item || !item.day) return;
        const d = new Date(item.day + 'T00:00:00Z');
        if (isNaN(d)) return;
        const isoDay = d.getUTCDay() || 7;
        const monday = new Date(Date.UTC(
            d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - (isoDay - 1),
        ));
        const key = monday.toISOString().slice(0, 10);
        const v = Number(item[valueKey]) || 0;
        byWeek.set(key, (byWeek.get(key) || 0) + v);
    });
    return [...byWeek.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([weekStart, value]) => ({ weekStart, value }));
}

// The early part of a weekly series is often a different, lower regime than
// the present — either a pre-tracking coverage ramp-up, or a baseline before a
// sustained step-up (e.g. volume more than doubling and staying there). Both
// make the chart deceptive. Find the earliest week from which the series holds
// at its *current* level, and return that week-start so the chart trims to it.
// Returns null when the series is already at the current level throughout.
function detectCoverageStartWeek(weekly) {
    if (!Array.isArray(weekly) || weekly.length < 16) return null;
    const values = weekly.map(w => w.value);
    const median = (arr) => {
        if (!arr.length) return 0;
        const s = [...arr].sort((a, b) => a - b);
        return s[Math.floor(s.length / 2)];
    };
    // "Current" level — median of the most recent 26 weeks.
    const recentLevel = median(values.slice(-26));
    if (recentLevel <= 0) return null;
    // Weeks below half the current level belong to a lower regime (ramp-up or
    // pre-step-up baseline). This catches a "more than double" sustained jump.
    const threshold = recentLevel * 0.5;
    for (let i = 0; i < values.length; i++) {
        if (values[i] < threshold) continue;
        const suffix = values.slice(i);
        const above = suffix.filter(v => v >= threshold).length;
        if (above / suffix.length < 0.7) continue; // not yet sustained
        if (i === 0) return null; // already at the current level throughout
        // Trim only when the earlier stretch really was a lower regime.
        return median(values.slice(0, i)) < threshold ? weekly[i].weekStart : null;
    }
    return null;
}

// Minimum posts a source needs across the lookback window to earn its own
// slide. Below this it's noise (e.g. a review site with a handful of posts
// ever) and produces a misleading near-empty chart, so we drop it entirely.
const MIN_SOURCE_TOTAL_POSTS = 100;

// Decide whether a source's weekly series has enough data to be worth a slide.
// Rules (per product feedback): need a meaningful total volume
// (>= MIN_SOURCE_TOTAL_POSTS over the window), >= 6 weeks of data, and the
// source must still be live — at least one post in the 14 days before the run
// date. Returns { qualifies, coverageStart } where coverageStart trims ramp-up.
function assessSourceCoverage(weekly, runDate = new Date()) {
    const coverageStart = detectCoverageStartWeek(weekly);
    const trimmed = coverageStart
        ? weekly.filter(w => w.weekStart >= coverageStart)
        : weekly;
    const weeksWithData = trimmed.filter(w => w.value > 0).length;
    // Total volume over the whole window — excludes trivial sources outright.
    const totalPosts = (weekly || []).reduce((sum, w) => sum + (w.value || 0), 0);
    const cutoff = new Date(runDate.getTime() - 14 * 24 * 60 * 60 * 1000);
    const hasRecent = weekly.some(w => w.value > 0 && new Date(w.weekStart + 'T00:00:00Z') >= cutoff)
        // also count posts dated within the last 14 days even mid-week
        || weekly.some(w => {
            const wEnd = new Date(w.weekStart + 'T00:00:00Z');
            wEnd.setUTCDate(wEnd.getUTCDate() + 6);
            return w.value > 0 && wEnd >= cutoff;
        });
    return {
        qualifies: totalPosts >= MIN_SOURCE_TOTAL_POSTS && weeksWithData >= 6 && hasRecent,
        coverageStart,
    };
}

// Build the data-slide commentary when no AI narrative is available (Tier C).
// Purely factual and register-safe: state the current value and the short-/
// long-window changes in plain sentences, plus the largest detected move as a
// dated fact. No editorial framing — interpretation belongs to Tier A/B copy.
function buildFallbackBullets(deltas, metricName, shifts, deltaShortLabel = '30-Day', deltaLongLabel = '365-Day') {
    console.warn('[report] fallback narrative used for', metricName);
    const bullets = [];
    const fmt = (v) => typeof v !== 'number' ? v
        : (Number.isInteger(v) ? v.toLocaleString() : v.toFixed(1));
    const signed = (v) => `${v > 0 ? '+' : ''}${fmt(v)}`;

    if (deltas.current != null) {
        bullets.push(`${metricName} currently stands at ${fmt(deltas.current)}.`);
    }
    if (deltas.delta30 != null) {
        bullets.push(`${deltaShortLabel} change: ${signed(deltas.delta30)}.`);
    }
    if (deltas.delta365 != null) {
        bullets.push(`${deltaLongLabel} change: ${signed(deltas.delta365)}.`);
    }
    // Largest detected move, stated as a dated fact.
    if (shifts && shifts.length > 0 && shifts[0].description) {
        bullets.push(`Largest detected move: ${shifts[0].description.toLowerCase()}.`);
    }

    if (bullets.length === 0) {
        bullets.push(`${metricName} data is too sparse this period for trend analysis.`);
    }
    // Defense-in-depth: never let an em dash reach the slide.
    return bullets.map(stripEmDashes);
}

// --- LLM Recommendations helpers ------------------------------------------

// Weighted average of the smoothed daily LLM score over [now-offset-days, now-offset),
// weighting each real (non-empty) day by its response count — mirrors the LLM
// page's metric cards so the deck surfaces the same numbers as the app.
function llmWindowScore(rows, days, offsetDays = 0) {
    if (!rows || rows.length === 0) return { score: null, count: 0 };
    const dayStr = (back) => new Date(Date.now() - back * 86400000).toISOString().split('T')[0];
    const start = dayStr(offsetDays + days);
    const end = dayStr(offsetDays);
    const inRange = rows.filter(r => r.day >= start && r.day < end && (r.count || 0) > 0);
    if (inRange.length === 0) return { score: null, count: 0 };
    let weighted = 0, totalWeight = 0, totalCount = 0;
    inRange.forEach(r => {
        const w = r.count || 1;
        weighted += (r.mean_nps || 0) * w;
        totalWeight += w;
        totalCount += r.count || 0;
    });
    return { score: totalWeight > 0 ? Math.round(weighted / totalWeight) : null, count: totalCount };
}

// Pool the flat /positions rows into one standing per brand (weighted avg of
// the smoothed penalized position, appearance rate, opportunity count) —
// the same math as the Competitive Comparison tab's brand cards.
function summarizeLlmPositions(rows, nameFor) {
    const groups = new Map();
    (rows || []).forEach(r => {
        if (!groups.has(r.topic_id)) groups.set(r.topic_id, []);
        groups.get(r.topic_id).push(r);
    });
    const summaries = [];
    groups.forEach((series, topicId) => {
        if (!series.some(r => (r.window_opportunities || 0) > 0)) return;
        let opportunities = 0, appearances = 0, weighted = 0, weight = 0;
        series.forEach(r => {
            opportunities += r.opportunities || 0;
            appearances += r.appearances || 0;
            const v = r.avg_position;
            const w = r.opportunities || 0;
            if (v != null && w > 0) { weighted += Number(v) * w; weight += w; }
        });
        summaries.push({
            topicId,
            name: nameFor(topicId),
            position: weight > 0 ? weighted / weight : null,
            appearRate: opportunities > 0 ? appearances / opportunities : null,
            opportunities,
            series,
        });
    });
    return summaries.sort((a, b) => (a.position ?? Infinity) - (b.position ?? Infinity));
}

// Chart.js data for the inline LLM-score fallback chart (used only when the
// live LLM Recommendations page capture fails). Same shape the page builds.
function buildLlmScoreChartData(mainName, rows, compSeries) {
    const series = [
        { name: mainName, rows, isMain: true },
        ...compSeries.map(s => ({ name: s.name, rows: s.rows, isMain: false })),
    ];
    const daySet = new Set();
    series.forEach(s => s.rows.forEach(r => daySet.add(r.day)));
    const labels = [...daySet].sort();
    const datasets = series.map((s, i) => {
        const byDay = {};
        s.rows.forEach(r => { byDay[r.day] = r; });
        return {
            label: s.name,
            data: labels.map(d => ({ x: d, y: byDay[d]?.mean_nps ?? null })),
            borderColor: NPS_CHART_COLORS[i % NPS_CHART_COLORS.length],
            backgroundColor: 'transparent',
            borderWidth: s.isMain ? 3 : 2,
            tension: 0.1,
            pointRadius: 2,
            spanGaps: true,
        };
    });
    return { labels, datasets };
}

// Chart.js data for the inline LLM-position fallback chart (penalized avg
// position per brand per day, rank 1 best — LLMPositionChart reverses the axis).
function buildLlmPositionChartData(mainTopicId, summaries) {
    const daySet = new Set();
    summaries.forEach(s => s.series.forEach(r => daySet.add(r.day)));
    const labels = [...daySet].sort();
    const datasets = summaries.map((s, i) => {
        const byDay = {};
        s.series.forEach(r => { byDay[r.day] = r; });
        return {
            label: s.name,
            data: labels.map(d => {
                const v = byDay[d]?.avg_position;
                return { x: d, y: v != null ? Number(v) : null };
            }),
            borderColor: NPS_CHART_COLORS[i % NPS_CHART_COLORS.length],
            backgroundColor: 'transparent',
            borderWidth: s.topicId === mainTopicId ? 3 : 2,
            tension: 0.1,
            pointRadius: 2,
            spanGaps: true,
        };
    });
    return { labels, datasets };
}

// ---------------------------------------------------------------------------
// Location Insights helpers — deck-side mirrors of the tab's metric logic, so
// the store tables rank exactly the way the live heat chart does.
// ---------------------------------------------------------------------------

const locNps = (promoters, detractors, rated) =>
    rated > 0 ? ((promoters - detractors) / rated) * 100 : null;

// Mirror the tab's default metric: star ratings, switching to LLM NPS only for
// survey-only topics (no star ratings at all to rank by).
function locationMetricFor(payload) {
    const surveyOnly = (payload.total_rated || 0) === 0 && (payload.total_nps_rated || 0) > 0;
    if (!surveyOnly) {
        return {
            key: 'stars', label: 'Guest Rating', nLabel: 'reviews', columnLabel: 'Avg ★', nColumnLabel: 'Reviews',
            valueOf: (loc) => ({ value: loc.avg_rating != null ? Number(loc.avg_rating) : null, n: loc.rated_count || 0 }),
            fmt: (v) => `${v.toFixed(2)}★`,
        };
    }
    return {
        key: 'nps', label: 'NPS', nLabel: 'scored posts', columnLabel: 'NPS', nColumnLabel: 'Scored',
        valueOf: (loc) => ({ value: locNps(loc.nps_promoters, loc.nps_detractors, loc.nps_rated), n: loc.nps_rated || 0 }),
        fmt: (v) => `${v < 0 ? '−' : '+'}${Math.abs(Math.round(v))}`,
    };
}

// Rank stores by the metric among those with enough volume to be fair. The
// threshold cascade (25 -> 5 -> 1) matches the tab's heat-chart ranking.
function rankLocationStores(locations, metric) {
    const scored = locations
        .map(loc => ({ loc, m: metric.valueOf(loc) }))
        .filter(({ m }) => m.value != null && m.n > 0);
    let threshold = 25;
    let qualified = scored.filter(({ m }) => m.n >= threshold);
    if (qualified.length < 20) {
        threshold = 5;
        qualified = scored.filter(({ m }) => m.n >= threshold);
    }
    if (qualified.length < 4) {
        threshold = 1;
        qualified = scored;
    }
    qualified.sort((a, b) => b.m.value - a.m.value);
    return { ranked: qualified, threshold };
}

// Data-derived stand-in when the AI highlight/lowlight line is unavailable.
function storeHighlightFallback(loc, metric, kind) {
    const rated = metric.key === 'stars' ? (loc.rated_count || 0) : (loc.nps_rated || 0);
    if (rated === 0) return '';
    const share = metric.key === 'stars'
        ? (kind === 'top' ? (loc.star_promoters || 0) : (loc.star_detractors || 0))
        : (kind === 'top' ? (loc.nps_promoters || 0) : (loc.nps_detractors || 0));
    const pct = Math.round((100 * share) / rated);
    if (metric.key === 'stars') {
        return kind === 'top'
            ? `${pct}% five-star reviews this period`
            : `${pct}% of reviews at 3 stars or below`;
    }
    return kind === 'top' ? `${pct}% promoters this period` : `${pct}% detractors this period`;
}

function buildStoreHighlightPrompt(topicName, kind, entries, months) {
    const dir = kind === 'top' ? 'highest' : 'lowest';
    const ask = kind === 'top'
        ? 'what specifically makes it good (the concrete driver of its strong rating)'
        : 'what specifically makes it bad (the concrete recurring complaint)';
    const storeBlocks = entries.map((e, i) => {
        const lines = e.excerpts.map(x => `- [${x.tag}] "${x.text}"`).join('\n');
        return `STORE ${i + 1}: ${e.name} (${e.valueLabel}, ${e.n} ${e.nLabel})\n${lines || '- (no review text available)'}`;
    }).join('\n\n');
    return `You are an equity research analyst summarizing store-level customer reviews for ${topicName}. Below are the ${dir}-rated ${topicName} locations over the last ${months} months, each with sample reviews.

For EACH numbered store, write ONE phrase of at most 12 words naming ${ask}. Be specific to that store's reviews: name the dish, the wait time, the staff behaviour, the recurring issue. "Great service" alone is too vague. Do not restate the rating or review counts. Do not use em dashes.

Respond with EXACTLY ${entries.length} lines, one per store, each formatted as:
<store number>: <phrase>

${storeBlocks}`;
}

// Parse "N: phrase" lines back into an array aligned to the stores sent;
// unmatched stores stay null so callers can fall back per store.
function parseStoreHighlights(text, count) {
    const out = new Array(count).fill(null);
    if (!text) return out;
    for (const raw of text.split('\n')) {
        const m = raw.match(/^\s*(?:store\s*)?(\d{1,2})\s*[:.)-]\s*(.+)$/i);
        if (!m) continue;
        const idx = parseInt(m[1], 10) - 1;
        if (idx < 0 || idx >= count || out[idx]) continue;
        let phrase = stripEmDashes(m[2].trim()).replace(/^["']+|["']+$/g, '').trim();
        if (phrase.length > 110) phrase = `${phrase.slice(0, 107).trimEnd()}...`;
        if (phrase) out[idx] = phrase;
    }
    return out;
}

function processLeaverJoinerMonthly(assessmentData) {
    if (!assessmentData || assessmentData.length === 0) return null;
    const monthly = {};
    assessmentData.forEach(item => {
        if (item.rating == null || !item.time) return;
        const date = new Date(item.time * 1000);
        const month = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (!monthly[month]) monthly[month] = { sj: 0, pj: 0, pl: 0, sl: 0 };
        const r = item.rating;
        if (r > 6) monthly[month].sj++;
        else if (r > 1) monthly[month].pj++;
        else if (r < -4) monthly[month].sl++;
        else if (r < -1) monthly[month].pl++;
    });
    const sorted = Object.entries(monthly).sort((a, b) => a[0].localeCompare(b[0]));
    if (sorted.length === 0) return null;
    return {
        labels: sorted.map(([m]) => format(parseISO(m + '-01'), 'MMM yy')),
        data: sorted.map(([, v]) => ({
            strongJoiners: v.sj,
            possibleJoiners: v.pj,
            possibleLeavers: -v.pl,
            strongLeavers: -v.sl,
            net: v.sj + v.pj - v.pl - v.sl,
            total: v.sj + v.pj + v.pl + v.sl,
        })),
    };
}

function processPricingCategories(assessmentData) {
    const th = { veryReceptive: 7, moderatelyReceptive: 3, notReceptive: -3, extremelyNotReceptive: -7 };
    const cats = { 'Very Receptive': 0, 'Moderately Receptive': 0, 'Neutral': 0, 'Not Receptive': 0, 'Very Not Receptive': 0 };
    const fills = { 'Very Receptive': '#059669', 'Moderately Receptive': '#34D399', 'Neutral': '#D1D5DB', 'Not Receptive': '#F87171', 'Very Not Receptive': '#DC2626' };
    (assessmentData || []).forEach(item => {
        const r = item.rating;
        if (r == null) return;
        if (r > th.veryReceptive) cats['Very Receptive']++;
        else if (r > th.moderatelyReceptive) cats['Moderately Receptive']++;
        else if (r > th.notReceptive) cats['Neutral']++;
        else if (r > th.extremelyNotReceptive) cats['Not Receptive']++;
        else cats['Very Not Receptive']++;
    });
    const total = Object.values(cats).reduce((a, b) => a + b, 0);
    if (total === 0) return [];
    return Object.entries(cats)
        .map(([name, count]) => ({ name, value: Math.round((count / total) * 1000) / 10, fill: fills[name] }))
        .filter(c => c.value > 0);
}

// ------------------------------------------------------------------
// PPTX assembly helpers (LAYOUT_WIDE = 13.33" x 7.5")
// ------------------------------------------------------------------
const SLIDE_W = 13.33;
const SLIDE_H = 7.5;

// Blue footer bar — matches slide master from reference decks
function addFooterBar(pptx, slide) {
    try {
        slide.addShape(pptx.shapes.RECTANGLE, {
            x: 0, y: 7.0, w: SLIDE_W, h: 0.55,
            fill: { color: COLORS.accent1 },
        });
    } catch (e) {
        console.warn('[Report] addFooterBar failed:', e);
    }
}

// Add SB logo group to bottom-left of any slide (sits inside footer bar)
function addLogoGroup(slide, assets, lightText = false) {
    slide.addImage({
        data: `image/png;base64,${assets.logoIcon}`,
        x: 0.13, y: SLIDE_H - 0.37, w: 0.3, h: 0.3,
    });
    slide.addImage({
        data: `image/png;base64,${assets.logoText}`,
        x: 0.48, y: SLIDE_H - 0.37, w: 1.58, h: 0.3,
    });
}

function addTitleSlide(pptx, companyName, assets) {
    const slide = pptx.addSlide();

    // Full-bleed background image (dark blue data waves)
    slide.addImage({
        data: `image/jpeg;base64,${assets.background}`,
        x: 0, y: 0, w: SLIDE_W, h: SLIDE_H,
        sizing: { type: 'cover', w: SLIDE_W, h: SLIDE_H },
    });

    // Dark semi-transparent overlay
    slide.addShape(pptx.shapes.RECTANGLE, {
        x: 0, y: 0, w: SLIDE_W, h: SLIDE_H,
        fill: { color: COLORS.overlay, transparency: 23 },
    });

    // Company name (centered, large, bold)
    slide.addText(companyName + ' - Scuttlebutt', {
        x: 0.25, y: 2.69, w: SLIDE_W - 0.5, h: 1.24,
        fontSize: 60, bold: true, color: COLORS.white,
        fontFace: 'Aptos', align: 'center', valign: 'bottom',
        lineSpacingMultiple: 0.9,
    });

    // Subtitle — date only
    const dateStr = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    slide.addText(dateStr, {
        x: 2.41, y: 4.11, w: 8.52, h: 0.44,
        fontSize: 20, italic: true, color: COLORS.white,
        fontFace: 'Inter', align: 'center', valign: 'middle',
    });

    // Logo group bottom-left
    addLogoGroup(slide, assets, true);

    // Slide number
    slide.slideNumber = { x: '93.5%', y: '94.4%', fontSize: 12, color: COLORS.white };
}

function addSectionDivider(pptx, title, subtitle, assets) {
    const slide = pptx.addSlide();

    // Same branded background as title
    slide.addImage({
        data: `image/jpeg;base64,${assets.background}`,
        x: 0, y: 0, w: SLIDE_W, h: SLIDE_H,
        sizing: { type: 'cover', w: SLIDE_W, h: SLIDE_H },
    });
    slide.addShape(pptx.shapes.RECTANGLE, {
        x: 0, y: 0, w: SLIDE_W, h: SLIDE_H,
        fill: { color: COLORS.overlay, transparency: 23 },
    });

    // Section title
    slide.addText(title, {
        x: 1.5, y: 2.2, w: SLIDE_W - 3, h: 1.4,
        fontSize: 42, bold: true, color: COLORS.white,
        fontFace: 'Aptos', align: 'center', valign: 'bottom',
    });

    if (subtitle) {
        slide.addText(subtitle, {
            x: 2, y: 3.9, w: SLIDE_W - 4, h: 0.5,
            fontSize: 20, italic: true, color: COLORS.white,
            fontFace: 'Inter', align: 'center',
        });
    }

    addLogoGroup(slide, assets, true);
    slide.slideNumber = { x: '95%', y: '96%', fontSize: 10, color: COLORS.white };
}

// Decode a base64 PNG to its intrinsic pixel size. pptxgenjs stretches every
// image to the w/h frame it's given (its `sizing` option is a no-op for
// data-URI images), so each image must be placed at a frame matching its true
// aspect ratio.
function imageDimensions(base64) {
    return new Promise(resolve => {
        const probe = new Image();
        probe.onload = () => resolve({ w: probe.naturalWidth || 0, h: probe.naturalHeight || 0 });
        probe.onerror = () => resolve({ w: 0, h: 0 });
        probe.src = `data:image/png;base64,${base64}`;
    });
}

// Fit an imgW×imgH image inside `area` ({x,y,w,h}) preserving aspect ratio,
// centred — never stretched.
function fitImage(imgW, imgH, area) {
    if (!imgW || !imgH) return { x: area.x, y: area.y, w: area.w, h: area.h };
    const imgAspect = imgW / imgH;
    const areaAspect = area.w / area.h;
    let w, h;
    if (imgAspect > areaAspect) { w = area.w; h = area.w / imgAspect; }
    else { h = area.h; w = area.h * imgAspect; }
    return { x: area.x + (area.w - w) / 2, y: area.y + (area.h - h) / 2, w, h };
}

async function addDataSlide(pptx, { companyName, topicName, title, chartSubtitle, imageBase64, nativeChart, summaryBullets, methodologyFootnote }, assets) {
    const slide = pptx.addSlide();
    slide.background = { color: COLORS.white };
    slide.slideNumber = { x: '93.5%', y: '94.4%', fontSize: 12, color: COLORS.white };

    // Footer bar (behind all content)
    addFooterBar(pptx, slide);

    // Topic label top-left (gray, small) — identifies which topic this slide covers
    slide.addText(topicName || companyName, {
        x: 0.92, y: 0.24, w: 2.29, h: 0.51,
        fontSize: 12, bold: true, color: COLORS.gray,
        fontFace: 'Aptos',
    });

    // Title: the AI headline reads as a self-contained line. The topic is
    // already shown in the gray label above, so no "Topic:" prefix.
    slide.addText(title, {
        x: 0.92, y: 0.39, w: 11.5, h: 1.07,
        fontSize: 24, color: COLORS.black, fontFace: 'Aptos',
        valign: 'middle', lineSpacingMultiple: 0.9,
    });

    // Blue banner with drop shadow
    const bannerY = 1.39;
    if (chartSubtitle) {
        slide.addShape(pptx.shapes.RECTANGLE, {
            x: 1.04, y: bannerY, w: 11.38, h: 0.4,
            fill: { color: COLORS.accent1 },
            shadow: { type: 'outer', blur: 4, offset: 3, angle: 45, color: '000000', opacity: 0.4 },
        });
        slide.addText(chartSubtitle, {
            x: 1.04, y: bannerY, w: 11.38, h: 0.4,
            fontSize: 14, bold: true, color: COLORS.white, fontFace: 'Aptos',
            align: 'center', valign: 'middle',
        });
    }

    // ── Chart left, bullets right — always this layout (matches reference decks) ──
    // The chart takes ~73% of the content width (sized up after feedback that
    // charts were hard to read); the bullet column tightens to compensate.
    const chartTop = 2.01;
    const chartW = 8.30;
    const chartH = 4.90;

    if (imageBase64) {
        const dims = await imageDimensions(imageBase64);
        const fit = fitImage(dims.w, dims.h, { x: 1.04, y: chartTop, w: chartW, h: chartH });
        slide.addImage({
            data: `image/png;base64,${imageBase64}`,
            x: fit.x, y: fit.y, w: fit.w, h: fit.h,
        });
    } else if (nativeChart) {
        slide.addChart(pptx.charts[nativeChart.type.toUpperCase()], nativeChart.data, {
            x: 1.04, y: chartTop, w: chartW, h: chartH,
            showTitle: false, ...nativeChart.opts,
        });
    }

    // Bullet commentary right side
    if (summaryBullets && summaryBullets.length > 0) {
        const summaryX = 9.54;
        const summaryW = 3.50;
        const bulletElements = summaryBullets.map(bullet => ({
            text: bullet,
            options: {
                fontSize: 14, color: COLORS.black, fontFace: 'Aptos',
                bullet: { code: '2022' },
                breakLine: true, paraSpaceBefore: 6, lineSpacing: 16,
                paraSpaceAfter: 10,
            },
        }));
        slide.addText(bulletElements, {
            x: summaryX, y: chartTop, w: summaryW, h: chartH,
            valign: 'top', shrinkText: true,
        });
    }

    // Methodology footnote — small gray italic line hugging the bottom of the
    // white content area (just above the footer bar), well clear of the
    // slide-number at x 93.5%/y 94.4%. Only rendered when the deck-content
    // service supplied one; Tier B/C slides without it are unchanged.
    if (typeof methodologyFootnote === 'string' && methodologyFootnote.trim()) {
        slide.addText(methodologyFootnote.trim(), {
            x: 0.92, y: 6.72, w: 11.0, h: 0.26,
            fontSize: 7.5, italic: true, color: '8A8F98',
            fontFace: 'Aptos', valign: 'bottom',
        });
    }

    // Logo group bottom-left (inside footer bar)
    addLogoGroup(slide, assets);
}

// Post Volume "by source" slide — a single pre-composed grid image of per-
// source charts fills the content area below the banner; no bullet panel.
async function addChartGridSlide(pptx, { companyName, topicName, title, chartSubtitle, imageBase64 }, assets) {
    const slide = pptx.addSlide();
    slide.background = { color: COLORS.white };
    slide.slideNumber = { x: '93.5%', y: '94.4%', fontSize: 12, color: COLORS.white };

    addFooterBar(pptx, slide);

    // Topic label top-left
    slide.addText(topicName || companyName, {
        x: 0.92, y: 0.24, w: 2.29, h: 0.51,
        fontSize: 12, bold: true, color: COLORS.gray, fontFace: 'Aptos',
    });

    // Title: the AI headline reads as a self-contained line. The topic is
    // already shown in the gray label above, so no "Topic:" prefix.
    slide.addText(title, {
        x: 0.92, y: 0.39, w: 11.5, h: 1.07,
        fontSize: 24, color: COLORS.black, fontFace: 'Aptos',
        valign: 'middle', lineSpacingMultiple: 0.9,
    });

    // Blue banner
    if (chartSubtitle) {
        slide.addShape(pptx.shapes.RECTANGLE, {
            x: 1.04, y: 1.39, w: 11.38, h: 0.4,
            fill: { color: COLORS.accent1 },
            shadow: { type: 'outer', blur: 4, offset: 3, angle: 45, color: '000000', opacity: 0.4 },
        });
        slide.addText(chartSubtitle, {
            x: 1.04, y: 1.39, w: 11.38, h: 0.4,
            fontSize: 14, bold: true, color: COLORS.white, fontFace: 'Aptos',
            align: 'center', valign: 'middle',
        });
    }

    // Grid image — fills the content area below the banner
    if (imageBase64) {
        const areaW = 12.2, areaH = 4.95, top = 2.01;
        const dims = await imageDimensions(imageBase64);
        const fit = fitImage(dims.w, dims.h, { x: (SLIDE_W - areaW) / 2, y: top, w: areaW, h: areaH });
        slide.addImage({
            data: `image/png;base64,${imageBase64}`,
            x: fit.x, y: fit.y, w: fit.w, h: fit.h,
        });
    }

    addLogoGroup(slide, assets);
}

function addSummarySlide(pptx, { companyName, title, chartSubtitle, summaryText }, assets) {
    const slide = pptx.addSlide();
    slide.background = { color: COLORS.white };
    slide.slideNumber = { x: '93.5%', y: '94.4%', fontSize: 12, color: COLORS.white };
    addFooterBar(pptx, slide);

    // Company label
    slide.addText(companyName, {
        x: 0.92, y: 0.24, w: 2.5, h: 0.3,
        fontSize: 12, bold: true, color: COLORS.gray,
        fontFace: 'Aptos',
    });

    // Title
    slide.addText(title, {
        x: 0.92, y: 0.56, w: 11.5, h: 0.8,
        fontSize: 24, color: COLORS.black, fontFace: 'Aptos',
        valign: 'middle',
    });

    // Blue banner with shadow
    let nextY = 1.39;
    if (chartSubtitle) {
        slide.addShape(pptx.shapes.RECTANGLE, {
            x: 1.04, y: nextY, w: 11.38, h: 0.4,
            fill: { color: COLORS.accent1 },
            shadow: { type: 'outer', blur: 4, offset: 3, angle: 45, color: '000000', opacity: 0.4 },
        });
        slide.addText(chartSubtitle, {
            x: 1.04, y: nextY, w: 11.38, h: 0.4,
            fontSize: 14, bold: true, color: COLORS.white, fontFace: 'Aptos',
            align: 'center', valign: 'middle',
        });
        nextY += 0.5;
    }

    // Summary text — clean paragraphs
    const paragraphs = parseSummaryParagraphs(summaryText);
    const contentTop = nextY + 0.1;
    const availableH = SLIDE_H - contentTop - 0.6;

    const textElements = [];
    if (paragraphs) {
        for (const [label, body] of Object.entries(paragraphs)) {
            textElements.push({
                text: label + ':',
                options: {
                    fontSize: 16, bold: true, color: COLORS.accent1, fontFace: 'Aptos',
                    breakLine: true, paraSpaceBefore: 16,
                },
            });
            textElements.push({
                text: body,
                options: {
                    fontSize: 14, color: COLORS.black, fontFace: 'Aptos',
                    breakLine: true, lineSpacing: 20, paraSpaceAfter: 8,
                },
            });
        }
    }

    slide.addText(textElements, {
        x: 1.04, y: contentTop, w: 11.38, h: availableH,
        valign: 'top',
    });

    addLogoGroup(slide, assets);
}

function addExecutiveSummarySlide(pptx, { companyName, topicName = null, metrics, deltaShortLabel = '30-Day' }, assets) {
    // Explicit pagination: at most 11 data rows per slide so the table never
    // bleeds off the page. Geometry: table y=2.01, rowH=0.40 -> header + 11 rows
    // = 4.8", which clears the footer. (Replaces pptxgenjs autoPage.)
    const ROWS_PER_SLIDE = 11;
    const allMetrics = metrics || [];
    const chunks = [];
    for (let i = 0; i < allMetrics.length; i += ROWS_PER_SLIDE) {
        chunks.push(allMetrics.slice(i, i + ROWS_PER_SLIDE));
    }
    if (chunks.length === 0) chunks.push([]);

    // Build table data — header reused across all paginated slides
    const headerRow = [
        { text: 'Metric', options: { bold: true, fontSize: 13, color: 'FFFFFF', fill: { color: COLORS.accent1 }, fontFace: 'Aptos', align: 'left', valign: 'middle' } },
        { text: 'Topic', options: { bold: true, fontSize: 13, color: 'FFFFFF', fill: { color: COLORS.accent1 }, fontFace: 'Aptos', align: 'left', valign: 'middle' } },
        { text: 'Current', options: { bold: true, fontSize: 13, color: 'FFFFFF', fill: { color: COLORS.accent1 }, fontFace: 'Aptos', align: 'center', valign: 'middle' } },
        { text: `${deltaShortLabel} \u0394`, options: { bold: true, fontSize: 13, color: 'FFFFFF', fill: { color: COLORS.accent1 }, fontFace: 'Aptos', align: 'center', valign: 'middle' } },
    ];

    const formatDelta = (val) => {
        if (val == null || isNaN(val)) return { text: 'N/A', color: '888888' };
        const rounded = Math.round(val * 10) / 10;
        const sign = rounded > 0 ? '+' : '';
        const color = rounded > 0 ? '27ae60' : rounded < 0 ? 'e74c3c' : '888888';
        return { text: `${sign}${rounded}`, color };
    };

    const evenRowFill = 'F7F8FA';
    const oddRowFill = 'FFFFFF';

    chunks.forEach((chunk, chunkIdx) => {
        const slide = pptx.addSlide();
        slide.background = { color: COLORS.white };
        slide.slideNumber = { x: '93.5%', y: '94.4%', fontSize: 12, color: COLORS.white };

        // Footer bar
        addFooterBar(pptx, slide);

        // Topic (or company) label
        slide.addText(topicName || companyName, {
            x: 0.92, y: 0.24, w: 2.29, h: 0.51,
            fontSize: 12, bold: true, color: COLORS.gray,
            fontFace: 'Aptos',
        });

        // Title — topic-prefixed when per-topic; "(n/total)" only when paginated
        const baseTitle = topicName ? `${topicName}: Executive Summary` : 'Executive Summary';
        const titleText = chunks.length > 1
            ? `${baseTitle} (${chunkIdx + 1}/${chunks.length})`
            : baseTitle;
        slide.addText(titleText, {
            x: 0.92, y: 0.39, w: 11.5, h: 1.07,
            fontSize: 24, color: COLORS.black, fontFace: 'Aptos',
            valign: 'middle',
        });

        // Blue banner
        slide.addShape(pptx.shapes.RECTANGLE, {
            x: 1.04, y: 1.39, w: 11.38, h: 0.4,
            fill: { color: COLORS.accent1 },
            shadow: { type: 'outer', blur: 4, offset: 3, angle: 45, color: '000000', opacity: 0.4 },
        });
        slide.addText('Key Metrics Overview', {
            x: 1.04, y: 1.39, w: 11.38, h: 0.4,
            fontSize: 14, bold: true, color: COLORS.white, fontFace: 'Aptos',
            align: 'center', valign: 'middle',
        });

        // Header repeated on every slide
        const rows = [headerRow];

        chunk.forEach((m, idx) => {
            const d30 = formatDelta(m.delta30);
            const rowFill = idx % 2 === 0 ? evenRowFill : oddRowFill;
            const cellOpts = (extra = {}) => ({
                fontSize: 13, color: '333333', fontFace: 'Aptos',
                fill: { color: rowFill }, valign: 'middle', ...extra,
            });

            rows.push([
                { text: m.metric, options: cellOpts({ bold: true, align: 'left' }) },
                { text: m.topic || '', options: cellOpts({ align: 'left' }) },
                { text: m.current != null ? String(m.current) : 'N/A', options: cellOpts({ align: 'center', bold: true }) },
                { text: d30.text, options: cellOpts({ align: 'center', color: d30.color, bold: true }) },
            ]);
        });

        slide.addTable(rows, {
            x: 1.04, y: 2.01,
            w: 11.38,
            colW: [3.2, 2.6, 2.18, 3.4],
            rowH: 0.40,
            border: { type: 'solid', pt: 0.5, color: 'E0E0E0' },
        });

        addLogoGroup(slide, assets);
    });
}

// Generic bordered table slide (store rankings). Same chrome as the exec
// summary table; columns carry their own widths/alignment, rows are arrays of
// { text, bold?, color?, align?, fontSize? } cells.
function addTableSlide(pptx, { companyName, topicName, title, chartSubtitle, columns, rows, note, methodologyFootnote }, assets) {
    const slide = pptx.addSlide();
    slide.background = { color: COLORS.white };
    slide.slideNumber = { x: '93.5%', y: '94.4%', fontSize: 12, color: COLORS.white };
    addFooterBar(pptx, slide);

    slide.addText(topicName || companyName, {
        x: 0.92, y: 0.24, w: 2.29, h: 0.51,
        fontSize: 12, bold: true, color: COLORS.gray, fontFace: 'Aptos',
    });
    slide.addText(title, {
        x: 0.92, y: 0.39, w: 11.5, h: 1.07,
        fontSize: 24, color: COLORS.black, fontFace: 'Aptos',
        valign: 'middle', lineSpacingMultiple: 0.9,
    });
    if (chartSubtitle) {
        slide.addShape(pptx.shapes.RECTANGLE, {
            x: 1.04, y: 1.39, w: 11.38, h: 0.4,
            fill: { color: COLORS.accent1 },
            shadow: { type: 'outer', blur: 4, offset: 3, angle: 45, color: '000000', opacity: 0.4 },
        });
        slide.addText(chartSubtitle, {
            x: 1.04, y: 1.39, w: 11.38, h: 0.4,
            fontSize: 14, bold: true, color: COLORS.white, fontFace: 'Aptos',
            align: 'center', valign: 'middle',
        });
    }

    const headerRow = columns.map(c => ({
        text: c.label,
        options: {
            bold: true, fontSize: 12, color: 'FFFFFF', fill: { color: COLORS.accent1 },
            fontFace: 'Aptos', align: c.align || 'left', valign: 'middle',
        },
    }));
    const bodyRows = rows.map((cells, idx) => cells.map((cell, ci) => ({
        text: cell.text,
        options: {
            fontSize: cell.fontSize || 11.5, color: cell.color || '333333',
            bold: !!cell.bold, fontFace: 'Aptos',
            fill: { color: idx % 2 === 0 ? 'F7F8FA' : 'FFFFFF' },
            align: cell.align || columns[ci].align || 'left', valign: 'middle',
        },
    })));
    // Header + 10 rows at 0.42" ends at 6.57", clear of the 7.0" footer even
    // if the odd highlight line wraps.
    slide.addTable([headerRow, ...bodyRows], {
        x: 1.04, y: 1.95, w: 11.38,
        colW: columns.map(c => c.w),
        rowH: 0.42,
        border: { type: 'solid', pt: 0.5, color: 'E0E0E0' },
    });

    const hasFootnote = typeof methodologyFootnote === 'string' && methodologyFootnote.trim();
    if (note) {
        slide.addText(note, {
            x: 1.04, y: hasFootnote ? 6.55 : 6.6, w: 11.38, h: hasFootnote ? 0.24 : 0.3,
            fontSize: 10, italic: true, color: '888888', fontFace: 'Aptos',
        });
    }
    // Methodology footnote — same treatment as addDataSlide, tucked between
    // the note line and the footer bar.
    if (hasFootnote) {
        slide.addText(methodologyFootnote.trim(), {
            x: 1.04, y: 6.8, w: 11.38, h: 0.18,
            fontSize: 7.5, italic: true, color: '8A8F98',
            fontFace: 'Aptos', valign: 'bottom',
        });
    }
    addLogoGroup(slide, assets);
}

// "What the Data Says" — full-width takeaway bullets for one topic, written
// server-side from the deck's own headlines/bullets (deck-content
// `takeaways_from` contract). Chrome and text conventions mirror addDataSlide.
function addTakeawaysSlide(pptx, { companyName, topicName, takeaways }, assets) {
    const slide = pptx.addSlide();
    slide.background = { color: COLORS.white };
    slide.slideNumber = { x: '93.5%', y: '94.4%', fontSize: 12, color: COLORS.white };
    addFooterBar(pptx, slide);

    slide.addText(topicName || companyName, {
        x: 0.92, y: 0.24, w: 2.29, h: 0.51,
        fontSize: 12, bold: true, color: COLORS.gray, fontFace: 'Aptos',
    });
    slide.addText('What the Data Says', {
        x: 0.92, y: 0.39, w: 11.5, h: 1.07,
        fontSize: 24, color: COLORS.black, fontFace: 'Aptos',
        valign: 'middle', lineSpacingMultiple: 0.9,
    });

    // Blue banner
    slide.addShape(pptx.shapes.RECTANGLE, {
        x: 1.04, y: 1.39, w: 11.38, h: 0.4,
        fill: { color: COLORS.accent1 },
        shadow: { type: 'outer', blur: 4, offset: 3, angle: 45, color: '000000', opacity: 0.4 },
    });
    slide.addText(`${topicName || companyName} – Key Takeaways`, {
        x: 1.04, y: 1.39, w: 11.38, h: 0.4,
        fontSize: 14, bold: true, color: COLORS.white, fontFace: 'Aptos',
        align: 'center', valign: 'middle',
    });

    // Full-width bullet list — same bullet conventions as the data slides.
    const bulletElements = (takeaways || []).map(t => ({
        text: t,
        options: {
            fontSize: 16, color: COLORS.black, fontFace: 'Aptos',
            bullet: { code: '2022' },
            breakLine: true, paraSpaceBefore: 8, lineSpacing: 22,
            paraSpaceAfter: 12,
        },
    }));
    slide.addText(bulletElements, {
        x: 1.04, y: 2.01, w: 11.38, h: 4.7,
        valign: 'top', shrinkText: true,
    });

    addLogoGroup(slide, assets);
}

// Methodology appendix — label + appendix paragraph for every methodology key
// the deck actually used. Small type; two-column layout past 3 entries.
function addMethodologySlide(pptx, { companyName, entries }, assets) {
    const slide = pptx.addSlide();
    slide.background = { color: COLORS.white };
    slide.slideNumber = { x: '93.5%', y: '94.4%', fontSize: 12, color: COLORS.white };
    addFooterBar(pptx, slide);

    slide.addText(companyName, {
        x: 0.92, y: 0.24, w: 2.29, h: 0.51,
        fontSize: 12, bold: true, color: COLORS.gray, fontFace: 'Aptos',
    });
    slide.addText('Methodology', {
        x: 0.92, y: 0.39, w: 11.5, h: 1.07,
        fontSize: 24, color: COLORS.black, fontFace: 'Aptos', valign: 'middle',
    });

    const twoCol = entries.length > 3;
    const colX = [1.04, 6.87];
    const colW = twoCol ? 5.55 : 11.38;
    const perCol = twoCol ? Math.ceil(entries.length / 2) : entries.length;
    for (let c = 0; c < (twoCol ? 2 : 1); c++) {
        const colEntries = entries.slice(c * perCol, (c + 1) * perCol);
        if (colEntries.length === 0) continue;
        const els = [];
        colEntries.forEach(e => {
            els.push({
                text: e.label,
                options: {
                    fontSize: 10, bold: true, color: COLORS.accent1, fontFace: 'Aptos',
                    breakLine: true, paraSpaceBefore: 8,
                },
            });
            els.push({
                text: e.appendix,
                options: {
                    fontSize: 9, color: '333333', fontFace: 'Aptos',
                    breakLine: true, lineSpacing: 12, paraSpaceAfter: 6,
                },
            });
        });
        slide.addText(els, {
            x: colX[c], y: 1.7, w: colW, h: 5.1,
            valign: 'top', shrinkText: true,
        });
    }

    addLogoGroup(slide, assets);
}

function addQuoteSlide(pptx, { companyName, topicName, title, chartSubtitle, quotes }, assets) {
    const slide = pptx.addSlide();
    slide.background = { color: COLORS.white };
    slide.slideNumber = { x: '93.5%', y: '94.4%', fontSize: 12, color: COLORS.white };

    // Footer bar (behind all content)
    addFooterBar(pptx, slide);

    // Topic label top-left (gray, small) — identifies which topic this slide covers
    slide.addText(topicName || companyName, {
        x: 0.92, y: 0.24, w: 2.29, h: 0.51,
        fontSize: 12, bold: true, color: COLORS.gray,
        fontFace: 'Aptos',
    });

    // Title: the AI headline reads as a self-contained line. The topic is
    // already shown in the gray label above, so no "Topic:" prefix.
    slide.addText(title, {
        x: 0.92, y: 0.39, w: 11.5, h: 1.07,
        fontSize: 24, color: COLORS.black, fontFace: 'Aptos',
        valign: 'middle', lineSpacingMultiple: 0.9,
    });

    // Blue banner
    if (chartSubtitle) {
        slide.addShape(pptx.shapes.RECTANGLE, {
            x: 1.04, y: 1.39, w: 11.38, h: 0.4,
            fill: { color: COLORS.accent1 },
            shadow: { type: 'outer', blur: 4, offset: 3, angle: 45, color: '000000', opacity: 0.4 },
        });
        slide.addText(chartSubtitle, {
            x: 1.04, y: 1.39, w: 11.38, h: 0.4,
            fontSize: 14, bold: true, color: COLORS.white, fontFace: 'Aptos',
            align: 'center', valign: 'middle',
        });
    }

    // 2x3 grid of quote cards — matches reference deck layout exactly
    const colX = [1.04, 6.77];
    const rowY = [1.92, 3.52, 5.13];
    const cardW = 5.38;
    const cardH = 1.48;
    const cardPadX = 0.15;
    const cardPadY = 0.10;

    const displayQuotes = (quotes || []).slice(0, 6);

    for (let i = 0; i < 6; i++) {
        const col = i % 2;
        const row = Math.floor(i / 2);
        const x = colX[col];
        const y = rowY[row];

        if (i < displayQuotes.length) {
            const q = displayQuotes[i];
            let text = q.text || '';
            if (text.length > 240) text = text.substring(0, 237) + '...';
            // Deck-content quotes carry a `brand` (competitor decks mix brands
            // on one grid); local-fallback quotes don't, and attribution then
            // renders exactly as before.
            const brand = typeof q.brand === 'string' ? q.brand.trim() : '';
            const attribution = q.date ? `${brand ? brand + ' ' : ''}\u2014 ${q.source ? q.source + ', ' : ''}${q.date}` : '';

            // Gray card background with dashed border
            slide.addShape(pptx.shapes.ROUNDED_RECTANGLE, {
                x, y, w: cardW, h: cardH,
                fill: { color: 'F2F2F2' },
                rectRadius: 0.05,
                line: { color: '262626', width: 1, dashType: 'dash' },
            });

            // Quote text
            slide.addText([
                {
                    text: `\u201C${text}\u201D`,
                    options: { fontSize: 14, color: '333D42', fontFace: 'Aptos' },
                },
                {
                    text: `\n${attribution}`,
                    options: { fontSize: 12, color: '808080', fontFace: 'Aptos' },
                },
            ], {
                x: x + cardPadX, y: y + cardPadY,
                w: cardW - cardPadX * 2, h: cardH - cardPadY * 2,
                valign: 'top', autoFit: true, lineSpacing: 14,
            });
        }
    }

    addLogoGroup(slide, assets);
}

// ------------------------------------------------------------------
// Markdown helpers for /report/generate slides
// ------------------------------------------------------------------

// Split a string on `**bold**` into pptxgenjs text fragments.
// Accepts an options object that's merged into each fragment's options.
function mdBoldToFragments(str, baseOpts = {}) {
    if (!str) return [{ text: '', options: baseOpts }];
    const parts = String(str).split(/(\*\*[^*]+\*\*)/g);
    return parts
        .filter(p => p.length > 0)
        .map(p => {
            const m = p.match(/^\*\*([^*]+)\*\*$/);
            if (m) return { text: m[1], options: { ...baseOpts, bold: true } };
            return { text: p, options: baseOpts };
        });
}

// Strip markdown bold markers — used in contexts where we can't pass fragments
// (e.g., plain title text). Keeps the visible text.
function stripMdBold(str) {
    return String(str || '').replace(/\*\*([^*]+)\*\*/g, '$1');
}

// Slide shape:
//   { status, question, headline, narrative: [{comment, sources?}], timeseries?, sources?, follow_up?, error? }
function addQASlide(pptx, { companyName, question, slide: slideData }, assets) {
    const slide = pptx.addSlide();
    slide.background = { color: COLORS.white };
    slide.slideNumber = { x: '93.5%', y: '94.4%', fontSize: 12, color: COLORS.white };
    addFooterBar(pptx, slide);

    // Company label
    slide.addText(companyName, {
        x: 0.92, y: 0.24, w: 2.29, h: 0.51,
        fontSize: 12, bold: true, color: COLORS.gray, fontFace: 'Aptos',
    });

    // Headline (top). Render bold fragments. Fallback: the question itself.
    const headlineText = stripEmDashes(slideData.headline || question || 'Additional Analysis');
    slide.addText(
        mdBoldToFragments(headlineText, { fontSize: 24, color: COLORS.black, fontFace: 'Aptos' }),
        {
            x: 0.92, y: 0.39, w: 11.5, h: 1.07,
            valign: 'middle', lineSpacingMultiple: 0.9,
        },
    );

    // Blue banner = the user's question (acts as the subtitle/context)
    if (question) {
        slide.addShape(pptx.shapes.RECTANGLE, {
            x: 1.04, y: 1.39, w: 11.38, h: 0.4,
            fill: { color: COLORS.accent1 },
            shadow: { type: 'outer', blur: 4, offset: 3, angle: 45, color: '000000', opacity: 0.4 },
        });
        slide.addText(`Q: ${stripMdBold(question)}`, {
            x: 1.04, y: 1.39, w: 11.38, h: 0.4,
            fontSize: 14, bold: true, color: COLORS.white, fontFace: 'Aptos',
            align: 'center', valign: 'middle',
        });
    }

    const bodyTop = 2.01;
    const bodyH = 4.85;

    // ── Error state ──
    if (slideData.error) {
        slide.addShape(pptx.shapes.ROUNDED_RECTANGLE, {
            x: 1.04, y: bodyTop, w: 11.38, h: 1.2,
            fill: { color: 'FEF2F2' },
            rectRadius: 0.05,
            line: { color: 'DC2626', width: 1 },
        });
        slide.addText([
            { text: 'Error generating this slide\n', options: { fontSize: 14, bold: true, color: 'DC2626', fontFace: 'Aptos' } },
            { text: String(slideData.error), options: { fontSize: 12, color: '7F1D1D', fontFace: 'Aptos' } },
        ], {
            x: 1.2, y: bodyTop + 0.15, w: 11.06, h: 1.0,
            valign: 'top',
        });
        addLogoGroup(slide, assets);
        return;
    }

    // ── Needs-confirmation state ──
    if (slideData.status === 'needs_confirmation') {
        const followUp = stripEmDashes(slideData.follow_up || 'Clarification needed to answer this question.');
        slide.addShape(pptx.shapes.ROUNDED_RECTANGLE, {
            x: 1.04, y: bodyTop, w: 11.38, h: 2.0,
            fill: { color: 'FFFBEB' },
            rectRadius: 0.05,
            line: { color: 'F59E0B', width: 1 },
        });
        slide.addText([
            { text: 'Clarification requested\n', options: { fontSize: 14, bold: true, color: 'B45309', fontFace: 'Aptos' } },
            ...mdBoldToFragments(followUp, { fontSize: 14, color: '78350F', fontFace: 'Aptos' }),
        ], {
            x: 1.2, y: bodyTop + 0.15, w: 11.06, h: 1.8,
            valign: 'top', lineSpacing: 20,
        });
        addLogoGroup(slide, assets);
        return;
    }

    // ── Executed (normal) state ──
    // Left side: narrative paragraphs; Right side: sources list (if any)
    const narrative = Array.isArray(slideData.narrative) ? slideData.narrative : [];
    const hasSources = Array.isArray(slideData.sources) && slideData.sources.length > 0;

    const leftX = 1.04;
    const leftW = hasSources ? 7.44 : 11.38;

    const narrativeFragments = [];
    narrative.forEach((item) => {
        const comment = stripEmDashes(item?.comment ?? '');
        if (!comment) return;
        const frags = mdBoldToFragments(comment, {
            fontSize: 14, color: COLORS.black, fontFace: 'Aptos',
        });
        // Inline footnote markers for item.sources if present
        if (Array.isArray(item.sources) && item.sources.length > 0) {
            frags.push({
                text: ` [${item.sources.map((_, i) => i + 1).join(',')}]`,
                options: { fontSize: 10, color: COLORS.accent1, fontFace: 'Aptos', superscript: true },
            });
        }
        // Render each narrative item as its own bullet (matches addDataSlide's
        // summaryBullets pattern). The first fragment opens the bulleted
        // paragraph; the last fragment closes it with a line break.
        frags[0].options = {
            ...frags[0].options,
            bullet: { code: '2022' },
            paraSpaceBefore: 6,
        };
        frags[frags.length - 1].options = {
            ...frags[frags.length - 1].options,
            breakLine: true,
            paraSpaceAfter: 12,
        };
        frags.forEach(f => narrativeFragments.push(f));
    });

    if (narrativeFragments.length === 0) {
        narrativeFragments.push({
            text: 'No narrative content returned for this question.',
            options: { fontSize: 14, italic: true, color: '808080', fontFace: 'Aptos' },
        });
    }

    slide.addText(narrativeFragments, {
        x: leftX, y: bodyTop, w: leftW, h: bodyH,
        valign: 'top', lineSpacing: 20, shrinkText: true,
    });

    // Sources panel on the right
    if (hasSources) {
        const rightX = 8.78;
        const rightW = 4.26;
        slide.addText('Sources', {
            x: rightX, y: bodyTop, w: rightW, h: 0.35,
            fontSize: 12, bold: true, color: COLORS.accent1, fontFace: 'Aptos',
        });
        const sourceEls = slideData.sources.slice(0, 10).map((src, i) => ({
            text: `${i + 1}. ${typeof src === 'string' ? src : (src.title || src.url || src.id || 'source')}`,
            options: {
                fontSize: 10, color: '333D42', fontFace: 'Aptos',
                breakLine: true, paraSpaceAfter: 4,
            },
        }));
        slide.addText(sourceEls, {
            x: rightX, y: bodyTop + 0.35, w: rightW, h: bodyH - 0.35,
            valign: 'top', shrinkText: true,
        });
    }

    addLogoGroup(slide, assets);
}

function addEndSlide(pptx, companyName, assets) {
    const slide = pptx.addSlide();

    // Same branded background
    slide.addImage({
        data: `image/jpeg;base64,${assets.background}`,
        x: 0, y: 0, w: SLIDE_W, h: SLIDE_H,
        sizing: { type: 'cover', w: SLIDE_W, h: SLIDE_H },
    });
    slide.addShape(pptx.shapes.RECTANGLE, {
        x: 0, y: 0, w: SLIDE_W, h: SLIDE_H,
        fill: { color: COLORS.overlay, transparency: 23 },
    });

    // "Thank You"
    slide.addText('Thank You', {
        x: 0.5, y: 2.4, w: SLIDE_W - 1, h: 1.3,
        fontSize: 60, bold: true, color: COLORS.white,
        fontFace: 'Aptos', align: 'center', valign: 'bottom',
    });

    // Company name
    slide.addText(companyName, {
        x: 2, y: 4.0, w: SLIDE_W - 4, h: 0.5,
        fontSize: 20, italic: true, color: COLORS.white,
        fontFace: 'Inter', align: 'center',
    });

    // Branding
    slide.addText('Prepared by Scuttlebutt Research', {
        x: 2, y: 4.6, w: SLIDE_W - 4, h: 0.4,
        fontSize: 14, color: COLORS.gray,
        fontFace: 'Aptos', align: 'center',
    });

    addLogoGroup(slide, assets, true);
    slide.slideNumber = { x: '95%', y: '96%', fontSize: 10, color: COLORS.white };
}

// ------------------------------------------------------------------
// ------------------------------------------------------------------
// Module-level generation state — survives component unmount so the
// report keeps building even if the user navigates away.
// ------------------------------------------------------------------
let _activeGeneration = null;  // { promise, setProgress, setGenerating }

// ------------------------------------------------------------------
// Main modal component
// ------------------------------------------------------------------
const ReportGenerator = ({ isOpen, onClose, companyData, companyId, topicMap, allPosts }) => {
    const authValue = useAuth();
    const authValueRef = useRef(authValue);
    useEffect(() => { authValueRef.current = authValue; }, [authValue]);

    // Three-step UI: 1=matrix select, 2=arrange cards, 3=additional questions
    const [step, setStep] = useState(1);

    // Additional questions (step 3) — each becomes a /report/generate question.
    // Backend caps at 25. Topic scope is optional; empty = agent infers.
    const QA_QUESTION_CAP = 25;
    const [qaQuestions, setQaQuestions] = useState(['']);
    const [qaTopicId, setQaTopicId] = useState('');  // '' = auto-detect

    // Matrix selections: { [topicId]: Set<colId> }
    const [matrixSelections, setMatrixSelections] = useState({});
    const [includeExecSummary, setIncludeExecSummary] = useState(true);
    const [includeCompetitors, setIncludeCompetitors] = useState(false);

    // Report timeframe mode
    const [reportMode, setReportMode] = useState('yearly');
    const modeConfig = REPORT_MODES[reportMode];
    const modeConfigRef = useRef(modeConfig);
    useEffect(() => { modeConfigRef.current = modeConfig; }, [modeConfig]);

    // Slide cards for arrangement (built when moving to step 2)
    const [slideCards, setSlideCards] = useState([]);
    const [groupBy, setGroupBy] = useState('type');

    // Presets — loaded from Supabase
    const [presets, setPresets] = useState([]);
    const [presetsLoaded, setPresetsLoaded] = useState(false);
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [presetName, setPresetName] = useState('');

    const [selectedTopics, setSelectedTopics] = useState({});
    const [topicsInitialized, setTopicsInitialized] = useState(false);
    const [topicTypes, setTopicTypes] = useState({});           // { [topicId]: string[] }
    const [selectedTypes, setSelectedTypes] = useState({});      // { [topicId]: { [type]: boolean } }
    const [typesLoading, setTypesLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: 0, label: '' });
    // Non-blocking register-lint findings (deck/lint-copy/) surfaced in the
    // modal after generation; the download itself is never blocked.
    const [lintWarnings, setLintWarnings] = useState([]);

    // Methodology keys actually used by this run's slides — drives the
    // appendix Methodology page. Reset at the top of generate().
    const methodologyKeysRef = useRef(new Set());

    // Competitor topics added to the matrix (loaded from topicMap competitors)
    const [competitorTopics, setCompetitorTopics] = useState([]);

    // Initialize topics
    useEffect(() => {
        const topics = Object.values(topicMap || {});
        if (topics.length > 0 && !topicsInitialized) {
            const initial = {};
            topics.forEach(t => { initial[t.id] = true; });
            setSelectedTopics(initial);
            setTopicsInitialized(true);
        }
    }, [topicMap, topicsInitialized]);

    // Assessment types that must never become report slides:
    //  - NPS / pricing are added explicitly below (avoid duplicates)
    //  - competitive_comparison is a per-competitor mention tag, not a scored
    //    sentiment series (competitive standing is covered by the LLM slides)
    //  - relevance / relevant is an internal post-relevance score, not a
    //    customer-opinion metric, and should not appear as a sentiment slide
    const EXCLUDED_ASSESSMENT_TYPES = new Set([
        'nps', 'pricing', 'competitive_comparison', 'relevance', 'relevant',
    ]);

    // Helper: fetch analysis types for a single topic
    const fetchTypesForTopic = async (topicId) => {
        let types = ['NPS', 'pricing'];
        try {
            const resp = await apiPost('topic/assessments/other-types', { topic_id: topicId });
            if (resp.ok) {
                const json = await resp.json();
                const data = json.data ?? json ?? [];
                if (data.length > 0) {
                    const extras = data.map(r => r.type || r)
                        .filter(t => t && !EXCLUDED_ASSESSMENT_TYPES.has(String(t).toLowerCase()));
                    types = ['NPS', 'pricing', ...extras];
                }
            }
        } catch (e) { /* keep defaults */ }
        return types;
    };

    // Build default matrix cells for a topic given its types
    const buildDefaultCells = (types) => {
        const cells = new Set();
        AVAILABLE_SLIDES.filter(s => s.id !== 'executiveSummary' && !s.isDynamic && !s.defaultOff).forEach(s => cells.add(s.id));
        types.forEach(t => cells.add(`sentiment-${t}`));
        return cells;
    };

    // Fetch analysis types for all topics when modal opens
    useEffect(() => {
        if (!isOpen || !topicsInitialized) return;
        const topics = Object.values(topicMap || {});
        if (topics.length === 0) return;
        const alreadyFetched = topics.every(t => topicTypes[t.id]);
        if (alreadyFetched) return;

        let cancelled = false;
        setTypesLoading(true);

        (async () => {
            const newTopicTypes = { ...topicTypes };

            await Promise.all(topics.map(async (topic) => {
                if (newTopicTypes[topic.id]) return;
                newTopicTypes[topic.id] = await fetchTypesForTopic(topic.id);
            }));

            if (!cancelled) {
                setTopicTypes(newTopicTypes);
                setTypesLoading(false);

                // Initialize matrix — default all cells selected
                setMatrixSelections(prev => {
                    const next = { ...prev };
                    topics.forEach(topic => {
                        if (next[topic.id]) return;
                        next[topic.id] = buildDefaultCells(newTopicTypes[topic.id] || ['NPS', 'pricing']);
                    });
                    return next;
                });
            }
        })();

        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, topicsInitialized, topicMap]);

    // Load presets from Supabase
    useEffect(() => {
        if (!isOpen || presetsLoaded) return;
        const userId = authValue?.userId;
        if (!userId) return;

        (async () => {
            try {
                const { data, error } = await supabase
                    .from('report_presets')
                    .select('uuid, name, config, created_at')
                    .eq('user_id', userId)
                    .order('created_at', { ascending: true });
                if (!error && data) {
                    setPresets(data.map(row => ({
                        uuid: row.uuid,
                        name: row.name,
                        ...row.config,
                    })));
                }
            } catch (e) { console.warn('[Report] Failed to load presets:', e); }
            setPresetsLoaded(true);
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, presetsLoaded]);

    // Toggle competitors — fetch types for competitor topics and add to matrix
    const toggleCompetitors = useCallback(async () => {
        if (includeCompetitors) {
            // Remove competitors
            setIncludeCompetitors(false);
            setCompetitorTopics([]);
            setMatrixSelections(prev => {
                const next = { ...prev };
                competitorTopics.forEach(ct => { delete next[ct.id]; });
                return next;
            });
            return;
        }

        setIncludeCompetitors(true);

        // Gather all competitor topics from all active topics
        const compMap = {};
        const primaryTopics = Object.values(topicMap || {}).filter(t => selectedTopics[t.id]);
        primaryTopics.forEach(topic => {
            (topic.competitors || []).forEach(comp => {
                if (!comp.id || selectedTopics[comp.id]) return; // skip if already a primary topic
                if (!compMap[comp.id]) {
                    compMap[comp.id] = {
                        id: comp.id,
                        name: comp.topic || comp.alt_name || comp.id,
                        isCompetitor: true,
                        parentTopicId: topic.id,
                    };
                }
            });
        });

        const comps = Object.values(compMap);
        setCompetitorTopics(comps);

        // Fetch types for each competitor (in parallel)
        const newTopicTypes = { ...topicTypes };
        await Promise.all(comps.map(async (comp) => {
            if (newTopicTypes[comp.id]) return;
            newTopicTypes[comp.id] = await fetchTypesForTopic(comp.id);
        }));
        setTopicTypes(newTopicTypes);

        // Initialize competitor matrix rows — match parent topic's selections,
        // but only for columns the competitor actually has data for
        setMatrixSelections(prev => {
            const next = { ...prev };
            comps.forEach(comp => {
                const parentSel = prev[comp.parentTopicId] || new Set();
                const compTypes = newTopicTypes[comp.id] || ['NPS', 'pricing'];
                const compCells = new Set();

                parentSel.forEach(colId => {
                    if (colId.startsWith('sentiment-')) {
                        // Only include if competitor has this assessment type
                        const type = colId.replace('sentiment-', '');
                        if (compTypes.includes(type)) compCells.add(colId);
                    } else {
                        compCells.add(colId);
                    }
                });

                next[comp.id] = compCells;
            });
            return next;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [includeCompetitors, topicMap, selectedTopics, topicTypes, competitorTopics]);

    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const safeSetGenerating = useCallback((val) => {
        if (mountedRef.current) setGenerating(val);
    }, []);
    const safeSetProgress = useCallback((val) => {
        if (mountedRef.current) setProgress(val);
    }, []);
    const safeSetLintWarnings = useCallback((val) => {
        if (mountedRef.current) setLintWarnings(val);
    }, []);

    const CARD_COLORS = {
        executiveSummary: '#1042C1',
        npsCompetitive: '#0057FF',
        sentimentBreakdown: '#059669',
        leaverJoiner: '#F59E0B',
        llmRecommendations: '#8B5CF6',
        llmCompetitive: '#C026D3',
        locationMap: '#0EA5E9',
        locationStores: '#14B8A6',
        postVolume: '#6C5CE7',
        postLikes: '#0891B2',
        billingTaxonomy: '#DC2626',
        locationExtremes: '#0D9488',
        volumeNpsOverlay: '#7C3AED',
    };

    const allTopics = Object.values(topicMap || {});
    const primaryTopics = allTopics.filter(t => selectedTopics[t.id]);
    // Active topics = primary topics + competitor topics (if included)
    const activeTopics = useMemo(() => {
        const topics = [...primaryTopics];
        if (includeCompetitors) {
            competitorTopics.forEach(ct => {
                if (!topics.find(t => t.id === ct.id)) topics.push(ct);
            });
        }
        return topics;
    }, [primaryTopics, includeCompetitors, competitorTopics]);

    // Build matrix columns: static slides + expanded sentiment types from all topics
    const matrixColumns = useMemo(() => {
        const cols = [];
        AVAILABLE_SLIDES.filter(s => s.id !== 'executiveSummary').forEach(s => {
            if (s.isDynamic) {
                // Expand sentiment types — union across all topics (primary + competitors)
                const allTypes = new Set();
                activeTopics.forEach(topic => {
                    (topicTypes[topic.id] || ['NPS', 'pricing']).forEach(t => allTypes.add(t));
                });
                allTypes.forEach(type => {
                    cols.push({
                        id: `sentiment-${type}`,
                        label: ANALYSIS_TYPE_LABELS[type] || titleCase(type),
                        isSentiment: true,
                        assessmentType: type,
                    });
                });
            } else {
                cols.push({ id: s.id, label: s.label, isSentiment: false });
            }
        });
        return cols;
    }, [activeTopics, topicTypes]);

    // Matrix helpers
    const isSelected = (topicId, colId) => matrixSelections[topicId]?.has(colId) || false;

    const toggleCell = (topicId, colId) => {
        setMatrixSelections(prev => {
            const current = prev[topicId] || new Set();
            const next = new Set(current);
            if (next.has(colId)) next.delete(colId); else next.add(colId);
            return { ...prev, [topicId]: next };
        });
    };

    const selectAllRow = (topicId) => {
        setMatrixSelections(prev => ({
            ...prev,
            [topicId]: new Set(matrixColumns.map(c => c.id)),
        }));
    };

    const deselectAllRow = (topicId) => {
        setMatrixSelections(prev => ({ ...prev, [topicId]: new Set() }));
    };

    const selectAllColumn = (colId) => {
        setMatrixSelections(prev => {
            const next = { ...prev };
            activeTopics.forEach(t => {
                next[t.id] = new Set([...(prev[t.id] || new Set()), colId]);
            });
            return next;
        });
    };

    const deselectAllColumn = (colId) => {
        setMatrixSelections(prev => {
            const next = { ...prev };
            activeTopics.forEach(t => {
                const s = new Set(prev[t.id] || new Set());
                s.delete(colId);
                next[t.id] = s;
            });
            return next;
        });
    };

    const getTopicTotal = (topicId) => matrixSelections[topicId]?.size || 0;
    const getColTotal = (colId) => activeTopics.filter(t => isSelected(t.id, colId)).length;
    const isRowFull = (topicId) => getTopicTotal(topicId) === matrixColumns.length;
    const isColFull = (colId) => getColTotal(colId) === activeTopics.length;

    const getTotalSlides = () => {
        let count = includeExecSummary ? 1 : 0;
        activeTopics.forEach(t => { count += (matrixSelections[t.id]?.size || 0) * 2; });
        return count;
    };

    // Preset load/save (Supabase-backed)
    const loadPreset = (preset) => {
        const next = {};
        if (preset.selections) {
            Object.entries(preset.selections).forEach(([id, arr]) => {
                next[id] = new Set(Array.isArray(arr) ? arr : [...(arr || [])]);
            });
        }
        setMatrixSelections(next);
        setIncludeExecSummary(preset.executive_summary !== false);
        if (preset.include_competitors) {
            if (!includeCompetitors) toggleCompetitors();
        }
        if (preset.group_by) setGroupBy(preset.group_by);
        if (preset.report_mode && REPORT_MODES[preset.report_mode]) setReportMode(preset.report_mode);
    };

    const savePreset = async () => {
        if (!presetName.trim()) return;
        const userId = authValueRef.current?.userId;
        if (!userId) return;

        // Convert Sets to arrays for JSON storage
        const selections = {};
        Object.entries(matrixSelections).forEach(([id, set]) => {
            selections[id] = [...set];
        });

        const config = {
            executive_summary: includeExecSummary,
            include_competitors: includeCompetitors,
            group_by: groupBy,
            report_mode: reportMode,
            selections,
        };

        try {
            const { data, error } = await supabase
                .from('report_presets')
                .insert({ user_id: userId, name: presetName, config })
                .select('uuid, name, config, created_at')
                .single();
            if (!error && data) {
                setPresets(prev => [...prev, { uuid: data.uuid, name: data.name, ...data.config }]);
            }
        } catch (e) { console.warn('[Report] Failed to save preset:', e); }

        setPresetName('');
        setShowSaveDialog(false);
    };

    // Build slide cards from matrix selections (for step 2 + generate)
    const buildSlideCards = (ordering = groupBy) => {
        const cards = [];
        const contentCards = [];

        activeTopics.forEach((topic, topicIdx) => {
            const sel = matrixSelections[topic.id] || new Set();
            matrixColumns.forEach((col, colIdx) => {
                if (!sel.has(col.id)) return;
                contentCards.push({
                    id: col.isSentiment ? `sent-${col.assessmentType}-${topic.id}` : `${col.id}-${topic.id}`,
                    slideType: col.isSentiment ? 'sentimentBreakdown' : col.id,
                    topicId: topic.id,
                    topicName: topic.name,
                    assessmentType: col.isSentiment ? col.assessmentType : undefined,
                    label: col.label,
                    color: col.isSentiment ? CARD_COLORS.sentimentBreakdown : (CARD_COLORS[col.id] || '#888'),
                    _typeOrder: colIdx,
                    _topicOrder: topicIdx,
                });
            });
        });

        if (ordering === 'type') {
            contentCards.sort((a, b) => a._typeOrder - b._typeOrder || a._topicOrder - b._topicOrder);
        } else {
            contentCards.sort((a, b) => a._topicOrder - b._topicOrder || a._typeOrder - b._typeOrder);
        }

        if (includeExecSummary) {
            cards.push({ id: 'exec', slideType: 'executiveSummary', topicId: null, topicName: null, label: 'Executive Summary', color: CARD_COLORS.executiveSummary });
        }
        cards.push(...contentCards);
        return cards;
    };

    const goToArrange = () => {
        // Sync selectedTypes from matrix for the generate pipeline
        const newSelectedTypes = {};
        activeTopics.forEach(topic => {
            const sel = matrixSelections[topic.id] || new Set();
            const typeSel = {};
            (topicTypes[topic.id] || ['NPS', 'pricing']).forEach(t => {
                typeSel[t] = sel.has(`sentiment-${t}`);
            });
            newSelectedTypes[topic.id] = typeSel;
        });
        setSelectedTypes(newSelectedTypes);
        setSlideCards(buildSlideCards(groupBy));
        setStep(2);
    };

    const handleGroupByChange = (newGroupBy) => {
        setGroupBy(newGroupBy);
        setSlideCards(buildSlideCards(newGroupBy));
    };

    // Drag-and-drop for card grid
    const dragItem = useRef(null);
    const dragOverItem = useRef(null);

    const handleDragStart = (idx) => { dragItem.current = idx; };
    const handleDragOver = (e, idx) => { e.preventDefault(); dragOverItem.current = idx; };
    const handleDrop = () => {
        if (dragItem.current == null || dragOverItem.current == null || dragItem.current === dragOverItem.current) return;
        const copy = [...slideCards];
        const [dragged] = copy.splice(dragItem.current, 1);
        copy.splice(dragOverItem.current, 0, dragged);
        setSlideCards(copy);
        dragItem.current = null;
        dragOverItem.current = null;
    };

    const removeCard = (idx) => { setSlideCards(prev => prev.filter((_, i) => i !== idx)); };

    // Question-row handlers (step 3)
    const updateQuestion = (idx, value) => {
        setQaQuestions(prev => prev.map((q, i) => (i === idx ? value : q)));
    };
    const addQuestionRow = () => {
        setQaQuestions(prev => (prev.length >= QA_QUESTION_CAP ? prev : [...prev, '']));
    };
    const removeQuestionRow = (idx) => {
        setQaQuestions(prev => {
            const next = prev.filter((_, i) => i !== idx);
            return next.length === 0 ? [''] : next;
        });
    };
    // Trimmed, non-empty list used for the request
    const activeQuestions = qaQuestions.map(q => q.trim()).filter(Boolean).slice(0, QA_QUESTION_CAP);

    // ------------------------------------------------------------------
    // Fetch posts for a specific topic + assessment type (same as chart click)
    // Returns posts with flat structure: { text, rating, confidence, time, ... }
    // ------------------------------------------------------------------
    const fetchQuotePosts = async (topicId, assessmentType, windowDays = 90) => {
        try {
            const now = new Date();
            const start = new Date(now);
            // #6: candidate window matches the report mode's reporting window
            start.setDate(now.getDate() - windowDays);

            const resp = await apiPost('topic/sentiment/posts', {
                topic_id: topicId,
                start_date: start.toISOString(),
                end_date: now.toISOString(),
                type: assessmentType,
                source_types: null,
            });
            if (!resp.ok) return [];
            const result = await resp.json();
            return Array.isArray(result) ? result : (result.data ?? []);
        } catch (e) {
            console.warn(`[Report] fetchQuotePosts failed for ${topicId}/${assessmentType}:`, e);
            return [];
        }
    };

    // Fetch slide quotes from the Flask /company-report-quotes/ endpoint —
    // server-side selection (timeframe-windowed, YouTube-filtered, scored).
    // Falls back to the local heuristic (fetchQuotePosts + selectQuotes) when
    // Flask is unavailable or returns nothing, so a backend outage never
    // blocks deck generation. Returns an array of quote objects shaped
    // { text, date, source, ... } — directly consumable by addQuoteSlide.
    //
    // `score` is a 0-100 positivity score that drives the pos/neg quote
    // split server-side (70 = balanced 3/3). Sentiment slides pass the real
    // quality score; other slide types pass the 70 default pending a
    // metric-specific mapping.
    const getReportQuotes = async ({
        topicId, assessmentType, score = 70, count = 6,
        windowDays = 90, shifts = null, granularity = 'month',
        aspectName = null, timeSeries = null, fallbackType,
    }) => {
        const flaskAddr = authValueRef.current?.flaskServerAddress;
        if (flaskAddr && companyId && topicId && assessmentType) {
            try {
                const url = `${flaskAddr}company-report-quotes/${companyId}/`;
                const resp = await fetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        topic_id: topicId,
                        assessment_type: assessmentType,
                        score,
                        count,
                        window_days: windowDays,
                        shifts: shifts || [],
                        granularity,
                        aspect_name: aspectName,
                    }),
                });
                if (resp.ok) {
                    const data = await resp.json();
                    if (Array.isArray(data?.quotes) && data.quotes.length > 0) {
                        return data.quotes;
                    }
                    console.warn(`[Report][Quotes] endpoint returned no quotes for ${topicId}/${assessmentType}; using local fallback`);
                } else {
                    console.warn('[Report][Quotes] endpoint non-OK', resp.status, resp.statusText);
                }
            } catch (e) {
                console.warn('[Report][Quotes] endpoint failed, using local fallback:', e);
            }
        }
        // Fallback: local heuristic selection over the same window.
        const posts = await fetchQuotePosts(topicId, assessmentType, windowDays);
        return selectQuotes(
            posts,
            fallbackType !== undefined ? fallbackType : assessmentType,
            count, timeSeries, granularity, aspectName,
        );
    };

    // Fetch the cascade-generated narrative + quotes for a single metric from
    // the Flask /deck-content/<topic_id>/ endpoint. One metric per call so we
    // stay well under the 200s Vercel limit; callers fan these out concurrently.
    // The server decides the window and which low-data metrics to drop.
    //
    // Returns:
    //   - the metric's slide object { metric, headline, narrative_bullets,
    //     quotes, window_days, sample, ... } on success,
    //   - { dropped: true } when the server dropped the metric for low data,
    //   - null on missing config / HTTP failure / metric absent from the
    //     response, signalling the caller to fall back to its current path.
    // `metricKey` must be the assessment_type KEY (e.g. 'nps', 'beverage_taste',
    // 'leaver_joiner') — the same key used to filter the cached views — not a
    // display label. `domain` is intentionally omitted; the backend falls back
    // to its per-topic config and then `topic_name`.
    const getDeckContent = async ({ topicId, company, metricKey, score, asOf, topicName }) => {
        const flaskAddr = authValueRef.current?.flaskServerAddress;
        if (!flaskAddr || !topicId || !metricKey) return null;
        try {
            const url = `${flaskAddr}deck-content/${topicId}/`;
            const resp = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    company,
                    metrics: [metricKey],
                    as_of: asOf,
                    ...(score != null && !isNaN(score) ? { scores: { [metricKey]: score } } : {}),
                    topic_name: topicName,
                }),
            });
            if (!resp.ok) {
                console.warn('[Report][DeckContent] endpoint non-OK', resp.status, resp.statusText, `(${topicId}/${metricKey})`);
                return null;
            }
            const data = await resp.json();
            if (Array.isArray(data?.dropped) && data.dropped.some(d => d.metric === metricKey)) {
                return { dropped: true };
            }
            const slide = Array.isArray(data?.slides) ? data.slides.find(s => s.metric === metricKey) : null;
            return slide || null;
        } catch (e) {
            console.warn(`[Report][DeckContent] failed for ${topicId}/${metricKey}, using fallback:`, e);
            return null;
        }
    };

    // Server-written copy (contract B) for slides whose data lives client-side
    // (LLM, location, likes). Sends the same KPI text the local prompt
    // builders inject; returns { headline, narrative_bullets } on success or
    // null so the caller can fall back to its fetchAISummary path.
    const getCopyOnly = async ({ topicId, company, metricLabel, timeframe, kpis, domain }) => {
        const flaskAddr = authValueRef.current?.flaskServerAddress;
        if (!flaskAddr || !topicId || !metricLabel) return null;
        try {
            const resp = await fetch(`${flaskAddr}deck-content/${topicId}/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    copy_only: {
                        company,
                        metric_label: metricLabel,
                        timeframe,
                        kpis,
                        ...(domain ? { domain } : {}),
                    },
                }),
            });
            if (!resp.ok) {
                console.warn('[Report][CopyOnly] endpoint non-OK', resp.status, resp.statusText, `(${topicId}/${metricLabel})`);
                return null;
            }
            const data = await resp.json();
            if (typeof data?.headline === 'string' && data.headline.trim()
                && Array.isArray(data?.narrative_bullets) && data.narrative_bullets.length > 0) {
                return data;
            }
            return null;
        } catch (e) {
            console.warn(`[Report][CopyOnly] failed for ${topicId}/${metricLabel}, using fallback:`, e);
            return null;
        }
    };

    // Per-topic extras (contract C: billing taxonomy, location extremes,
    // volume×NPS series) fetched once per topic per run and shared by the
    // three extras generators. Each extra may come back as { error } — its
    // slide is then skipped gracefully. Cache cleared at the top of generate().
    const extrasCacheRef = useRef({});
    const fetchDeckExtras = (topicId, company) => {
        const flaskAddr = authValueRef.current?.flaskServerAddress;
        if (!flaskAddr || !topicId) return Promise.resolve(null);
        const cache = extrasCacheRef.current;
        if (!cache[topicId]) {
            cache[topicId] = (async () => {
                try {
                    const resp = await fetch(`${flaskAddr}deck-content/${topicId}/`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ metrics: [], extras: DECK_EXTRA_KEYS, company }),
                    });
                    if (!resp.ok) {
                        console.warn('[Report][Extras] endpoint non-OK', resp.status, resp.statusText, `(${topicId})`);
                        return null;
                    }
                    const data = await resp.json();
                    return data?.extras && typeof data.extras === 'object' ? data.extras : null;
                } catch (e) {
                    console.warn(`[Report][Extras] failed for ${topicId}:`, e);
                    return null;
                }
            })();
        }
        return cache[topicId];
    };

    // ------------------------------------------------------------------
    // Generate report
    // ------------------------------------------------------------------
    const generate = useCallback(async () => {
        safeSetGenerating(true);

        // Store generation at module level so it survives component unmount
        // (e.g., user navigates away). The async chain keeps running and
        // pptx.writeFile() triggers the download regardless.
        _activeGeneration = { setProgress: safeSetProgress, setGenerating: safeSetGenerating };

        const cards = slideCards;
        const companyName = companyData?.name || 'Company';

        // Snapshot the Q&A list — state may change during the 1–3 min request.
        const questionsForGeneration = qaQuestions
            .map(q => q.trim())
            .filter(Boolean)
            .slice(0, QA_QUESTION_CAP);

        // One /report/generate call per report (topic_id optional — agent infers if absent).
        const willRunQA = questionsForGeneration.length > 0;

        const totalSteps = cards.length + 2 + (willRunQA ? 1 : 0);
        let currentStep = 0;
        const tick = (label) => {
            currentStep++;
            safeSetProgress({ current: Math.min(currentStep, totalSteps), total: totalSteps, label });
        };

        const slides = [];
        const pending = [];
        const execMetrics = [];
        const flaskAddr = authValueRef.current?.flaskServerAddress;
        // Warm the module-level style-rules cache (PE register appended to the
        // Tier-B analyst preamble). Fire-and-forget: getStyleRules never
        // throws, and on failure the fallback prompts are unchanged.
        getStyleRules(flaskAddr);
        // Warm the methodology cache too (footnotes + appendix page); same
        // fire-and-forget contract.
        getMethodology(flaskAddr);
        // Deck "as of" timestamp (unix seconds), shared by every /deck-content/ call.
        const asOf = Math.floor(Date.now() / 1000);
        // Location analytics are shared by the map + store-table generators;
        // start each run fresh so a regenerate picks up new data.
        locationAnalyticsCacheRef.current = {};
        // Per-topic extras, methodology-key tracking, and lint findings also
        // start fresh each run.
        extrasCacheRef.current = {};
        methodologyKeysRef.current = new Set();
        safeSetLintWarnings([]);

        let assets;
        try {
            assets = await fetchBrandAssets();
        } catch (e) {
            console.warn('[Report] Could not load brand assets, using fallback:', e);
            assets = { background: '', logoText: '', logoIcon: '' };
        }

        try {
            // Generate slides in card order
            // Build combined topic list: primary topics + competitor topics
            const allGenerateTopics = [...allTopics];
            competitorTopics.forEach(ct => {
                if (!allGenerateTopics.find(t => t.id === ct.id)) allGenerateTopics.push(ct);
            });

            for (const card of cards) {
                if (card.slideType === 'executiveSummary') continue; // built at end

                const topic = allGenerateTopics.find(t => t.id === card.topicId);
                if (!topic) continue;

                tick(`${card.label} \u2014 ${card.topicName}...`);
                try {
                    let result = null;
                    switch (card.slideType) {
                        case 'npsCompetitive':
                            result = await generateNpsSlides(topic, topicMap, companyName, execMetrics, asOf);
                            break;
                        case 'sentimentBreakdown':
                            // Generate for this specific assessment type only
                            result = await generateSingleSentimentSlide(topic, companyName, execMetrics, card.assessmentType, asOf);
                            break;
                        case 'leaverJoiner':
                            result = await generateLeaverJoinerSlides(topic, topicMap, companyName, execMetrics, asOf);
                            break;
                        case 'llmRecommendations':
                            result = await generateLlmRecommendationSlides(topic, companyName, execMetrics);
                            break;
                        case 'llmCompetitive':
                            result = await generateLlmCompetitiveSlides(topic, companyName, execMetrics);
                            break;
                        case 'locationMap':
                            result = await generateLocationMapSlides(topic, companyName);
                            break;
                        case 'locationStores':
                            result = await generateLocationStoreSlides(topic, companyName, execMetrics);
                            break;
                        case 'postVolume':
                            result = await generatePostVolumeSlides(topic, companyName, execMetrics);
                            break;
                        case 'postLikes':
                            result = await generatePostLikesSlides(topic, companyName, execMetrics);
                            break;
                        case 'billingTaxonomy':
                            result = await generateBillingTaxonomySlides(topic, companyName);
                            break;
                        case 'locationExtremes':
                            result = await generateLocationExtremesSlides(topic, companyName);
                            break;
                        case 'volumeNpsOverlay':
                            result = await generateVolumeNpsSlides(topic, companyName);
                            break;
                        default:
                            break;
                    }
                    if (result) {
                        // Normalize: the deck-content generators (NPS, sentiment,
                        // leaver/joiner) return { slides, resolve } so their
                        // narrative + quotes can be fetched concurrently after the
                        // sequential chart captures finish; the other generators
                        // still return a plain slide array.
                        const record = Array.isArray(result) ? { slides: result } : result;
                        // Tag each slide with its topic so titles/labels self-identify
                        // and the takeaways call can address the right topic.
                        record.slides.forEach(s => { s.topicName = topic.name; s.topicId = topic.id; });
                        pending.push(record);
                        // Track which methodology keys this deck uses (appendix
                        // page) — only when the generator produced slides.
                        const mkeys = record.slides.length === 0 ? [] : {
                            npsCompetitive: ['nps'],
                            leaverJoiner: ['leaver_joiner'],
                            llmRecommendations: ['llm_recommendations'],
                            llmCompetitive: ['llm_recommendations'],
                            locationMap: ['location_ratings'],
                            locationStores: ['location_ratings'],
                            postVolume: ['post_volume'],
                            postLikes: ['post_volume'],
                            billingTaxonomy: ['nps'],
                            locationExtremes: ['location_ratings'],
                            volumeNpsOverlay: ['post_volume'],
                        }[card.slideType] || (card.slideType === 'sentimentBreakdown'
                            ? [(card.assessmentType === 'NPS' || ANALYSIS_TYPE_LABELS[card.assessmentType]) ? 'nps' : 'custom_assessment']
                            : []);
                        mkeys.forEach(k => methodologyKeysRef.current.add(k));
                        if (record.slides.length > 0 && card.slideType === 'npsCompetitive'
                            && (topic.competitors || []).length > 0) {
                            methodologyKeysRef.current.add('competitive_comparison');
                        }
                    }
                } catch (e) { console.error(`${card.slideType} error (${card.topicName}):`, e); }
            }

            // Fan out the per-metric /deck-content/ calls concurrently (chart
            // captures already ran sequentially above), then flatten — skipping
            // any metric the server returned in `dropped`.
            await Promise.all(pending.map(p => (p.resolve ? p.resolve() : Promise.resolve())));
            for (const p of pending) {
                if (!p.dropped) slides.push(...p.slides);
            }

            // ---- "What the Data Says" takeaways (per topic, contract A) ----
            // Built from the resolved slides that carry deck-content copy
            // (_dcMetric marks them). One POST per topic; any failure skips
            // that topic's takeaways slide silently.
            const takeawaysByTopic = {};
            if (flaskAddr) {
                const takeawaysInput = new Map();
                for (const s of slides) {
                    if (s.type !== 'data' || !s._dcMetric || !s.topicId) continue;
                    if (!takeawaysInput.has(s.topicId)) takeawaysInput.set(s.topicId, []);
                    takeawaysInput.get(s.topicId).push({
                        metric: s._dcMetric,
                        headline: s.title,
                        bullets: Array.isArray(s.summaryBullets) ? s.summaryBullets : [],
                    });
                }
                await Promise.all([...takeawaysInput.entries()].map(async ([topicId, items]) => {
                    try {
                        const resp = await fetch(`${flaskAddr}deck-content/${topicId}/`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ takeaways_from: items, company: companyName }),
                        });
                        if (!resp.ok) {
                            console.warn('[Report][Takeaways] endpoint non-OK', resp.status, resp.statusText, `(${topicId})`);
                            return;
                        }
                        const data = await resp.json();
                        if (Array.isArray(data?.takeaways) && data.takeaways.length > 0) {
                            const tName = allGenerateTopics.find(t => t.id === topicId)?.name;
                            if (tName) takeawaysByTopic[tName] = data.takeaways.map(stripEmDashes);
                        }
                    } catch (e) {
                        console.warn('[Report][Takeaways] failed for topic', topicId, e);
                    }
                }));
            }

            // ---- Additional Q&A slides (step 3 / /report/generate) ----
            // Single request: all questions + optional topic_id (agent infers topic
            // per question if unset). Collected as { question, slide } pairs.
            const qaSlideRecords = [];
            if (willRunQA) {
                const mc = modeConfigRef.current;
                const timeDaysBack = mc.postStartDaysBack;
                const orgId = authValueRef.current?.userDetails?.user_org?.id;
                const flaskBase = flaskAddr || '';

                tick(`Analyzing ${questionsForGeneration.length} question${questionsForGeneration.length !== 1 ? 's' : ''}...`);
                try {
                    const token = localStorage.getItem('token');
                    const url = `${flaskBase}${flaskBase.endsWith('/') ? '' : '/'}report/generate`;
                    const body = {
                        questions: questionsForGeneration,
                        time_days_back: timeDaysBack,
                    };
                    if (qaTopicId) body.topic_id = qaTopicId;
                    if (companyId) body.company_id = companyId;
                    if (orgId) body.organization_id = orgId;

                    const resp = await fetch(url, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            ...(token ? { Authorization: `Bearer ${token}` } : {}),
                        },
                        body: JSON.stringify(body),
                    });

                    if (!resp.ok) {
                        console.warn('[Report][QA] /report/generate failed', resp.status, resp.statusText);
                        questionsForGeneration.forEach(q => {
                            qaSlideRecords.push({
                                question: q,
                                slide: { error: `Request failed: ${resp.status} ${resp.statusText}` },
                            });
                        });
                    } else {
                        const payload = await resp.json();
                        const returnedSlides = Array.isArray(payload?.slides) ? payload.slides : [];
                        // Pair each returned slide with the question it answers.
                        // Backend echoes the question on each slide if available; fall back to index.
                        returnedSlides.forEach((s, i) => {
                            const q = s.question || questionsForGeneration[i] || '';
                            qaSlideRecords.push({ question: q, slide: s });
                        });
                    }
                } catch (e) {
                    console.error('[Report][QA] generate error', e);
                    questionsForGeneration.forEach(q => {
                        qaSlideRecords.push({
                            question: q,
                            slide: { error: e?.message || 'Network error' },
                        });
                    });
                }
            }

            // ---- Assemble PPTX ----
            tick('Assembling presentation...');
            const pptx = new pptxgen();
            pptx.layout = 'LAYOUT_WIDE';
            pptx.author = 'Scuttlebutt Research';
            pptx.company = 'Scuttlebutt Research';
            pptx.subject = `${companyName} Analysis Report`;

            // Register-lint accumulator (deck/lint-copy/): every user-visible
            // string written to a slide — titles, subtitles, bullets, quotes,
            // takeaways — is collected and checked just before download.
            const lintTexts = [];
            let lintSlideIdx = 0;
            const lintAdd = (field, text, kind) => {
                if (typeof text === 'string' && text.trim()) {
                    lintTexts.push({ id: `slide${lintSlideIdx}-${field}`, text: text.trim(), kind });
                }
            };

            try { addTitleSlide(pptx, companyName, assets); } catch (e) { console.error('[Report] Title slide error:', e); }

            // Executive Summary + per-topic section dividers.
            const hasExec = cards.some(c => c.slideType === 'executiveSummary');
            const mc = modeConfigRef.current;

            // Distinct topics in slide order — drives the section dividers and
            // the per-topic Executive Summary split.
            const topicOrder = [];
            for (const s of slides) {
                if (s.topicName && !topicOrder.includes(s.topicName)) topicOrder.push(s.topicName);
            }
            const multiTopic = topicOrder.length >= 2;

            const emitExecSummary = (forTopic) => {
                if (!hasExec) return;
                // Skip metrics the deck-content service dropped for low data.
                const metrics = execMetrics.filter(m => !m._dropped && (forTopic ? m.topic === forTopic : true));
                if (metrics.length === 0) return;
                try {
                    addExecutiveSummarySlide(pptx, {
                        companyName, topicName: forTopic || null, metrics,
                        deltaShortLabel: mc.deltaShortLabel,
                    }, assets);
                } catch (e) { console.error('[Report] Exec summary error:', e); }
            };

            // "What the Data Says" — server-composed takeaways, emitted right
            // after the topic's exec summary. Missing takeaways = no slide.
            const emitTakeaways = (forTopic) => {
                const t = takeawaysByTopic[forTopic];
                if (!Array.isArray(t) || t.length === 0) return;
                try {
                    lintSlideIdx++;
                    t.forEach((tx, ti) => lintAdd(`takeaway${ti}`, tx, 'body'));
                    addTakeawaysSlide(pptx, { companyName, topicName: forTopic, takeaways: t }, assets);
                } catch (e) { console.error('[Report] Takeaways slide error:', e); }
            };

            // Single-topic report: one Executive Summary right after the title.
            // Multi-topic: each topic gets its own (emitted inside its section).
            if (!multiTopic) {
                emitExecSummary(null);
                emitTakeaways(topicOrder[0]);
            }

            // Quote slides are deferred to an Appendix section after the
            // metric slides and Q&A (title/subtitle kept so context survives).
            const appendixQuoteSlides = [];

            let sectionTopic = null;
            for (const s of slides) {
                // Multi-topic: open each topic's section with a divider slide,
                // then that topic's own Executive Summary and takeaways.
                if (multiTopic && s.topicName && s.topicName !== sectionTopic) {
                    sectionTopic = s.topicName;
                    try { addSectionDivider(pptx, s.topicName, companyName, assets); }
                    catch (e) { console.error('[Report] Section divider error:', e); }
                    emitExecSummary(s.topicName);
                    emitTakeaways(s.topicName);
                }
                if (s.type === 'quotes') {
                    appendixQuoteSlides.push(s);
                    continue;
                }
                try {
                    lintSlideIdx++;
                    lintAdd('title', s.title, 'title');
                    lintAdd('subtitle', s.chartSubtitle, 'title');
                    if (s.type === 'data') {
                        (s.summaryBullets || []).forEach((b, bi) => lintAdd(`bullet${bi}`, b, 'body'));
                        await addDataSlide(pptx, {
                            companyName,
                            topicName: s.topicName,
                            title: s.title,
                            chartSubtitle: s.chartSubtitle,
                            imageBase64: s.imageBase64,
                            nativeChart: s.nativeChart,
                            summaryBullets: s.summaryBullets,
                            methodologyFootnote: s.methodologyFootnote,
                        }, assets);
                    } else if (s.type === 'chartGrid') {
                        await addChartGridSlide(pptx, {
                            companyName,
                            topicName: s.topicName,
                            title: s.title,
                            chartSubtitle: s.chartSubtitle,
                            imageBase64: s.imageBase64,
                        }, assets);
                    } else if (s.type === 'table') {
                        addTableSlide(pptx, {
                            companyName,
                            topicName: s.topicName,
                            title: s.title,
                            chartSubtitle: s.chartSubtitle,
                            columns: s.columns,
                            rows: s.rows,
                            note: s.note,
                            methodologyFootnote: s.methodologyFootnote,
                        }, assets);
                    }
                } catch (slideErr) {
                    console.error(`[Report] Slide assembly error (${s.type}/${s.title}):`, slideErr);
                }
            }

            // Q&A slides — appended after the matrix-driven slides
            for (const rec of qaSlideRecords) {
                try {
                    lintSlideIdx++;
                    lintAdd('title', rec.slide?.headline || rec.question, 'title');
                    if (Array.isArray(rec.slide?.narrative)) {
                        rec.slide.narrative.forEach((item, ni) => lintAdd(`bullet${ni}`, item?.comment, 'body'));
                    }
                    addQASlide(pptx, {
                        companyName,
                        question: rec.question,
                        slide: rec.slide,
                    }, assets);
                } catch (qaErr) {
                    console.error('[Report] QA slide assembly error:', qaErr, rec);
                }
            }

            // ---- Appendix: quote slides, then the Methodology page ----
            // Order: metrics → Q&A → "Appendix" divider → quotes → methodology.
            const methodologyMap = await getMethodology(flaskAddr);
            const methodologyEntries = methodologyMap
                ? [...methodologyKeysRef.current]
                    .map(k => methodologyMap[k])
                    .filter(e => e && (e.appendix || e.footnote))
                    .map(e => ({ label: e.label || 'Methodology', appendix: e.appendix || e.footnote }))
                : [];
            if (appendixQuoteSlides.length > 0 || methodologyEntries.length > 0) {
                try { addSectionDivider(pptx, 'Appendix', companyName, assets); }
                catch (e) { console.error('[Report] Appendix divider error:', e); }
            }
            for (const s of appendixQuoteSlides) {
                try {
                    lintSlideIdx++;
                    lintAdd('title', s.title, 'title');
                    lintAdd('subtitle', s.chartSubtitle, 'title');
                    (s.quotes || []).forEach((q, qi) => lintAdd(`quote${qi}`, q.text, 'quote'));
                    addQuoteSlide(pptx, {
                        companyName,
                        topicName: s.topicName,
                        title: s.title,
                        chartSubtitle: s.chartSubtitle,
                        quotes: s.quotes,
                    }, assets);
                } catch (slideErr) {
                    console.error(`[Report] Appendix quote slide error (${s.title}):`, slideErr);
                }
            }
            if (methodologyEntries.length > 0) {
                try { addMethodologySlide(pptx, { companyName, entries: methodologyEntries }, assets); }
                catch (e) { console.error('[Report] Methodology slide error:', e); }
            }

            // ---- Register lint gate (non-blocking) ----
            // Failures never block the download; violations are logged and
            // surfaced as a soft warning in the modal.
            if (flaskAddr && lintTexts.length > 0) {
                try {
                    const lintResp = await fetch(`${flaskAddr}deck/lint-copy/`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ texts: lintTexts }),
                    });
                    if (lintResp.ok) {
                        const lintData = await lintResp.json();
                        const violations = Array.isArray(lintData?.violations) ? lintData.violations : [];
                        if (lintData?.clean === false && violations.length > 0) {
                            violations.forEach(v => console.warn('[Report][Lint] violation:', v));
                            safeSetLintWarnings(violations.slice(0, 5).map(v =>
                                `Register check: "${v.match}" flagged in ${v.id} (${v.reason})`));
                        }
                    } else {
                        console.warn('[Report][Lint] endpoint non-OK', lintResp.status, lintResp.statusText);
                    }
                } catch (e) {
                    console.warn('[Report][Lint] check failed (deck proceeds):', e);
                }
            }

            const fileName = `${companyName.replace(/[^a-zA-Z0-9]/g, '_')}_Report_${new Date().toISOString().slice(0, 10)}.pptx`;
            await pptx.writeFile({ fileName });
        } catch (err) {
            console.error('Report generation failed:', err);
        } finally {
            _activeGeneration = null;
            safeSetGenerating(false);
            safeSetProgress({ current: 0, total: 0, label: '' });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slideCards, companyData, topicMap, allTopics, qaQuestions, qaTopicId, companyId, safeSetGenerating, safeSetProgress]);

    // ==================================================================
    //  NPS — one chart per topic, with that topic's competitors overlaid
    // ==================================================================
    const generateNpsSlides = async (topic, topicMap, companyName, execMetrics, asOf) => {
        const mc = modeConfigRef.current;
        const slides = [];
        const competitorIds = (topic.competitors || []).map(c => c.id).filter(Boolean);
        const allIds = [topic.id, ...competitorIds];

        // Data source: topic/sentiment/overview with type='NPS', frequency='day'.
        // Returns daily rolling sentiment shares; we aggregate to ISO weeks client-side
        // so the Flask LLM gets weekly resolution (sentiment breakdown uses the same
        // endpoint and returns hundreds of daily points).
        const endDate = new Date();
        endDate.setHours(23, 59, 59, 999);
        const startDate = new Date(endDate);
        startDate.setMonth(endDate.getMonth() - mc.npsMonthsBack);

        let npsById = {};
        try {
            console.log('[Report][NPS] Fetching daily NPS from topic/sentiment/overview', {
                topicIds: allIds,
                start: startDate.toISOString(),
                end: endDate.toISOString(),
                monthsBack: mc.npsMonthsBack,
                aggregate: 'week',
            });
            const perTopic = await Promise.all(allIds.map(async (id) => {
                try {
                    const resp = await apiPost('topic/sentiment/overview', {
                        topic_id: id,
                        start_date: startDate.toISOString(),
                        end_date: endDate.toISOString(),
                        type: 'NPS',
                        frequency: 'day',
                        source_types: null,
                    });
                    if (!resp.ok) {
                        console.warn('[Report][NPS] sentiment/overview failed for topic', id, resp.status, resp.statusText);
                        return { id, daily: [], weekly: [] };
                    }
                    const json = await resp.json();
                    const rawData = json.data ?? json ?? null;
                    const timeseries = rawData?.timeseries || [];
                    if (timeseries.length === 0) return { id, daily: [], weekly: [] };
                    const daily = timeseries.map(point => ({
                        timestamp: new Date(point.bucket_start).getTime(),
                        totalPosts: point.total_posts || 0,
                        percentVeryPositive: Math.round((point.rolling_shares?.very_positive || 0) * 1000) / 10,
                        percentModeratePositive: Math.round((point.rolling_shares?.positive || 0) * 1000) / 10,
                        percentNeutral: Math.round((point.rolling_shares?.neutral || 0) * 1000) / 10,
                        percentModerateNegative: Math.round((point.rolling_shares?.negative || 0) * 1000) / 10,
                        percentVeryNegative: Math.round((point.rolling_shares?.very_negative || 0) * 1000) / 10,
                    }));
                    const weekly = aggregateNpsWeekly(daily);
                    return { id, daily, weekly };
                } catch (e) {
                    console.error('[Report][NPS] fetch error for topic', id, e);
                    return { id, daily: [], weekly: [] };
                }
            }));

            perTopic.forEach(({ id, daily, weekly }) => {
                // Shape each weekly row to match the schema normaliseNpsRows() + the
                // prompt builder expect: `month` holds the bucket key, `nps` the score.
                npsById[id] = weekly.map(r => ({
                    topic_id: id,
                    month: r.week,
                    nps: r.nps,
                    sample_size: r.sample_size,
                }));
                console.log(`[Report][NPS] topic ${id}: ${daily.length} daily → ${weekly.length} weekly points`);
            });
            console.log('[Report][NPS] weekly rows per topic:',
                Object.fromEntries(Object.entries(npsById).map(([k, v]) => [k, v.length])));
            console.log('[Report][NPS] sample weekly rows (main topic, first 5):', (npsById[topic.id] || []).slice(0, 5));
            console.log('[Report][NPS] sample weekly rows (main topic, last 3):', (npsById[topic.id] || []).slice(-3));
        } catch (e) { console.error('[Report][NPS] batch fetch error:', e); }

        const allLabels = new Set();
        const allDatasets = [];
        let colorIdx = 0;

        const mainResults = normaliseNpsRows(npsById[topic.id]);
        console.log('[Report] NPS mainResults:', mainResults.length, 'for topic', topic.id);
        if (mainResults.length === 0) return slides;
        const mainCd = prepareChartData(mainResults, topic.name, false);
        if (mainCd) {
            mainCd.labels.forEach(l => allLabels.add(l));
            mainCd.datasets.forEach(ds => { ds.borderColor = NPS_CHART_COLORS[colorIdx]; });
            allDatasets.push(...mainCd.datasets);
            colorIdx++;
        }

        for (const comp of (topic.competitors || [])) {
            const compResults = normaliseNpsRows(npsById[comp.id]);
            if (compResults.length === 0) continue;
            const cd = prepareChartData(compResults, comp.topic || comp.id, false);
            if (cd) {
                cd.labels.forEach(l => allLabels.add(l));
                const color = NPS_CHART_COLORS[colorIdx % NPS_CHART_COLORS.length];
                cd.datasets.forEach(ds => { ds.borderColor = color; });
                allDatasets.push(...cd.datasets);
                colorIdx++;
            }
        }

        const sortedLabels = [...allLabels].sort();
        const chartData = { labels: sortedLabels, datasets: allDatasets };
        const competitorNames = (topic.competitors || []).map(c => c.topic).filter(Boolean);

        const npsSeries = mainResults.map(r => ({ month: r.month, value: r.meanNPS, count: r.count || 0 }));
        const npsShifts = detectKeyShifts(npsSeries, { criticalThreshold: mc.criticalMoveThreshold, majorThreshold: mc.majorMoveThreshold });
        const deltas = computeDeltas(npsSeries, mc.deltaShortDays, mc.deltaLongDays);
        console.log('[Report][NPS] time_series built for Flask (', npsSeries.length, 'weekly points for topic', topic.name, '):', npsSeries);
        console.log('[Report][NPS] shifts detected:', npsShifts);
        console.log('[Report][NPS] deltas:', deltas);

        const totalN = mainResults.reduce((sum, r) => sum + (r.count || 0), 0);

        // Capture the chart FIRST so the on-slide metric-box values drive the
        // exec summary, the banner, and the AI prompt — the slide's numbers
        // are the source of truth (no parallel computation).
        const npsCap = await captureSentimentChart(topic, 'NPS');
        const img = npsCap?.base64 ?? await captureChartImage('nps', chartData);
        const npsKpis = npsCap?.kpis ?? null;

        const npsExecObj = {
            metric: 'NPS',
            topic: topic.name,
            current: npsKpis?.currentNps ?? deltas.current,
            delta30: deltas.delta30,
            delta365: deltas.delta365,
            postsAnalyzed: npsKpis?.postsAnalyzed ?? totalN,
        };
        execMetrics.push(npsExecObj);

        const flaskAddr = authValueRef.current?.flaskServerAddress;
        const npsPrompt = (flaskAddr && companyId) ? buildNpsCompetitiveSummaryPrompt(topic.name, npsById, competitorNames, allIds, mc.promptSections, mc.promptTimeframe, mc.npsMonthsBack, npsKpis) : null;
        if (npsPrompt) {
            console.log('[Report][NPS] full prompt sent to Flask:\n' + npsPrompt);
        } else {
            console.log('[Report][NPS] skipping Flask call (no flaskAddr/companyId):', { flaskAddr, companyId });
        }

        // Banner posts-analyzed — also from the slide boxes (or fall back).
        const bannerPosts = npsKpis?.postsAnalyzed ?? totalN;
        const bannerSubtitle = `Net Promoter Score (Based on Online Content, ${bannerPosts > 1000 ? (bannerPosts / 1000).toFixed(1) + 'k' : bannerPosts} posts analyzed)`;

        // Build the data + quote slides now with fallback copy. The narrative
        // headline/bullets and the curated quotes are filled in resolve(),
        // which the orchestrator fans out concurrently across metrics from
        // /deck-content/ (falling back to the AI summary + quote endpoints if
        // that call fails or the metric is absent). See generate().
        const dataSlide = {
            type: 'data',
            title: `NPS at ${deltas.current}${deltas.delta30 != null ? `, Change of ${deltas.delta30 >= 0 ? '+' : ''}${deltas.delta30} Points Over ${mc.deltaShortDays} Days` : ''}`,
            chartSubtitle: bannerSubtitle,
            imageBase64: img,
            summaryBullets: buildFallbackBullets(deltas, 'NPS', npsShifts, mc.deltaShortLabel, mc.deltaLongLabel),
        };
        const quotesSlide = {
            type: 'quotes',
            title: `NPS Sentiment \u2013 Key Quotes`,
            chartSubtitle: bannerSubtitle,
            quotes: [],
        };
        const record = { slides: [dataSlide, quotesSlide] };
        record.resolve = async () => {
            const dc = await getDeckContent({
                topicId: topic.id, company: companyName, metricKey: 'nps',
                score: npsKpis?.currentNps ?? deltas.current, asOf, topicName: topic.name,
            });
            if (dc?.dropped) { record.dropped = true; npsExecObj._dropped = true; return; }
            if (dc) {
                dataSlide._dcMetric = 'nps'; // marks deck-content copy for the takeaways call
                if (dc.headline) dataSlide.title = stripEmDashes(dc.headline);
                if (Array.isArray(dc.narrative_bullets) && dc.narrative_bullets.length > 0) {
                    dataSlide.summaryBullets = dc.narrative_bullets.map(stripEmDashes);
                }
                if (typeof dc.methodology_footnote === 'string' && dc.methodology_footnote.trim()) {
                    dataSlide.methodologyFootnote = stripEmDashes(dc.methodology_footnote);
                }
                const qs = (dc.quotes || []).map(q => ({ text: q.quote, date: q.date, brand: q.brand }));
                if (qs.length > 0) quotesSlide.quotes = qs; else record.slides = [dataSlide];
                return;
            }
            // Fallback: today's AI summary + quote endpoints.
            const [npsSummary, quotes] = await Promise.all([
                npsPrompt ? fetchAISummary(flaskAddr, companyId, npsPrompt, {
                    topicId: topic.id, assessmentType: 'NPS',
                    timeSeries: npsSeries, shifts: npsShifts, granularity: 'week',
                }) : Promise.resolve(null),
                getReportQuotes({
                    topicId: topic.id, assessmentType: 'NPS',
                    windowDays: mc.quotesWindowDays, shifts: npsShifts,
                    granularity: 'week', aspectName: topic?.name, timeSeries: npsSeries,
                }),
            ]);
            const headline = deriveHeadline(npsSummary, null, mc.promptSections[0]);
            if (headline) dataSlide.title = headline;
            const npsBullets = parseSummaryBullets(npsSummary, mc.promptSections);
            if (npsBullets.length > 0) dataSlide.summaryBullets = npsBullets;
            if (quotes && quotes.length > 0) quotesSlide.quotes = quotes; else record.slides = [dataSlide];
        };
        return record;
    };

    // ==================================================================
    //  Sentiment Breakdown — per topic, per quality metric type
    // ==================================================================
    const generateSentimentSlides = async (topic, topicMap, companyName, execMetrics) => {
        const mc = modeConfigRef.current;
        const slides = [];

        const allTypes = topicTypes[topic.id] || ['NPS', 'pricing'];
        const topicSel = selectedTypes[topic.id] || {};
        const types = allTypes.filter(t => topicSel[t]);
        if (types.length === 0) return slides;

        const timeRange = mc.sentimentDisplayMonths;
        const endDate = new Date();
        endDate.setHours(23, 59, 59, 999);
        const displayStartDate = new Date(endDate);
        displayStartDate.setMonth(endDate.getMonth() - timeRange);
        const dataStartDate = new Date(displayStartDate);
        dataStartDate.setMonth(dataStartDate.getMonth() - mc.sentimentDataExtraMonths);

        for (const type of types) {
            try {
                const response = await apiPost('topic/sentiment/overview', {
                    topic_id: topic.id,
                    start_date: dataStartDate.toISOString(),
                    end_date: endDate.toISOString(),
                    type: type,
                    frequency: mc.sentimentFrequency,
                    source_types: null,
                });

                if (!response.ok) continue;
                const json = await response.json();
                const rawData = json.data ?? json ?? null;
                if (!rawData || !rawData.timeseries || rawData.timeseries.length === 0) continue;

                const displayStartTimestamp = displayStartDate.getTime();
                const displayEndTimestamp = endDate.getTime();

                const allChartData = rawData.timeseries.map(point => ({
                    timestamp: new Date(point.bucket_start).getTime(),
                    totalPosts: point.total_posts || 0,
                    npsVolume: 1,
                    percentVeryPositive: Math.round((point.rolling_shares?.very_positive || 0) * 1000) / 10,
                    percentModeratePositive: Math.round((point.rolling_shares?.positive || 0) * 1000) / 10,
                    percentNeutral: Math.round((point.rolling_shares?.neutral || 0) * 1000) / 10,
                    percentModerateNegative: Math.round((point.rolling_shares?.negative || 0) * 1000) / 10,
                    percentVeryNegative: Math.round((point.rolling_shares?.very_negative || 0) * 1000) / 10,
                }));

                const firstData = allChartData.find(p => p.totalPosts > 0);
                const lastData = [...allChartData].reverse().find(p => p.totalPosts > 0);
                const effectiveStart = firstData ? Math.max(firstData.timestamp, displayStartTimestamp) : displayStartTimestamp;
                const effectiveEnd = lastData ? Math.min(lastData.timestamp, displayEndTimestamp) : displayEndTimestamp;

                let chartData = allChartData.filter(p => p.timestamp >= effectiveStart && p.timestamp <= effectiveEnd);
                if (chartData.length === 0) continue;

                const totalResponses = chartData.reduce((s, d) => s + d.totalPosts, 0);

                // Build time series for movement detection
                const sentimentSeries = chartData.map(d => {
                    const ts = new Date(d.timestamp);
                    return {
                        month: `${ts.getFullYear()}-${String(ts.getMonth() + 1).padStart(2, '0')}`,
                        value: calcQualityScore(d),
                        count: d.totalPosts || 0,
                    };
                });

                const deltas = computeDeltas(sentimentSeries, mc.deltaShortDays, mc.deltaLongDays);
                const sentShifts = detectKeyShifts(sentimentSeries, { criticalThreshold: mc.criticalMoveThreshold, majorThreshold: mc.majorMoveThreshold });

                // Collect exec summary metric
                execMetrics.push({
                    metric: `${titleCase(type)} Score`,
                    topic: topic.name,
                    current: deltas.current,
                    delta30: deltas.delta30,
                    delta365: deltas.delta365,
                    postsAnalyzed: totalResponses,
                });

                const flaskAddr = authValueRef.current?.flaskServerAddress;
                const sentStats = [
                    { label: 'CURRENT SCORE', value: (deltas.current || 0).toFixed(0) },
                    { label: 'POSTS ANALYZED', value: totalResponses.toLocaleString() },
                ];
                const sentPrompt = (flaskAddr && companyId) ? buildSentimentSummaryPrompt(topic.name, type, chartData, sentStats, mc.promptSections, mc.promptTimeframe) : null;

                const [img, sentSummary, quotes] = await Promise.all([
                    captureChartImage('sentiment', { data: chartData }),
                    sentPrompt ? fetchAISummary(flaskAddr, companyId, sentPrompt, {
                        topicId: topic.id,
                        assessmentType: type,
                        timeSeries: sentimentSeries,
                        shifts: sentShifts,
                    }) : Promise.resolve(null),
                    getReportQuotes({
                        topicId: topic.id, assessmentType: type,
                        score: Math.max(0, Math.min(100, deltas.current ?? 70)),
                        windowDays: mc.quotesWindowDays, shifts: sentShifts,
                        granularity: 'month', aspectName: topic?.name, timeSeries: sentimentSeries,
                    }),
                ]);

                if (!img) continue;
                const headline = deriveHeadline(sentSummary, null, mc.promptSections[0]);

                const bannerSubtitle = `${titleCase(type)} Customer Opinion (Based on Online Content, ${totalResponses > 1000 ? (totalResponses / 1000).toFixed(1) + 'k' : totalResponses} posts analyzed)`;

                const sentBullets = parseSummaryBullets(sentSummary, mc.promptSections);
                slides.push({
                    type: 'data',
                    title: headline || `${titleCase(type)} Score at ${(deltas.current || 0).toFixed(0)}, ${deltas.delta30 != null ? (deltas.delta30 >= 0 ? 'Up' : 'Down') + ' ' + Math.abs(deltas.delta30) + ' Points' : 'Holding Steady'}`,
                    chartSubtitle: bannerSubtitle,
                    imageBase64: img,
                    summaryBullets: sentBullets.length > 0 ? sentBullets : buildFallbackBullets(deltas, `${titleCase(type)} Score`, sentShifts, mc.deltaShortLabel, mc.deltaLongLabel),
                });

                if (quotes.length > 0) {
                    slides.push({
                        type: 'quotes',
                        title: `${titleCase(type)} Sentiment \u2013 Key Quotes`,
                        chartSubtitle: bannerSubtitle,
                        quotes,
                    });
                }
            } catch (e) {
                console.error(`[Report] Sentiment ${type} error (${topic.name}):`, e);
            }
        }

        return slides;
    };

    // ==================================================================
    //  Single Sentiment — for card-based generation (one assessment type)
    // ==================================================================
    const generateSingleSentimentSlide = async (topic, companyName, execMetrics, assessmentType, asOf) => {
        const mc = modeConfigRef.current;
        const slides = [];
        const timeRange = mc.sentimentDisplayMonths;
        const endDate = new Date();
        endDate.setHours(23, 59, 59, 999);
        const displayStartDate = new Date(endDate);
        displayStartDate.setMonth(endDate.getMonth() - timeRange);
        const dataStartDate = new Date(displayStartDate);
        dataStartDate.setMonth(dataStartDate.getMonth() - mc.sentimentDataExtraMonths);

        try {
            const response = await apiPost('topic/sentiment/overview', {
                topic_id: topic.id,
                start_date: dataStartDate.toISOString(),
                end_date: endDate.toISOString(),
                type: assessmentType,
                frequency: mc.sentimentFrequency,
                source_types: null,
            });

            if (!response.ok) return slides;
            const json = await response.json();
            const rawData = json.data ?? json ?? null;
            if (!rawData || !rawData.timeseries || rawData.timeseries.length === 0) return slides;

            const displayStartTimestamp = displayStartDate.getTime();
            const displayEndTimestamp = endDate.getTime();

            const allChartData = rawData.timeseries.map(point => ({
                timestamp: new Date(point.bucket_start).getTime(),
                totalPosts: point.total_posts || 0,
                npsVolume: 1,
                percentVeryPositive: Math.round((point.rolling_shares?.very_positive || 0) * 1000) / 10,
                percentModeratePositive: Math.round((point.rolling_shares?.positive || 0) * 1000) / 10,
                percentNeutral: Math.round((point.rolling_shares?.neutral || 0) * 1000) / 10,
                percentModerateNegative: Math.round((point.rolling_shares?.negative || 0) * 1000) / 10,
                percentVeryNegative: Math.round((point.rolling_shares?.very_negative || 0) * 1000) / 10,
            }));

            const firstData = allChartData.find(p => p.totalPosts > 0);
            const lastData = [...allChartData].reverse().find(p => p.totalPosts > 0);
            const effectiveStart = firstData ? Math.max(firstData.timestamp, displayStartTimestamp) : displayStartTimestamp;
            const effectiveEnd = lastData ? Math.min(lastData.timestamp, displayEndTimestamp) : displayEndTimestamp;

            let chartData = allChartData.filter(p => p.timestamp >= effectiveStart && p.timestamp <= effectiveEnd);
            if (chartData.length === 0) return slides;

            const totalResponses = chartData.reduce((s, d) => s + d.totalPosts, 0);
            const sentimentSeries = chartData.map(d => {
                const ts = new Date(d.timestamp);
                return {
                    month: `${ts.getFullYear()}-${String(ts.getMonth() + 1).padStart(2, '0')}`,
                    value: calcQualityScore(d),
                    count: d.totalPosts || 0,
                };
            });

            const deltas = computeDeltas(sentimentSeries, mc.deltaShortDays, mc.deltaLongDays);
            const sentShifts = detectKeyShifts(sentimentSeries, { criticalThreshold: mc.criticalMoveThreshold, majorThreshold: mc.majorMoveThreshold });

            // Capture FIRST so the on-slide metric-box values drive the exec
            // summary, the banner, and the AI prompt — the slide's numbers are
            // the source of truth (no parallel computation).
            const sentCap = await captureSentimentChart(topic, assessmentType);
            const img = sentCap?.base64 ?? await captureChartImage('sentiment', { data: chartData });
            const sentKpis = sentCap?.kpis ?? null;

            const sentExecObj = {
                metric: `${titleCase(assessmentType)} Score`,
                topic: topic.name,
                current: sentKpis?.currentNps ?? deltas.current,
                delta30: deltas.delta30,
                delta365: deltas.delta365,
                postsAnalyzed: sentKpis?.postsAnalyzed ?? totalResponses,
            };
            execMetrics.push(sentExecObj);

            const flaskAddr = authValueRef.current?.flaskServerAddress;
            const sentStats = [
                { label: 'CURRENT SCORE', value: (sentKpis?.currentNps ?? deltas.current ?? 0).toFixed(0) },
                { label: 'POSTS ANALYZED', value: (sentKpis?.postsAnalyzed ?? totalResponses).toLocaleString() },
            ];
            const sentPrompt = (flaskAddr && companyId) ? buildSentimentSummaryPrompt(topic.name, assessmentType, chartData, sentStats, mc.promptSections, mc.promptTimeframe, sentKpis) : null;

            if (!img) return slides;

            const bannerPosts = sentKpis?.postsAnalyzed ?? totalResponses;
            const bannerSubtitle = `${titleCase(assessmentType)} Customer Opinion (Based on Online Content, ${bannerPosts > 1000 ? (bannerPosts / 1000).toFixed(1) + 'k' : bannerPosts} posts analyzed)`;

            // Fallback copy now; narrative + quotes filled concurrently in
            // resolve() from /deck-content/ (see generate()).
            const dataSlide = {
                type: 'data',
                title: `${titleCase(assessmentType)} Score at ${(deltas.current || 0).toFixed(0)}`,
                chartSubtitle: bannerSubtitle,
                imageBase64: img,
                summaryBullets: buildFallbackBullets(deltas, `${titleCase(assessmentType)} Score`, sentShifts, mc.deltaShortLabel, mc.deltaLongLabel),
            };
            const quotesSlide = {
                type: 'quotes',
                title: `${titleCase(assessmentType)} Sentiment \u2013 Key Quotes`,
                chartSubtitle: bannerSubtitle,
                quotes: [],
            };
            const record = { slides: [dataSlide, quotesSlide] };
            record.resolve = async () => {
                const dc = await getDeckContent({
                    topicId: topic.id, company: companyName, metricKey: assessmentType,
                    score: sentKpis?.currentNps ?? deltas.current, asOf, topicName: topic.name,
                });
                if (dc?.dropped) { record.dropped = true; sentExecObj._dropped = true; return; }
                if (dc) {
                    dataSlide._dcMetric = assessmentType; // marks deck-content copy for the takeaways call
                    if (dc.headline) dataSlide.title = stripEmDashes(dc.headline);
                    if (Array.isArray(dc.narrative_bullets) && dc.narrative_bullets.length > 0) {
                        dataSlide.summaryBullets = dc.narrative_bullets.map(stripEmDashes);
                    }
                    if (typeof dc.methodology_footnote === 'string' && dc.methodology_footnote.trim()) {
                        dataSlide.methodologyFootnote = stripEmDashes(dc.methodology_footnote);
                    }
                    const qs = (dc.quotes || []).map(q => ({ text: q.quote, date: q.date, brand: q.brand }));
                    if (qs.length > 0) quotesSlide.quotes = qs; else record.slides = [dataSlide];
                    return;
                }
                // Fallback: today's AI summary + quote endpoints.
                const [sentSummary, quotes] = await Promise.all([
                    sentPrompt ? fetchAISummary(flaskAddr, companyId, sentPrompt, {
                        topicId: topic.id, assessmentType: assessmentType,
                        timeSeries: sentimentSeries, shifts: sentShifts,
                    }) : Promise.resolve(null),
                    getReportQuotes({
                        topicId: topic.id, assessmentType: assessmentType,
                        score: Math.max(0, Math.min(100, deltas.current ?? 70)),
                        windowDays: mc.quotesWindowDays, shifts: sentShifts,
                        granularity: 'month', aspectName: topic?.name, timeSeries: sentimentSeries,
                    }),
                ]);
                const headline = deriveHeadline(sentSummary, null, mc.promptSections[0]);
                if (headline) dataSlide.title = headline;
                const singleBullets = parseSummaryBullets(sentSummary, mc.promptSections);
                if (singleBullets.length > 0) dataSlide.summaryBullets = singleBullets;
                if (quotes && quotes.length > 0) quotesSlide.quotes = quotes; else record.slides = [dataSlide];
            };
            return record;
        } catch (e) {
            console.error(`[Report] Single sentiment ${assessmentType} error (${topic.name}):`, e);
        }

        return slides;
    };

    // ==================================================================
    //  Leaver / Joiner — stacked bar chart
    // ==================================================================
    const generateLeaverJoinerSlides = async (topic, topicMap, companyName, execMetrics, asOf) => {
        const mc = modeConfigRef.current;
        const slides = [];
        const idsToFetch = [topic.id, ...(topic.competitors || []).map(c => c.id).filter(Boolean)];

        let ljData = [];
        let mainLjRows = [];
        for (const tid of idsToFetch) {
            let rows = topicMap[tid]?.assessments?.leaverJoiner;
            if (!rows) {
                try {
                    const resp = await apiPost('topic/leaver-joiner/cached', { topic_id: tid });
                    if (resp.ok) { const r = await resp.json(); rows = r.data; }
                } catch (e) { /* skip */ }
            }
            if (!Array.isArray(rows) || rows.length === 0) continue;
            // The live plot groups rows by topic_id to drive its product
            // selector — stamp it in case the cached endpoint omits the column.
            rows = rows.map(r => (r.topic_id ? r : { ...r, topic_id: tid }));
            if (tid === topic.id) mainLjRows = rows;
            ljData.push(...rows);
        }

        if (ljData.length === 0) return slides;

        const monthly = processLeaverJoinerMonthly(ljData);
        if (!monthly) return slides;

        const chartData = monthly.labels.map((label, i) => ({
            month: label,
            ...monthly.data[i],
        }));

        // Build time series
        const ljSeries = monthly.labels.map((label, i) => {
            const d = new Date(Date.parse('1 ' + label));
            const month = isNaN(d) ? null : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            return { month, value: monthly.data[i].net, count: monthly.data[i].total || 0 };
        }).filter(s => s.month);

        const deltas = computeDeltas(ljSeries, mc.deltaShortDays, mc.deltaLongDays);
        const ljShifts = detectKeyShifts(ljSeries, { criticalThreshold: mc.criticalMoveThreshold, majorThreshold: mc.majorMoveThreshold });

        // Collect exec summary metric
        const ljExecObj = {
            metric: 'Leaver/Joiner Net',
            topic: topic.name,
            current: deltas.current,
            delta30: deltas.delta30,
            delta365: deltas.delta365,
            postsAnalyzed: ljData.length,
        };
        execMetrics.push(ljExecObj);

        const flaskAddr = authValueRef.current?.flaskServerAddress;
        const ljPrompt = (flaskAddr && companyId) ? buildLeaverJoinerSummaryPrompt(topic.name, monthly, [], mc.promptSections, mc.promptTimeframe) : null;
        // Capture the real DailyLeaverJoiner chart first (sequential \u2014 shared
        // off-screen render node); fall back to the inline chart if the live
        // capture fails.
        const img = (await captureLiveComponent('leaverJoiner', topic, companyName, { ljAssessments: mainLjRows }))
            || await captureChartImage('leaverJoiner', { data: chartData });

        const bannerSubtitle = `${topic.name} \u2013 Net Adds / Losses (${mc.label})`;

        // Fallback copy now; narrative + quotes filled concurrently in resolve()
        // from /deck-content/ (see generate()).
        const dataSlide = {
            type: 'data',
            title: `Net Leaver/Joiner at ${deltas.current != null ? (deltas.current >= 0 ? '+' : '') + deltas.current : 'N/A'}`,
            chartSubtitle: bannerSubtitle,
            imageBase64: img,
            summaryBullets: buildFallbackBullets(deltas, 'Leaver/Joiner Net', ljShifts, mc.deltaShortLabel, mc.deltaLongLabel),
        };
        const quotesSlide = {
            type: 'quotes',
            title: `Net Adds / Losses \u2013 Key Quotes`,
            chartSubtitle: bannerSubtitle,
            quotes: [],
        };
        const record = { slides: [dataSlide, quotesSlide] };
        record.resolve = async () => {
            const dc = await getDeckContent({
                topicId: topic.id, company: companyName, metricKey: 'leaver_joiner',
                score: deltas.current, asOf, topicName: topic.name,
            });
            if (dc?.dropped) { record.dropped = true; ljExecObj._dropped = true; return; }
            if (dc) {
                dataSlide._dcMetric = 'leaver_joiner'; // marks deck-content copy for the takeaways call
                if (dc.headline) dataSlide.title = stripEmDashes(dc.headline);
                if (Array.isArray(dc.narrative_bullets) && dc.narrative_bullets.length > 0) {
                    dataSlide.summaryBullets = dc.narrative_bullets.map(stripEmDashes);
                }
                if (typeof dc.methodology_footnote === 'string' && dc.methodology_footnote.trim()) {
                    dataSlide.methodologyFootnote = stripEmDashes(dc.methodology_footnote);
                }
                const qs = (dc.quotes || []).map(q => ({ text: q.quote, date: q.date, brand: q.brand }));
                if (qs.length > 0) quotesSlide.quotes = qs; else record.slides = [dataSlide];
                return;
            }
            // Fallback: today's AI summary + quote endpoints.
            const [ljSummary, quotes] = await Promise.all([
                ljPrompt ? fetchAISummary(flaskAddr, companyId, ljPrompt, {
                    topicId: topic.id, assessmentType: 'leaver_joiner',
                    timeSeries: ljSeries, shifts: ljShifts,
                }) : Promise.resolve(null),
                getReportQuotes({
                    topicId: topic.id, assessmentType: 'leaver_joiner',
                    windowDays: mc.quotesWindowDays, shifts: ljShifts,
                    granularity: 'month', aspectName: topic?.name, timeSeries: ljSeries,
                }),
            ]);
            const headline = deriveHeadline(ljSummary, null, mc.promptSections[0]);
            if (headline) dataSlide.title = headline;
            const ljBullets = parseSummaryBullets(ljSummary, mc.promptSections);
            if (ljBullets.length > 0) dataSlide.summaryBullets = ljBullets;
            if (quotes && quotes.length > 0) quotesSlide.quotes = quotes; else record.slides = [dataSlide];
        };
        return record;
    };

    // ==================================================================
    //  LLM Analysis — recommendation score (LLM Recommendations tab)
    // ==================================================================
    const generateLlmRecommendationSlides = async (topic, companyName, execMetrics) => {
        const mc = modeConfigRef.current;
        const slides = [];
        const rangeKey = mc.llmTimeRange || '10d';
        const days = LLM_RANGE_DAYS[rangeKey] || 10;
        const todayStr = new Date().toISOString().split('T')[0];
        // Fetch twice the chart window so the exec delta can compare the
        // window against the one before it; the capture and the numbers
        // themselves stay scoped to `days`.
        const startStr = new Date(Date.now() - days * 2 * 86400000).toISOString().split('T')[0];
        const chartStartStr = new Date(Date.now() - days * 86400000).toISOString().split('T')[0];

        const fetchSeries = async (topicId) => {
            try {
                const resp = await apiPost('topic/llm-recommendations/moving-average', {
                    topic_id: topicId, start_date: startStr, end_date: todayStr,
                    prompt_type: null, persona: null,
                });
                if (!resp.ok) return [];
                const json = await resp.json();
                return Array.isArray(json?.data) ? json.data : [];
            } catch (e) { return []; }
        };

        const rows = await fetchSeries(topic.id);
        // Weighted score over the chart window, plus a delta against the
        // preceding window of the same length for the exec summary.
        const current = llmWindowScore(rows, days);
        const prior = llmWindowScore(rows, days, days);
        const delta30 = current.score != null && prior.score != null ? current.score - prior.score : null;
        // Thin coverage makes the score noise — require a real sample inside
        // the window or skip the slide (and its exec row) entirely.
        if (current.count < MIN_LLM_SAMPLE) return slides;

        // Competitor series feed the AI prompt; the captured page plots them too.
        const comps = (topic.competitors || []).filter(c => c.id);
        const compSeries = (await Promise.all(comps.map(async c => ({
            name: c.topic || c.alt_name || c.id,
            rows: await fetchSeries(c.id),
        })))).filter(s => s.rows.some(r => (r.count || 0) > 0));

        execMetrics.push({
            metric: 'LLM Recommendation Score',
            topic: topic.name,
            current: current.score,
            delta30,
            delta365: null,
            postsAnalyzed: current.count,
        });

        const flaskAddr = authValueRef.current?.flaskServerAddress;
        const compScores = compSeries
            .map(s => ({ name: s.name, score: llmWindowScore(s.rows, days).score }))
            .filter(s => s.score != null);
        const llmPrompt = (flaskAddr && companyId)
            ? buildLlmRecommendationSummaryPrompt(topic.name, rows, compScores, mc.promptSections, mc.promptTimeframe, days)
            : null;

        // Server copy (contract B) carries the same KPI facts the local prompt
        // injects; when it fails, the legacy fetchAISummary prompt still runs.
        const kpiText =
            `Current LLM recommendation score: ${current.score != null ? current.score : 'N/A'} of 100 over the last ${days} days (${current.count} scored answers).` +
            (delta30 != null ? ` Change vs the prior ${days}-day window: ${delta30 >= 0 ? '+' : ''}${delta30} points.` : '') +
            (compScores.length > 0 ? ` Competitor ${days}-day scores: ${compScores.map(c => `${c.name}: ${c.score}`).join(', ')}.` : '');

        // The inline fallback chart shows the same window the live capture does,
        // not the doubled fetch range the delta needs.
        const clip = (rs) => rs.filter(r => r.day >= chartStartStr);
        const [img, copy] = await Promise.all([
            // Capture the real LLM Recommendations page (chart + metric cards);
            // fall back to an inline NPSChart built from the fetched series.
            (async () => (await captureLiveComponent('llmRecommendations', topic, companyName, { llmTimeRange: rangeKey }))
                || captureChartImage('llmRecommendations', { chartData: buildLlmScoreChartData(topic.name, clip(rows), compSeries.map(s => ({ name: s.name, rows: clip(s.rows) }))) }))(),
            (async () => {
                const co = await getCopyOnly({
                    topicId: topic.id, company: companyName,
                    metricLabel: 'LLM Recommendation Score',
                    timeframe: mc.promptTimeframe, kpis: kpiText,
                });
                if (co) return { server: co };
                const legacy = llmPrompt ? await fetchAISummary(flaskAddr, companyId, llmPrompt) : null;
                return { legacyText: legacy };
            })(),
        ]);
        const llmSummary = copy?.legacyText || null;
        const headline = copy?.server
            ? stripEmDashes(copy.server.headline)
            : deriveHeadline(llmSummary, null, mc.promptSections[0]);

        const bannerSubtitle = `${topic.name} – LLM Recommendation Score (${mc.label})`;
        const methodology = await getMethodology(flaskAddr);

        if (img) {
            const bullets = copy?.server
                ? copy.server.narrative_bullets.map(stripEmDashes)
                : parseSummaryBullets(llmSummary, mc.promptSections);
            const fallback = [];
            if (current.score != null) fallback.push(`AI assistants score ${topic.name} at ${current.score} of 100 over the last ${days} days (${current.count} scored answers).`);
            if (delta30 != null) fallback.push(`${delta30 >= 0 ? 'Up' : 'Down'} ${Math.abs(delta30)} points versus the prior ${days}-day window.`);
            if (compScores.length > 0) {
                const best = [...compScores].sort((a, b) => b.score - a.score)[0];
                fallback.push(`Best-scored competitor: ${best.name} at ${best.score}.`);
            }
            if (fallback.length === 0) fallback.push('LLM recommendation data is available but too sparse this period for trend analysis.');
            slides.push({
                type: 'data',
                title: headline || (current.score != null ? `LLM Recommendation Score at ${current.score}` : `How AI Assistants Rate ${topic.name}`),
                chartSubtitle: bannerSubtitle,
                imageBase64: img,
                summaryBullets: bullets.length > 0 ? bullets : fallback,
                methodologyFootnote: methodology?.llm_recommendations?.footnote,
            });
        }

        return slides;
    };

    // ==================================================================
    //  LLM Competitive Comparison — answer position vs competitors
    // ==================================================================
    const generateLlmCompetitiveSlides = async (topic, companyName, execMetrics) => {
        const mc = modeConfigRef.current;
        const slides = [];
        const rangeKey = mc.llmTimeRange || '10d';
        const days = LLM_RANGE_DAYS[rangeKey] || 10;
        const todayStr = new Date().toISOString().split('T')[0];
        const startStr = new Date(Date.now() - days * 86400000).toISOString().split('T')[0];
        const competitors = (topic.competitors || []).filter(c => c.id);

        // One request scores the whole peer group together — a brand's absence
        // penalty depends on the answers its rivals were named in.
        let rows = [];
        try {
            const resp = await apiPost('topic/llm-recommendations/positions', {
                topic_id: topic.id,
                competitor_ids: competitors.map(c => c.id),
                start_date: startStr, end_date: todayStr,
                prompt_type: null, persona: null,
            });
            if (resp.ok) {
                const json = await resp.json();
                rows = Array.isArray(json?.data) ? json.data : [];
            }
        } catch (e) { /* no data -> skip slide */ }

        const nameFor = (topicId) => {
            if (topicId === topic.id) return topic.name;
            const c = competitors.find(x => x.id === topicId);
            return c?.topic || c?.alt_name || topicId;
        };
        const summaries = summarizeLlmPositions(rows, nameFor);
        if (summaries.length === 0) return slides;

        const mainIdx = summaries.findIndex(s => s.topicId === topic.id);
        const main = mainIdx >= 0 ? summaries[mainIdx] : null;

        // Opportunities = open-ended answers the peer group was in contention
        // for. A thin window isn't a real ranking — skip the slide (and its
        // exec row) below the minimum sample.
        const sampleSize = main ? main.opportunities
            : Math.max(...summaries.map(s => s.opportunities));
        if (sampleSize < MIN_LLM_SAMPLE) return slides;

        execMetrics.push({
            metric: 'LLM Answer Position',
            topic: topic.name,
            current: main && main.position != null
                ? `#${mainIdx + 1} of ${summaries.length} (avg ${main.position.toFixed(2)})`
                : 'Not ranked',
            delta30: null,
            delta365: null,
            postsAnalyzed: main ? main.opportunities : summaries.reduce((s, x) => s + x.opportunities, 0),
        });

        const flaskAddr = authValueRef.current?.flaskServerAddress;
        const posPrompt = (flaskAddr && companyId)
            ? buildLlmPositionSummaryPrompt(topic.name, summaries, mc.promptSections, mc.promptTimeframe)
            : null;

        // Server copy (contract B) gets the same brand-standings KPI text the
        // local prompt injects; fetchAISummary remains the fallback.
        const kpiText = `Brand standings over the last ${days} days (best position first): ` +
            summaries.map((s, i) =>
                `${i + 1}. ${s.name}: avg position ${s.position != null ? s.position.toFixed(2) : 'N/A'}, named in ${s.appearRate != null ? Math.round(s.appearRate * 100) : 0}% of ${s.opportunities} open-ended answers`).join('; ') + '.';

        const [img, copy] = await Promise.all([
            // Capture the real Competitive Comparison page (position chart +
            // brand cards); fall back to an inline LLMPositionChart.
            (async () => (await captureLiveComponent('llmCompetitive', topic, companyName, { llmTimeRange: rangeKey }))
                || captureChartImage('llmCompetitive', { chartData: buildLlmPositionChartData(topic.id, summaries) }))(),
            (async () => {
                const co = await getCopyOnly({
                    topicId: topic.id, company: companyName,
                    metricLabel: 'LLM Answer Position',
                    timeframe: mc.promptTimeframe, kpis: kpiText,
                });
                if (co) return { server: co };
                const legacy = posPrompt ? await fetchAISummary(flaskAddr, companyId, posPrompt) : null;
                return { legacyText: legacy };
            })(),
        ]);
        const posSummary = copy?.legacyText || null;
        const headline = copy?.server
            ? stripEmDashes(copy.server.headline)
            : deriveHeadline(posSummary, null, mc.promptSections[0]);

        const bannerSubtitle = `${topic.name} \u2013 LLM Competitive Comparison (${mc.label})`;
        const methodology = await getMethodology(flaskAddr);

        if (img) {
            const bullets = copy?.server
                ? copy.server.narrative_bullets.map(stripEmDashes)
                : parseSummaryBullets(posSummary, mc.promptSections);
            const fallback = summaries.slice(0, 3).map(s =>
                `${s.name}: avg position ${s.position != null ? s.position.toFixed(2) : 'N/A'}, named in ${s.appearRate != null ? Math.round(s.appearRate * 100) : 0}% of ${s.opportunities} answers`);
            const leader = summaries[0];
            slides.push({
                type: 'data',
                title: headline || (main && mainIdx === 0
                    ? `${topic.name} is the AI Assistants' First Recommendation`
                    : `${leader.name} Leads AI Recommendations${main && main.position != null ? `; ${topic.name} Ranks #${mainIdx + 1}` : ''}`),
                chartSubtitle: bannerSubtitle,
                imageBase64: img,
                summaryBullets: bullets.length > 0 ? bullets : fallback,
                methodologyFootnote: methodology?.llm_recommendations?.footnote,
            });
        }

        return slides;
    };

    // ==================================================================
    //  Location Insights — footprint map + top/bottom store tables
    // ==================================================================
    // One analytics payload feeds both location slide types; cached per run
    // (the cache is cleared at the top of generate()).
    const locationAnalyticsCacheRef = useRef({});
    const fetchLocationAnalytics = (topicId) => {
        const mc = modeConfigRef.current;
        const months = mc.locationMonths || 12;
        const key = `${topicId}|${months}`;
        const cache = locationAnalyticsCacheRef.current;
        if (!cache[key]) {
            cache[key] = (async () => {
                const endDate = new Date();
                const startDate = new Date();
                startDate.setMonth(startDate.getMonth() - months);
                try {
                    const resp = await apiPost('topic/locations/analytics', {
                        topic_id: topicId,
                        start_date: startDate.toISOString(),
                        end_date: endDate.toISOString(),
                    });
                    if (!resp.ok) return null;
                    const json = await resp.json();
                    const payload = json?.data;
                    // Topics without location data come back empty — both
                    // location slide types skip silently in that case.
                    return payload && Array.isArray(payload.locations) && payload.locations.length > 0
                        ? { payload, months, startDate, endDate }
                        : null;
                } catch (e) {
                    return null;
                }
            })();
        }
        return cache[key];
    };

    const generateLocationMapSlides = async (topic, companyName) => {
        const mc = modeConfigRef.current;
        const slides = [];
        const la = await fetchLocationAnalytics(topic.id);
        if (!la) return slides;
        const { payload, months } = la;
        const metric = locationMetricFor(payload);
        const { ranked, threshold } = rankLocationStores(payload.locations, metric);
        if (ranked.length < MIN_LOCATION_STORES) return slides;
        // Named-store-code topics carry no coordinates — nothing to map.
        const mappable = payload.locations.filter(l => l.lat != null && l.lng != null).length;
        if (mappable < MIN_MAPPABLE_LOCATIONS) return slides;

        const img = await captureLiveComponent('locationMap', topic, companyName, { locationMonths: months });
        if (!img) return slides; // the map has no inline fallback chart

        // Chain-wide weighted average across the qualifying stores.
        let weighted = 0, n = 0;
        ranked.forEach(({ m }) => { weighted += m.value * m.n; n += m.n; });
        const chainAvg = n > 0 ? weighted / n : null;
        const best = ranked[0];
        const worst = ranked[ranked.length - 1];

        // Every number here is already visible on the tab, so the bullets are
        // data-driven — no AI call for this slide.
        const bullets = [];
        bullets.push(`${payload.total_locations} locations with review activity; ${(payload.total_posts || 0).toLocaleString()} reviews over the last ${months} months.`);
        if (chainAvg != null) {
            bullets.push(`Chain-average ${metric.label.toLowerCase()} of ${metric.fmt(chainAvg)}, weighted by review volume.`);
        }
        if (ranked.length >= 2) {
            bullets.push(`Best store: ${locationDisplayName(best.loc)} at ${metric.fmt(best.m.value)}; weakest: ${locationDisplayName(worst.loc)} at ${metric.fmt(worst.m.value)}.`);
        }
        // State rollup (volume-weighted) when enough states have real volume.
        const byState = {};
        ranked.forEach(({ loc, m }) => {
            const code = locationStateOf(loc);
            if (!code) return;
            if (!byState[code]) byState[code] = { code, weighted: 0, n: 0 };
            byState[code].weighted += m.value * m.n;
            byState[code].n += m.n;
        });
        const states = Object.values(byState)
            .filter(s => s.n >= 30)
            .map(s => ({ code: s.code, value: s.weighted / s.n }))
            .sort((a, b) => b.value - a.value);
        if (states.length >= 2) {
            bullets.push(`Strongest state: ${states[0].code} at ${metric.fmt(states[0].value)}; weakest: ${states[states.length - 1].code} at ${metric.fmt(states[states.length - 1].value)}.`);
        }

        // Server copy (contract B) gets the same store-level KPI facts the
        // data-driven bullets state; those bullets remain the fallback.
        const kpiText = bullets.join(' ');
        bullets.push(`Bubble size reflects review volume; color compares each store to the chain average (min ${threshold} ${metric.nLabel}).`);

        const flaskAddr = authValueRef.current?.flaskServerAddress;
        const copy = await getCopyOnly({
            topicId: topic.id, company: companyName,
            metricLabel: `Store ${metric.label} by Location`,
            timeframe: mc.promptTimeframe, kpis: kpiText,
        });
        const methodology = await getMethodology(flaskAddr);

        slides.push({
            type: 'data',
            title: copy ? stripEmDashes(copy.headline) : `Store ${metric.label} Across ${payload.total_locations} Locations`,
            chartSubtitle: `${topic.name} – Location Performance Map (${mc.label})`,
            imageBase64: img,
            summaryBullets: copy ? copy.narrative_bullets.map(stripEmDashes) : bullets,
            methodologyFootnote: methodology?.location_ratings?.footnote,
        });
        return slides;
    };

    const generateLocationStoreSlides = async (topic, companyName, execMetrics) => {
        const mc = modeConfigRef.current;
        const slides = [];
        const la = await fetchLocationAnalytics(topic.id);
        if (!la) return slides;
        const { payload, months, startDate, endDate } = la;
        const metric = locationMetricFor(payload);
        const { ranked, threshold } = rankLocationStores(payload.locations, metric);
        if (ranked.length < MIN_LOCATION_STORES) return slides;

        // Top/bottom split with no overlap when fewer than 20 stores qualify.
        const topCount = Math.min(10, Math.ceil(ranked.length / 2));
        const bottomCount = Math.min(10, ranked.length - topCount);
        const top = ranked.slice(0, topCount);
        // Worst first on the bottom slide — that's where the reader's eye goes.
        const bottom = bottomCount > 0 ? ranked.slice(ranked.length - bottomCount).reverse() : [];

        // Exec row: chain-wide weighted average across qualifying stores.
        let weighted = 0, n = 0;
        ranked.forEach(({ m }) => { weighted += m.value * m.n; n += m.n; });
        const chainAvg = n > 0 ? weighted / n : null;
        execMetrics.push({
            metric: `Store ${metric.label}`,
            topic: topic.name,
            current: chainAvg != null ? `${metric.fmt(chainAvg)} across ${ranked.length} stores` : 'N/A',
            delta30: null,
            delta365: null,
            postsAnalyzed: n,
        });

        // --- Highlight / lowlight lines --------------------------------
        // Pull each listed store's reviews so the AI can name what
        // specifically drives its rating. Recent-first, biased toward the
        // ratings that explain the ranking; fetched in small batches and
        // capped to the last 6 months to keep payloads sane.
        const listed = [...top, ...bottom];
        const postsStart = new Date(Math.max(startDate.getTime(), endDate.getTime() - 183 * 86400000));
        const excerptsByKey = {};
        const fetchStoreExcerpts = async (loc, kind) => {
            try {
                const resp = await apiPost('topic/locations/posts', {
                    topic_id: topic.id,
                    location_key: loc.key,
                    start_date: postsStart.toISOString(),
                    end_date: endDate.toISOString(),
                });
                if (!resp.ok) return [];
                const json = await resp.json();
                const posts = (Array.isArray(json?.data) ? json.data : [])
                    .filter(p => p.text && p.text.trim().length >= 30)
                    .sort((a, b) => (b.time || 0) - (a.time || 0));
                const ratingOf = (p) => (p.rating != null ? p.rating : (p.nps_rating != null ? p.nps_rating / 2 : null));
                const wanted = kind === 'top'
                    ? posts.filter(p => (ratingOf(p) ?? 5) >= 4)
                    : posts.filter(p => (ratingOf(p) ?? 1) <= 3);
                const wantedSet = new Set(wanted);
                const fill = posts.filter(p => !wantedSet.has(p));
                return [...wanted, ...fill].slice(0, STORE_EXCERPTS_PER_STORE).map(p => ({
                    tag: p.rating != null ? `${p.rating}★` : (p.nps_rating != null ? `NPS ${p.nps_rating}/10` : 'review'),
                    text: p.text.trim().replace(/\s+/g, ' ').slice(0, STORE_EXCERPT_CHARS),
                }));
            } catch (e) {
                return [];
            }
        };
        const topSet = new Set(top);
        for (let i = 0; i < listed.length; i += 5) {
            await Promise.all(listed.slice(i, i + 5).map(async (entry) => {
                excerptsByKey[entry.loc.key] = await fetchStoreExcerpts(entry.loc, topSet.has(entry) ? 'top' : 'bottom');
            }));
        }

        const flaskAddr = authValueRef.current?.flaskServerAddress;
        const promptEntries = (entries) => entries.map(({ loc, m }) => ({
            name: locationDisplayName(loc),
            valueLabel: metric.fmt(m.value),
            n: m.n,
            nLabel: metric.nLabel,
            excerpts: excerptsByKey[loc.key] || [],
        }));
        const askHighlights = async (kind, entries) => {
            const blanks = new Array(entries.length).fill(null);
            if (!flaskAddr || !companyId || entries.length === 0) return blanks;
            if (!entries.some(e => (excerptsByKey[e.loc.key] || []).length > 0)) return blanks;
            const summary = await fetchAISummary(flaskAddr, companyId,
                buildStoreHighlightPrompt(topic.name, kind, promptEntries(entries), months));
            return parseStoreHighlights(summary, entries.length);
        };
        const [topHighlights, bottomHighlights] = await Promise.all([
            askHighlights('top', top),
            askHighlights('bottom', bottom),
        ]);

        const columnsFor = (kind) => ([
            { label: '#', w: 0.55, align: 'center' },
            { label: 'Store', w: 2.95, align: 'left' },
            { label: metric.columnLabel, w: 1.15, align: 'center' },
            { label: metric.nColumnLabel, w: 1.05, align: 'center' },
            { label: kind === 'top' ? 'Highlight – what guests praise' : 'Lowlight – what guests flag', w: 5.68, align: 'left' },
        ]);
        const buildRows = (entries, highlights, kind) => entries.map((e, i) => {
            // Rank is the store's position in the full chain ranking, so the
            // bottom table reads "#231 of 234", not "#1 of the worst".
            const rank = kind === 'top' ? i + 1 : ranked.length - i;
            const highlight = highlights[i] || storeHighlightFallback(e.loc, metric, kind);
            return [
                { text: `#${rank}`, bold: true, align: 'center' },
                { text: locationDisplayName(e.loc), bold: true },
                { text: metric.fmt(e.m.value), bold: true, align: 'center', color: kind === 'top' ? '27ae60' : 'e74c3c' },
                { text: String(e.m.n), align: 'center' },
                { text: highlight || '' },
            ];
        });

        const fmtDate = (d) => d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        const note = `Ranked by ${metric.label.toLowerCase()} among stores with ≥${threshold} ${metric.nLabel} (${ranked.length} qualify), ${fmtDate(startDate)} – ${fmtDate(endDate)}. Highlights are AI-written from each store's reviews.`;
        const subtitle = `${topic.name} – Store-Level Performance (${mc.label})`;

        slides.push({
            type: 'table',
            title: `Top ${top.length} Stores by ${metric.label}`,
            chartSubtitle: subtitle,
            columns: columnsFor('top'),
            rows: buildRows(top, topHighlights, 'top'),
            note,
        });
        if (bottom.length > 0) {
            slides.push({
                type: 'table',
                title: `Bottom ${bottom.length} Stores by ${metric.label}`,
                chartSubtitle: subtitle,
                columns: columnsFor('bottom'),
                rows: buildRows(bottom, bottomHighlights, 'bottom'),
                note,
            });
        }
        return slides;
    };

    // ==================================================================
    //  Post Volume — bar chart
    // ==================================================================
    const generatePostVolumeSlides = async (topic, companyName, execMetrics) => {
        const mc = modeConfigRef.current;
        const slides = [];
        // Look back ~2 years so we can detect, per source, where data coverage
        // stabilises (incomplete early data makes the chart deceptive).
        const COVERAGE_DAYS = 730;
        const today = new Date();
        const todayStr = today.toISOString().split('T')[0];
        const lookbackStr = new Date(today.getTime() - COVERAGE_DAYS * 86400000)
            .toISOString().split('T')[0];

        // All-sources aggregate: feeds the Exec Summary metric row.
        const aggResp = await apiPost('topic/posts/get-daily-posts', {
            topic_id: topic.id, start_date: lookbackStr, end_date: todayStr, source_type: null,
        });
        if (!aggResp.ok) return slides;
        const aggJson = await aggResp.json();
        const aggRaw = aggJson.data ?? aggJson ?? [];
        const monthly = aggregateMonthly(aggRaw, 'num_posts');
        if (monthly.labels.length === 0) return slides;
        const chartData = monthly.labels.map((label, i) => ({ month: label, Posts: monthly.values[i] }));

        const volSeries = monthly.labels.map((label, i) => {
            const d = new Date(Date.parse('1 ' + label));
            const month = isNaN(d) ? null : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            return { month, value: monthly.values[i], count: monthly.values[i] };
        }).filter(s => s.month);
        // Rolling-window deltas from the daily rows — avoids the partial-
        // current-month artefact a monthly-bucket compare produces.
        const deltas = computeRollingDeltas(aggRaw, 'num_posts', mc.deltaShortDays, mc.deltaLongDays);

        execMetrics.push({
            metric: 'Post Volume',
            topic: topic.name,
            current: deltas.current,
            delta30: deltas.delta30,
            delta365: deltas.delta365,
            postsAnalyzed: monthly.values.reduce((a, b) => a + b, 0),
        });

        // Per-source coverage: keep only sources with enough credible data.
        let sources = [];
        try {
            const srcResp = await apiPost('topic/assessments/available-sources', {
                topic_id: topic.id,
                assessment_type: 'NPS',
                start_time: Math.floor(new Date(lookbackStr).getTime() / 1000),
                end_time: Math.floor(today.getTime() / 1000),
            });
            if (srcResp.ok) {
                const sj = await srcResp.json();
                sources = (sj.data ?? sj ?? []).filter(s => s && s.value && s.value !== 'all');
            }
        } catch (e) { /* fall through to the aggregate chart */ }

        const assessed = await Promise.all(sources.map(async (src) => {
            try {
                const r = await apiPost('topic/posts/get-daily-posts', {
                    topic_id: topic.id, start_date: lookbackStr, end_date: todayStr,
                    source_type: src.value,
                });
                if (!r.ok) return null;
                const j = await r.json();
                const weekly = aggregateDailyToWeekly(j.data ?? j ?? [], 'num_posts');
                const { qualifies, coverageStart } = assessSourceCoverage(weekly, today);
                if (!qualifies) return null;
                return { source: src.value, startDate: coverageStart || weekly[0]?.weekStart || lookbackStr };
            } catch (e) { return null; }
        }));
        const qualifying = assessed.filter(Boolean);

        // 2+ qualifying sources: per-source grid replaces the aggregate chart.
        if (qualifying.length >= 2) {
            const gridSubtitle = `Post Volume by Source — ${topic.name}`;
            for (let i = 0; i < qualifying.length; i += 6) {
                const chunk = qualifying.slice(i, i + 6);
                const gridImg = await captureSourceGrid(topic, companyName, chunk);
                if (!gridImg) continue;
                const part = qualifying.length > 6 ? ` (${Math.floor(i / 6) + 1})` : '';
                slides.push({
                    type: 'chartGrid',
                    title: 'Post Volume by Source',
                    chartSubtitle: gridSubtitle + part,
                    imageBase64: gridImg,
                });
            }
            if (slides.length > 0) return slides;
            // grid capture failed entirely — fall through to a single chart.
        }

        // 0 or 1 qualifying source: a single chart (that source, or the
        // all-sources aggregate when none qualifies on its own).
        let cell;
        if (qualifying.length === 1) {
            cell = qualifying[0];
        } else {
            const aggWeekly = aggregateDailyToWeekly(aggRaw, 'num_posts');
            cell = {
                source: 'all',
                startDate: detectCoverageStartWeek(aggWeekly) || aggWeekly[0]?.weekStart || lookbackStr,
            };
        }
        const singleImg = await captureSourceGrid(topic, companyName, [cell])
            || captureChartImage('postVolume', { data: chartData });
        slides.push({
            type: 'chartGrid',
            title: 'Post Volume',
            chartSubtitle: `Post Volume \u2014 ${topic.name}`,
            imageBase64: singleImg,
        });
        return slides;
    };

    // ==================================================================
    //  Post Likes — bar chart
    // ==================================================================
    const generatePostLikesSlides = async (topic, companyName, execMetrics) => {
        const mc = modeConfigRef.current;
        const slides = [];
        // Look back ~2 years to detect where likes-data coverage stabilises.
        const today = new Date();
        const todayStr = today.toISOString().split('T')[0];
        const lookbackStr = new Date(today.getTime() - 730 * 86400000)
            .toISOString().split('T')[0];
        const resp = await apiPost('topic/posts/get-daily-likes', {
            topic_id: topic.id,
            start_date: lookbackStr,
            end_date: todayStr,
        });
        if (!resp.ok) return slides;
        const json = await resp.json();
        const rawData = json.data ?? json ?? [];
        const monthly = aggregateMonthly(rawData, 'num_posts');
        if (monthly.labels.length === 0) return slides;
        const chartData = monthly.labels.map((label, i) => ({ month: label, Likes: monthly.values[i] }));

        const likesSeries = monthly.labels.map((label, i) => {
            const d = new Date(Date.parse('1 ' + label));
            const month = isNaN(d) ? null : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            return { month, value: monthly.values[i], count: monthly.values[i] };
        }).filter(s => s.month);
        // Rolling-window deltas from the daily rows — avoids the partial-
        // current-month artefact a monthly-bucket compare produces.
        const deltas = computeRollingDeltas(rawData, 'num_posts', mc.deltaShortDays, mc.deltaLongDays);
        const likesShifts = detectKeyShifts(likesSeries, { criticalThreshold: mc.criticalMoveThreshold, majorThreshold: mc.majorMoveThreshold });

        execMetrics.push({
            metric: 'Post Likes',
            topic: topic.name,
            current: deltas.current,
            delta30: deltas.delta30,
            delta365: deltas.delta365,
            postsAnalyzed: monthly.values.reduce((a, b) => a + b, 0),
        });

        const flaskAddr = authValueRef.current?.flaskServerAddress;
        const likesPrompt = (flaskAddr && companyId) ? buildPostLikesSummaryPrompt(topic.name, monthly.labels, monthly.values, mc.promptSections, mc.promptTimeframe) : null;
        // Trim the chart to where likes-data coverage stabilises; show weekly.
        const likesWeekly = aggregateDailyToWeekly(rawData, 'num_posts');
        const likesChartStart = detectCoverageStartWeek(likesWeekly)
            || likesWeekly[0]?.weekStart || lookbackStr;
        // Server copy (contract B) with the same monthly-likes KPI text the
        // local prompt injects; fetchAISummary remains the fallback.
        const likesTotal = monthly.values.reduce((a, b) => a + b, 0);
        const kpiText =
            `Average: ${Math.round(likesTotal / monthly.values.length)} likes/month; total ${likesTotal.toLocaleString()} across ${monthly.labels.length} months.` +
            (deltas.current != null ? ` Current ${mc.deltaShortDays}-day total: ${deltas.current.toLocaleString()}.` : '') +
            (deltas.delta30 != null ? ` Change vs prior window: ${deltas.delta30 >= 0 ? '+' : ''}${deltas.delta30}.` : '') +
            ` Monthly likes: ${monthly.labels.map((label, i) => `${label}: ${monthly.values[i]}`).join(', ')}.`;

        // Post Likes is an engagement-count metric — no quote slide, so no
        // quote fetch.
        const [img, copy] = await Promise.all([
            // #4: capture the real PostLikesAnalytics chart; fall back to the
            // inline chart only if the live capture fails.
            (async () => {
                const live = await captureLiveComponent('postLikes', topic, companyName, {
                    startDate: likesChartStart, endDate: todayStr, bucket: 'week',
                });
                return live || captureChartImage('postLikes', { data: chartData });
            })(),
            (async () => {
                const co = await getCopyOnly({
                    topicId: topic.id, company: companyName,
                    metricLabel: 'Post Likes',
                    timeframe: mc.promptTimeframe, kpis: kpiText,
                });
                if (co) return { server: co };
                const legacy = likesPrompt ? await fetchAISummary(flaskAddr, companyId, likesPrompt, {
                    topicId: topic.id,
                    assessmentType: 'NPS',
                    timeSeries: likesSeries,
                    shifts: likesShifts,
                }) : null;
                return { legacyText: legacy };
            })(),
        ]);
        const likesSummary = copy?.legacyText || null;
        const headline = copy?.server
            ? stripEmDashes(copy.server.headline)
            : deriveHeadline(likesSummary, null, mc.promptSections[0]);
        const methodology = await getMethodology(flaskAddr);

        const bannerSubtitle = `${mc.label} Post Likes \u2014 ${topic.name}`;
        const likesBullets = copy?.server
            ? copy.server.narrative_bullets.map(stripEmDashes)
            : parseSummaryBullets(likesSummary, mc.promptSections);
        slides.push({
            type: 'data',
            title: headline || `Post Likes at ${deltas.current != null ? deltas.current.toLocaleString() : 'N/A'}/Month`,
            chartSubtitle: bannerSubtitle,
            imageBase64: img,
            summaryBullets: likesBullets.length > 0 ? likesBullets : buildFallbackBullets(deltas, 'Post Likes', likesShifts, mc.deltaShortLabel, mc.deltaLongLabel),
            methodologyFootnote: methodology?.post_volume?.footnote,
        });

        return slides;
    };

    // ==================================================================
    //  Extras slides (deck-content contract C, fetched once per topic)
    // ==================================================================
    // Percent fields may arrive as fractions (0-1) or percentages (0-100);
    // normalise to a display percentage.
    const asPct = (v) => {
        if (v == null || isNaN(v)) return null;
        const n = Number(v);
        return n > 0 && n <= 1 ? n * 100 : n;
    };
    const fmtPct = (v) => {
        const p = asPct(v);
        return p == null ? 'N/A' : `${Math.round(p * 10) / 10}%`;
    };

    // (a) Billing decomposition — what share of detractors is billing, split
    // into complaint buckets. Table slide; skipped when the extra errors.
    const generateBillingTaxonomySlides = async (topic, companyName) => {
        const extras = await fetchDeckExtras(topic.id, companyName);
        const bt = extras?.billing_taxonomy;
        if (!bt || bt.error || !Array.isArray(bt.buckets) || bt.buckets.length === 0) {
            if (bt?.error) console.warn('[Report][Extras] billing_taxonomy unavailable:', bt.error);
            return [];
        }
        const methodology = await getMethodology(authValueRef.current?.flaskServerAddress);
        const columns = [
            { label: 'Complaint type', w: 4.9, align: 'left' },
            { label: 'Count', w: 1.6, align: 'center' },
            { label: '% of billing complaints', w: 2.44, align: 'center' },
            { label: '% of all detractors', w: 2.44, align: 'center' },
        ];
        const rows = bt.buckets.slice(0, 10).map(b => ([
            { text: String(b.label ?? ''), bold: true },
            { text: b.count != null ? Number(b.count).toLocaleString() : 'N/A', align: 'center' },
            { text: fmtPct(b.pct_of_billing), align: 'center' },
            { text: fmtPct(b.pct_of_detractors), align: 'center' },
        ]));
        // One-line factual lead as the slide title.
        const lead = `Billing appears in ${bt.billing_flavored != null ? Number(bt.billing_flavored).toLocaleString() : 'N/A'} of ${bt.detractors != null ? Number(bt.detractors).toLocaleString() : 'N/A'} detractor posts (${fmtPct(bt.billing_pct_of_detractors)})`;
        return [{
            type: 'table',
            title: lead,
            chartSubtitle: `${topic.name} – Billing Complaint Decomposition`,
            columns,
            rows,
            note: `Counts and shares come from detractor posts classified into billing complaint buckets.`,
            methodologyFootnote: methodology?.nps?.footnote,
        }];
    };

    // (b) Top/bottom locations — table slide, best rows first then the worst.
    const generateLocationExtremesSlides = async (topic, companyName) => {
        const extras = await fetchDeckExtras(topic.id, companyName);
        const le = extras?.location_extremes;
        const topList = Array.isArray(le?.top) ? le.top : [];
        const bottomList = Array.isArray(le?.bottom) ? le.bottom : [];
        if (!le || le.error || (topList.length === 0 && bottomList.length === 0)) {
            if (le?.error) console.warn('[Report][Extras] location_extremes unavailable:', le.error);
            return [];
        }
        const methodology = await getMethodology(authValueRef.current?.flaskServerAddress);
        // Header + 10 body rows fit the table geometry — split the budget.
        const perSide = Math.max(1, Math.floor(10 / 2));
        const themesText = (t) => Array.isArray(t) ? t.join(', ') : (t || '');
        const nameOf = (l) => `${l.name || ''}${l.city_state ? ` (${l.city_state})` : ''}`;
        const buildRow = (l, kind) => ([
            { text: nameOf(l), bold: true },
            {
                text: l.mean_rating != null ? Number(l.mean_rating).toFixed(2) : 'N/A',
                bold: true, align: 'center', color: kind === 'top' ? '27ae60' : 'e74c3c',
            },
            { text: l.n_reviews != null ? Number(l.n_reviews).toLocaleString() : 'N/A', align: 'center' },
            { text: themesText(kind === 'top' ? l.pos_themes : l.neg_themes) },
        ]);
        const rows = [
            ...topList.slice(0, perSide).map(l => buildRow(l, 'top')),
            ...bottomList.slice(0, perSide).map(l => buildRow(l, 'bottom')),
        ];
        const columns = [
            { label: 'Club', w: 4.3, align: 'left' },
            { label: 'Rating', w: 1.2, align: 'center' },
            { label: 'Reviews', w: 1.2, align: 'center' },
            { label: 'Themes', w: 4.68, align: 'left' },
        ];
        return [{
            type: 'table',
            title: `Top ${Math.min(topList.length, perSide)} and Bottom ${Math.min(bottomList.length, perSide)} Locations by Rating`,
            chartSubtitle: `${topic.name} – Location Extremes`,
            columns,
            rows,
            note: `Among ${le.qualifying_locations != null ? Number(le.qualifying_locations).toLocaleString() : 'N/A'} locations with at least ${le.min_reviews ?? 'N/A'} reviews. Green rows rank highest, red rows lowest.`,
            methodologyFootnote: methodology?.location_ratings?.footnote,
        }];
    };

    // (c) Volume × NPS overlay — dual-axis Chart.js render (bars = monthly
    // volume, line = NPS with gaps where unrated) for the 1-2 highest-volume
    // channels, one data slide each.
    const generateVolumeNpsSlides = async (topic, companyName) => {
        const extras = await fetchDeckExtras(topic.id, companyName);
        const vn = extras?.volume_nps_series;
        if (!vn || vn.error || !vn.series || typeof vn.series !== 'object') {
            if (vn?.error) console.warn('[Report][Extras] volume_nps_series unavailable:', vn.error);
            return [];
        }
        const channels = Object.entries(vn.series)
            .map(([channel, rows]) => ({
                channel,
                rows: Array.isArray(rows) ? rows : [],
                total: (Array.isArray(rows) ? rows : []).reduce((s, r) => s + (Number(r?.volume) || 0), 0),
            }))
            .filter(c => c.rows.length > 0 && c.total > 0)
            .sort((a, b) => b.total - a.total)
            .slice(0, 2);
        if (channels.length === 0) return [];
        const methodology = await getMethodology(authValueRef.current?.flaskServerAddress);

        const slides = [];
        for (const ch of channels) {
            const config = {
                type: 'bar',
                data: {
                    labels: ch.rows.map(r => r.month),
                    datasets: [
                        {
                            type: 'line', label: 'NPS',
                            data: ch.rows.map(r => (r.nps == null ? null : r.nps)),
                            borderColor: '#E63946', backgroundColor: '#E63946',
                            borderWidth: 2.5, pointRadius: 3, spanGaps: false,
                            yAxisID: 'yNps', order: 1,
                        },
                        {
                            type: 'bar', label: 'Monthly volume',
                            data: ch.rows.map(r => Number(r.volume) || 0),
                            backgroundColor: 'rgba(37,99,235,0.55)',
                            borderRadius: 3, yAxisID: 'yVol', order: 2,
                        },
                    ],
                },
                options: {
                    scales: {
                        x: {
                            grid: { display: false },
                            ticks: { maxTicksLimit: 14, font: { size: 15 }, color: '#6B7280', maxRotation: 45 },
                        },
                        yVol: {
                            position: 'left', grid: { color: '#F3F4F6' },
                            ticks: { font: { size: 15 }, color: '#6B7280' },
                            title: { display: true, text: 'Posts / reviews per month', font: { size: 16 }, color: '#9CA3AF' },
                        },
                        yNps: {
                            position: 'right', grid: { display: false },
                            ticks: { font: { size: 15 }, color: '#6B7280' },
                            title: { display: true, text: 'NPS', font: { size: 16 }, color: '#9CA3AF' },
                        },
                    },
                    plugins: {
                        legend: { position: 'top', labels: { font: { size: 15 }, usePointStyle: true, padding: 16 } },
                    },
                },
            };
            let img = null;
            try {
                img = renderChartToBase64(config, 1400, 800);
            } catch (e) {
                console.warn(`[Report][Extras] volume×NPS chart render failed (${ch.channel}):`, e);
                continue;
            }
            const ratedMonths = ch.rows.filter(r => r.nps != null);
            const latestRated = ratedMonths[ratedMonths.length - 1];
            const bullets = [
                `${ch.total.toLocaleString()} posts across ${ch.rows.length} months on ${titleCase(ch.channel)}.`,
                `NPS is available for ${ratedMonths.length} of ${ch.rows.length} months; gaps mark months without rated posts.`,
            ];
            if (latestRated) {
                bullets.push(`Most recent rated month (${latestRated.month}): NPS ${Math.round(latestRated.nps)} on ${latestRated.n_rated != null ? Number(latestRated.n_rated).toLocaleString() : 'N/A'} rated posts.`);
            }
            slides.push({
                type: 'data',
                title: `${titleCase(ch.channel)}: Monthly Volume and NPS`,
                chartSubtitle: `${topic.name} – Volume × NPS Overlay (${titleCase(ch.channel)})`,
                imageBase64: img,
                summaryBullets: bullets,
                methodologyFootnote: methodology?.post_volume?.footnote,
            });
        }
        return slides;
    };

    // ------------------------------------------------------------------
    // Capture the REAL Daily-Tracking section component (Post Likes /
    // Leaver-Joiner) off-screen. The component is handed a single-topic map so
    // it auto-selects the slide's topic, fetches its own data, and renders the
    // genuine frontend chart. We capture the FULL component (header + chart),
    // not a chart-only crop — a chart-only crop comes out too wide; the full
    // component matches the reference-deck proportions (~1.6-1.8 aspect).
    // Returns base64, or null on failure so the caller can fall back.
    // ------------------------------------------------------------------
    const captureLiveComponent = async (kind, topic, companyName, opts = {}) => {
        if (!topic?.id) return null;
        const singleTopicMap = { [topic.id]: { ...topic } };
        let inner, readySelector, cropSelector;
        if (kind === 'postLikes') {
            inner = <PostLikesAnalytics topicMap={singleTopicMap} companyName={companyName} autoSearch
                initialStartDate={opts.startDate} initialEndDate={opts.endDate} initialBucket={opts.bucket} />;
            readySelector = '.recharts-wrapper';
        } else if (kind === 'leaverJoiner') {
            // Capture the real "Net Adds and Losses" tab chart (LeaverJoinerPlot),
            // scoped to this topic. The generator hands over the rows it already
            // fetched — topicMap only carries leaverJoiner assessments if the
            // user opened that tab this session, and gating on topicMap alone
            // made this capture silently fail every time, shipping the restyled
            // inline fallback chart instead of the page's own render.
            const ljAssessments = (opts.ljAssessments && opts.ljAssessments.length > 0)
                ? opts.ljAssessments
                : (topicMap[topic.id]?.assessments?.leaverJoiner || []);
            if (ljAssessments.length === 0) return null; // no data -> fall back to inline chart
            inner = (
                <LeaverJoinerPlot
                    posts={allPosts}
                    stat_name="leaver_joiner"
                    product_name={companyName}
                    topics={[topicMap[topic.id] || topic]}
                    assessmentData={ljAssessments}
                />
            );
            readySelector = 'canvas';
            // Crop to the chart canvas itself: the slide then shows exactly the
            // pixels the page renders (same colors, legend, axes) with the
            // product selector, time-range buttons, and switching-reasons
            // histogram never in frame.
            cropSelector = 'canvas';
        } else if (kind === 'llmRecommendations' || kind === 'llmCompetitive') {
            // Capture the real LLM Recommendations pages. The single-topic map
            // makes the page's ProductSelector auto-select this topic; the page
            // then fetches its own data (competitors come off the topic object).
            // Interactive chrome — selector/toggle row, prompt movers, and the
            // explanatory footnote — is hidden with capture-scoped CSS so the
            // image is just the chart plus the metric/brand cards.
            inner = (
                <>
                    <style>{`
                        .rg-live-capture .controls-section,
                        .rg-live-capture .llm-movers-section,
                        .rg-live-capture .llm-metric-note { display: none !important; }
                    `}</style>
                    {kind === 'llmRecommendations' ? (
                        <LLMRecommendationsPage
                            topicMap={singleTopicMap}
                            initialTimeRange={opts.llmTimeRange}
                            initialShowCompetitors
                        />
                    ) : (
                        <LLMCompetitiveComparisonPage
                            topicMap={singleTopicMap}
                            initialTimeRange={opts.llmTimeRange}
                        />
                    )}
                </>
            );
            readySelector = 'canvas';
        } else if (kind === 'locationMap') {
            // Capture the Location Insights tab reduced to its map: the
            // single-topic map auto-selects, the component fetches its own
            // analytics, and capture-scoped CSS strips everything interactive
            // (all MUI controls), the sections other slides already cover
            // (heat chart, movers, regions), and the KPI tiles the bullet
            // column restates — leaving the diverging legend plus the map.
            inner = (
                <>
                    <style>{`
                        .rg-live-capture .li-controls,
                        .rg-live-capture .li-kpi-row,
                        .rg-live-capture .li-source-note,
                        .rg-live-capture .li-map-header,
                        .rg-live-capture .li-regions-section,
                        .rg-live-capture .li-momentum-section,
                        .rg-live-capture .li-heat-section,
                        .rg-live-capture .MuiFormControl-root,
                        .rg-live-capture .MuiToggleButtonGroup-root,
                        .rg-live-capture .MuiChip-root,
                        .rg-live-capture button { display: none !important; }
                    `}</style>
                    <LocationInsights
                        topicMap={singleTopicMap}
                        companyName={companyName}
                        initialRange={opts.locationMonths}
                    />
                </>
            );
            // A store bubble inside the map card is the ready signal — scoped
            // so the MUI loading spinner (also an svg circle) can't match.
            readySelector = '.li-map-section circle';
        } else {
            return null;
        }
        // Width sets the capture's font-to-chart proportion: narrower CSS
        // width means the page's fixed-px text lands larger once the image is
        // scaled onto the slide (dropped from 1180 after readability feedback).
        const element = (
            <div className="rg-live-capture" style={{ width: 1000, background: '#fff' }}>
                {inner}
            </div>
        );
        try {
            // delay covers the component's async data fetch; readySelector
            // confirms the chart rendered before we capture the whole node.
            const url = await captureOffScreen(element, {
                width: 1000, height: 900, delay: 7000,
                authValue: authValueRef.current,
                cropSelector: cropSelector || '.rg-live-capture',
                readySelector, readyCount: 1,
            });
            return url && url.length > 1000 ? toBase64(url) : null;
        } catch (e) {
            console.warn(`[Report] live ${kind} capture failed:`, e?.message || e);
            return null;
        }
    };

    // Capture the real SentimentAnalysisChart for an NPS / sentiment slide —
    // the area chart PLUS the metric boxes underneath (NPS Score / Peak / Low /
    // Posts Analyzed), exactly as the Sentiment Breakdown page shows them.
    // `hideDetails` drops the snapshot card + posts list so the capture is just
    // chart + boxes. Returns base64, or null so the caller can fall back.
    const captureSentimentChart = async (topic, type) => {
        if (!topic?.id) return null;
        const mc = modeConfigRef.current;
        // Capture the KPI values the chart itself displays, so the exec
        // summary / banner / AI prompt all surface the SAME numbers as the
        // on-slide boxes (no parallel computation).
        let kpis = null;
        const element = (
            <div className="rg-live-capture" style={{ width: 1180, background: '#fff' }}>
                <SentimentAnalysisChart
                    topicId={topic.id}
                    type={type}
                    productName={topic.name}
                    hideHeader
                    hideDetails
                    timeRange={mc.sentimentDisplayMonths || 12}
                    onKpiUpdate={(k) => { kpis = k; }}
                />
            </div>
        );
        try {
            const url = await captureOffScreen(element, {
                width: 1220, height: 1150, delay: 8000,
                authValue: authValueRef.current,
                cropSelector: '.rg-live-capture',
                readySelector: '.recharts-wrapper', readyCount: 1,
            });
            if (!(url && url.length > 1000)) return null;
            return { base64: toBase64(url), kpis };
        } catch (e) {
            console.warn(`[Report] sentiment chart capture failed (${type}):`, e?.message || e);
            return null;
        }
    };

    // Capture a grid of per-source Post Volume charts as a single image. Each
    // cell renders the real PostAnalytics component filtered to one source,
    // weekly, trimmed to where that source's data coverage stabilises.
    // `cells` = [{ source, startDate }], max 6.
    const captureSourceGrid = async (topic, companyName, cells) => {
        if (!topic?.id || !cells || cells.length === 0) return null;
        const singleTopicMap = { [topic.id]: { ...topic } };
        const todayStr = new Date().toISOString().split('T')[0];
        // Column count chosen so the grid's overall shape stays roughly
        // rectangular (e.g. 3 charts -> 2x2 with one gap, not a thin 1x3 strip).
        const cols = Math.min(3, Math.max(1, Math.ceil(Math.sqrt(cells.length))));
        const rows = Math.ceil(cells.length / cols);
        // Smaller CSS cells render each chart's fixed-px text proportionally
        // larger on the slide (readability feedback; was 1180×780).
        const CELL_W = 1000, CELL_H = 660, GAP = 20;
        const gridW = cols * CELL_W + (cols + 1) * GAP;
        const gridH = rows * CELL_H + (rows + 1) * GAP;
        const element = (
            <div className="rg-source-grid" style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${cols}, ${CELL_W}px)`,
                gap: `${GAP}px`, padding: `${GAP}px`, background: '#fff',
                width: gridW, boxSizing: 'border-box',
            }}>
                {cells.map((c, i) => (
                    <div key={i} style={{ width: CELL_W }}>
                        <PostAnalytics
                            topicMap={singleTopicMap}
                            companyName={companyName}
                            autoSearch
                            initialSource={c.source}
                            initialStartDate={c.startDate}
                            initialEndDate={todayStr}
                            initialBucket="week"
                            initialRollingDays={30}
                        />
                    </div>
                ))}
            </div>
        );
        try {
            const url = await captureOffScreen(element, {
                width: gridW, height: gridH, delay: 12000,
                authValue: authValueRef.current,
                // Capture the grid node itself at natural size (no stretch).
                cropSelector: '.rg-source-grid',
                readySelector: '.recharts-wrapper', readyCount: cells.length,
            });
            return url && url.length > 1000 ? toBase64(url) : null;
        } catch (e) {
            console.warn('[Report] source-grid capture failed:', e?.message || e);
            return null;
        }
    };

    // ------------------------------------------------------------------
    // Chart capture — renders Recharts off-screen via toPng (primary),
    // falls back to Chart.js direct canvas if capture fails.
    // ------------------------------------------------------------------
    const captureChartImage = async (chartType, props) => {
        // Per-type aspect ratios calibrated to the reference deck so charts are
        // captured in natural proportions rather than stretched wide.
        const ASPECTS = {
            nps: 1.46, sentiment: 1.46, pricing: 1.46,
            llmRecommendations: 1.46, llmCompetitive: 1.46,
            postVolume: 1.6, postLikes: 1.6, leaverJoiner: 1.77,
        };
        const CW = 1000;
        const CH = Math.round(CW / (ASPECTS[chartType] || 1.6));

        try {
            // Build Recharts element
            const element = buildRechartsElement(chartType, props, CW, CH);
            if (element) {
                try {
                    const url = await captureOffScreen(element, { width: CW, height: CH, delay: 2000, authValue: authValueRef.current });
                    if (url && url.length > 1000) {
                        return toBase64(url);
                    }
                } catch (e) {
                    console.warn(`[Report] Recharts capture failed for ${chartType}, using Chart.js fallback:`, e.message);
                }
            }

            // Fallback: Chart.js direct canvas rendering
            const config = buildChartJsFallbackConfig(chartType, props);
            if (config) {
                console.log(`[Report] Using Chart.js fallback for ${chartType}`);
                return renderChartToBase64(config);
            }

            return null;
        } catch (err) {
            console.error(`Failed to capture chart ${chartType}:`, err);
            return null;
        }
    };

    // Build a Recharts JSX element for off-screen rendering
    const buildRechartsElement = (chartType, props, CW, CH) => {
        switch (chartType) {
            case 'nps':
                return (
                    <div style={{ width: CW, height: CH, padding: 20, boxSizing: 'border-box', background: '#fff' }}>
                        <NPSChart chartData={props} showConfidenceIntervals={false} />
                    </div>
                );

            case 'sentiment':
                // Render the ACTUAL PricingChart component — exact same chart as the frontend
                return (
                    <div style={{ width: CW, height: CH, background: '#fff' }}>
                        <PricingChart
                            data={props.data}
                            showTwoYears={props.data.length > 200}
                        />
                    </div>
                );

            case 'pricing':
                return (
                    <div style={{ width: CW, height: CH, background: '#fff' }}>
                        <RBarChart width={CW} height={CH} data={props.categories}
                            layout="vertical" margin={{ top: 20, right: 60, bottom: 20, left: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} />
                            <XAxis type="number" unit="%" domain={[0, 'auto']}
                                tick={{ fontSize: 11, fill: '#6B7280' }}
                                axisLine={{ stroke: '#E5E7EB' }} tickLine={{ stroke: '#E5E7EB' }} />
                            <YAxis type="category" dataKey="name" width={170}
                                tick={{ fontSize: 12, fill: '#374151', fontWeight: 500 }}
                                axisLine={false} tickLine={false} />
                            <RTooltip formatter={(v) => `${v}%`}
                                contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB' }} />
                            <RBar dataKey="value" barSize={28} radius={[0, 4, 4, 0]}>
                                {props.categories.map((entry, i) => (
                                    <Cell key={i} fill={entry.fill} />
                                ))}
                                <LabelList dataKey="value" position="right"
                                    formatter={(v) => `${v}%`}
                                    style={{ fontSize: 11, fill: '#374151', fontWeight: 600 }} />
                            </RBar>
                        </RBarChart>
                    </div>
                );

            case 'leaverJoiner':
                return (
                    <div style={{ width: CW, height: CH, background: '#fff' }}>
                        <ComposedChart width={CW} height={CH} data={props.data}
                            margin={{ top: 20, right: 30, bottom: 30, left: 30 }}
                            stackOffset="sign">
                            <defs>
                                <linearGradient id="gSJ" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#059669" stopOpacity={0.95}/>
                                    <stop offset="100%" stopColor="#059669" stopOpacity={0.7}/>
                                </linearGradient>
                                <linearGradient id="gPJ" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#34D399" stopOpacity={0.95}/>
                                    <stop offset="100%" stopColor="#34D399" stopOpacity={0.7}/>
                                </linearGradient>
                                <linearGradient id="gPL" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#F87171" stopOpacity={0.7}/>
                                    <stop offset="100%" stopColor="#F87171" stopOpacity={0.95}/>
                                </linearGradient>
                                <linearGradient id="gSL" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#DC2626" stopOpacity={0.7}/>
                                    <stop offset="100%" stopColor="#DC2626" stopOpacity={0.95}/>
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" vertical={false} />
                            <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#6B7280', fontFamily: 'Aptos, sans-serif' }}
                                interval={Math.max(1, Math.floor(props.data.length / 12))}
                                angle={-45} textAnchor="end" height={50}
                                axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
                            <YAxis tick={{ fontSize: 11, fill: '#6B7280', fontFamily: 'Aptos, sans-serif' }}
                                axisLine={false} tickLine={false} />
                            <RTooltip contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontFamily: 'Aptos, sans-serif' }} />
                            <RLegend wrapperStyle={{ fontSize: 11, paddingTop: 8, fontFamily: 'Aptos, sans-serif' }}
                                iconType="square" iconSize={10} />
                            <RBar dataKey="strongJoiners" stackId="stack" fill="url(#gSJ)" name="Strong Joiners" radius={[2, 2, 0, 0]} />
                            <RBar dataKey="possibleJoiners" stackId="stack" fill="url(#gPJ)" name="Possible Joiners" />
                            <RBar dataKey="possibleLeavers" stackId="stack" fill="url(#gPL)" name="Possible Leavers" />
                            <RBar dataKey="strongLeavers" stackId="stack" fill="url(#gSL)" name="Strong Leavers" radius={[0, 0, 2, 2]} />
                            <RLine type="monotone" dataKey="net" stroke="#1F2937" strokeWidth={2.5}
                                dot={false} name="Net" strokeDasharray="6 3" />
                        </ComposedChart>
                    </div>
                );

            case 'llmRecommendations':
                // Inline fallback when the live LLM Recommendations page capture
                // fails — same NPSChart the page renders, prebuilt data.
                return (
                    <div style={{ width: CW, height: CH, padding: 20, boxSizing: 'border-box', background: '#fff' }}>
                        <NPSChart chartData={props.chartData} showConfidenceIntervals={false}
                            yAxisTitle="LLM Recommendation Score (0-100)" />
                    </div>
                );

            case 'llmCompetitive':
                return (
                    <div style={{ width: CW, height: CH, padding: 20, boxSizing: 'border-box', background: '#fff' }}>
                        <LLMPositionChart chartData={props.chartData}
                            yAxisTitle="Average Position (absences penalized)" />
                    </div>
                );

            case 'postVolume':
                return (
                    <div style={{ width: CW, height: CH, background: '#fff' }}>
                        <RBarChart width={CW} height={CH} data={props.data}
                            margin={{ top: 20, right: 30, bottom: 40, left: 30 }}>
                            <defs>
                                <linearGradient id="gVol" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#2563EB" stopOpacity={0.95}/>
                                    <stop offset="50%" stopColor="#6C5CE7" stopOpacity={0.8}/>
                                    <stop offset="100%" stopColor="#6C5CE7" stopOpacity={0.35}/>
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" vertical={false} />
                            <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#6B7280', fontFamily: 'Aptos, sans-serif' }}
                                interval={Math.max(1, Math.floor(props.data.length / 12))}
                                angle={-45} textAnchor="end" height={50}
                                axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
                            <YAxis tick={{ fontSize: 11, fill: '#6B7280', fontFamily: 'Aptos, sans-serif' }}
                                axisLine={false} tickLine={false}
                                label={{ value: 'Posts', angle: -90, position: 'insideLeft',
                                    style: { fontSize: 12, fill: '#9CA3AF', fontFamily: 'Aptos, sans-serif' } }} />
                            <RTooltip contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontFamily: 'Aptos, sans-serif' }}
                                cursor={{ fill: 'rgba(37,99,235,0.06)' }} />
                            <RBar dataKey="Posts" fill="url(#gVol)" radius={[4, 4, 0, 0]} />
                        </RBarChart>
                    </div>
                );

            case 'postLikes':
                return (
                    <div style={{ width: CW, height: CH, background: '#fff' }}>
                        <RBarChart width={CW} height={CH} data={props.data}
                            margin={{ top: 20, right: 30, bottom: 40, left: 30 }}>
                            <defs>
                                <linearGradient id="gLike" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#059669" stopOpacity={0.95}/>
                                    <stop offset="50%" stopColor="#00B894" stopOpacity={0.8}/>
                                    <stop offset="100%" stopColor="#00B894" stopOpacity={0.35}/>
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" vertical={false} />
                            <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#6B7280', fontFamily: 'Aptos, sans-serif' }}
                                interval={Math.max(1, Math.floor(props.data.length / 12))}
                                angle={-45} textAnchor="end" height={50}
                                axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
                            <YAxis tick={{ fontSize: 11, fill: '#6B7280', fontFamily: 'Aptos, sans-serif' }}
                                axisLine={false} tickLine={false}
                                label={{ value: 'Likes', angle: -90, position: 'insideLeft',
                                    style: { fontSize: 12, fill: '#9CA3AF', fontFamily: 'Aptos, sans-serif' } }} />
                            <RTooltip contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontFamily: 'Aptos, sans-serif' }}
                                cursor={{ fill: 'rgba(5,150,105,0.06)' }} />
                            <RBar dataKey="Likes" fill="url(#gLike)" radius={[4, 4, 0, 0]} />
                        </RBarChart>
                    </div>
                );

            default:
                return null;
        }
    };

    // Chart.js fallback config builder (used if Recharts capture fails)
    const buildChartJsFallbackConfig = (chartType, props) => {
        switch (chartType) {
            case 'sentiment':
                return buildSentimentConfig(props.data);
            case 'pricing':
                return buildPricingConfig(props.categories);
            case 'leaverJoiner':
                return buildLeaverJoinerConfig(props.data);
            case 'postVolume':
                return buildBarConfig(props.data, 'Posts', 'rgba(108,92,231,0.9)', 'rgba(108,92,231,0.3)', 'Posts');
            case 'postLikes':
                return buildBarConfig(props.data, 'Likes', 'rgba(0,184,148,0.9)', 'rgba(0,184,148,0.3)', 'Likes');
            default:
                return null;
        }
    };

    // Keep portal visible during generation
    // Keep portal visible during generation even if parent tries to close/unmount
    if (!isOpen && !generating && !_activeGeneration) return null;

    const progressPercent = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

    const totalSlides = getTotalSlides();

    return ReactDOM.createPortal(
        <div className="report-generator-overlay" onClick={(e) => { if (e.target === e.currentTarget && !generating) onClose(); }}>
            <div className={`report-generator-modal${step === 1 && !generating ? ' report-generator-modal--wide' : ''}`}>
                {/* Header */}
                <div className="rg-header">
                    <div>
                        <h2 className="rg-title">Generate Report</h2>
                        {!generating && step === 1 && (
                            <p className="rg-subtitle">Check intersections to add slides</p>
                        )}
                        {!generating && step === 3 && (
                            <p className="rg-subtitle">Step 3 of 3 — Additional questions (optional)</p>
                        )}
                    </div>
                    {!generating && (
                        <button className="rg-close" onClick={onClose}><X size={18} /></button>
                    )}
                </div>

                <div className="rg-body">
                    {generating ? (
                        <div className="report-progress">
                            <div style={{ width: '100%' }}>
                                <div className="report-progress-text">{progress.label}</div>
                                <div className="report-progress-bar" style={{ marginTop: 10 }}>
                                    <div className="report-progress-fill" style={{ width: `${progressPercent}%` }} />
                                </div>
                                <div className="report-progress-text" style={{ marginTop: 10, textAlign: 'right' }}>
                                    {progress.current} / {progress.total} sections
                                </div>
                            </div>
                        </div>
                    ) : step === 1 ? (
                        <>
                            {/* Top bar: Exec Summary + Presets */}
                            <div className="rg-toolbar">
                                <button
                                    className="rg-exec-toggle"
                                    onClick={() => setIncludeExecSummary(prev => !prev)}
                                >
                                    <span className={`rg-check${includeExecSummary ? ' rg-check--on' : ''}`}>
                                        {includeExecSummary && <Check size={14} strokeWidth={3} />}
                                    </span>
                                    <span className="rg-exec-label">Executive Summary</span>
                                </button>

                                <div className="rg-mode-selector">
                                    {Object.entries(REPORT_MODES).map(([key, mode]) => (
                                        <button
                                            key={key}
                                            className={`rg-mode-btn${reportMode === key ? ' rg-mode-btn--active' : ''}`}
                                            onClick={() => setReportMode(key)}
                                            title={mode.description}
                                        >
                                            {mode.label}
                                        </button>
                                    ))}
                                </div>

                                <div className="rg-preset-controls">
                                    <button
                                        className={`rg-comp-toggle${includeCompetitors ? ' rg-comp-toggle--active' : ''}`}
                                        onClick={toggleCompetitors}
                                        title={includeCompetitors ? 'Remove competitor topics' : 'Add competitor topics to the matrix'}
                                    >
                                        <Users size={14} />
                                        {includeCompetitors ? 'Competitors On' : 'Add Competitors'}
                                    </button>
                                    <select
                                        className="rg-preset-select"
                                        value=""
                                        onChange={(e) => {
                                            const p = presets.find(p => p.name === e.target.value);
                                            if (p) loadPreset(p);
                                        }}
                                    >
                                        <option value="">Load Preset...</option>
                                        {presets.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
                                    </select>
                                    <button className="rg-save-btn" onClick={() => setShowSaveDialog(true)}>
                                        <Save size={14} /> Save
                                    </button>
                                </div>
                            </div>

                            {/* Save preset dialog */}
                            {showSaveDialog && (
                                <div className="rg-save-dialog">
                                    <input
                                        className="rg-save-input"
                                        type="text"
                                        value={presetName}
                                        onChange={(e) => setPresetName(e.target.value)}
                                        placeholder="Preset name..."
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') savePreset();
                                            if (e.key === 'Escape') { setShowSaveDialog(false); setPresetName(''); }
                                        }}
                                        autoFocus
                                    />
                                    <button className="rg-save-confirm" onClick={savePreset} disabled={!presetName.trim()}>Save</button>
                                    <button className="rg-save-cancel" onClick={() => { setShowSaveDialog(false); setPresetName(''); }}>Cancel</button>
                                </div>
                            )}

                            {/* Matrix table */}
                            <div className="rg-matrix-wrap">
                                <table className="rg-matrix">
                                    <thead>
                                        <tr>
                                            <th className="rg-matrix-corner">Topics</th>
                                            {matrixColumns.map(col => (
                                                <th key={col.id} className="rg-matrix-col-header">
                                                    <div className="rg-col-label">{col.label}</div>
                                                    <div className="rg-col-count">{getColTotal(col.id)}/{activeTopics.length}</div>
                                                    <button
                                                        className="rg-col-toggle"
                                                        onClick={() => isColFull(col.id) ? deselectAllColumn(col.id) : selectAllColumn(col.id)}
                                                    >
                                                        {isColFull(col.id) ? '\u2013\u2013' : 'All'}
                                                    </button>
                                                </th>
                                            ))}
                                            <th className="rg-matrix-col-header rg-matrix-total-header">Slides</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {activeTopics.map(topic => {
                                            const isComp = topic.isCompetitor;
                                            const topicSentimentTypes = topicTypes[topic.id] || ['NPS', 'pricing'];

                                            return (
                                                <tr key={topic.id} className={isComp ? 'rg-matrix-row--comp' : ''}>
                                                    <td className="rg-matrix-row-header">
                                                        <div className="rg-row-info">
                                                            <span className="rg-row-name">
                                                                {isComp && <span className="rg-comp-badge">COMP</span>}
                                                                {topic.name || topic.topic}
                                                            </span>
                                                            <span className="rg-row-count">{getTopicTotal(topic.id)}/{matrixColumns.length}</span>
                                                        </div>
                                                        <button
                                                            className="rg-row-toggle"
                                                            onClick={() => isRowFull(topic.id) ? deselectAllRow(topic.id) : selectAllRow(topic.id)}
                                                        >
                                                            {isRowFull(topic.id) ? '\u2013\u2013' : 'All'}
                                                        </button>
                                                    </td>
                                                    {matrixColumns.map(col => {
                                                        // Gray out sentiment cells where this topic doesn't have the type
                                                        const noData = col.isSentiment && !topicSentimentTypes.includes(col.assessmentType);

                                                        return (
                                                            <td key={col.id} className={`rg-matrix-cell${noData ? ' rg-matrix-cell--disabled' : ''}`}>
                                                                {noData ? (
                                                                    <span className="rg-check rg-check--disabled" title="No data available">&mdash;</span>
                                                                ) : (
                                                                    <button className="rg-cell-btn" onClick={() => toggleCell(topic.id, col.id)}>
                                                                        <span className={`rg-check${isSelected(topic.id, col.id) ? ' rg-check--on' : ''}`}>
                                                                            {isSelected(topic.id, col.id) && <Check size={14} strokeWidth={3} />}
                                                                        </span>
                                                                    </button>
                                                                )}
                                                            </td>
                                                        );
                                                    })}
                                                    <td className="rg-matrix-cell rg-matrix-total-cell">
                                                        {getTopicTotal(topic.id) * 2}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {typesLoading && <div className="rg-loading">Loading assessment types...</div>}
                        </>
                    ) : step === 2 ? (
                        /* Step 2: Arrange cards */
                        <>
                            <div className="report-arrange-header">
                                <span className="report-arrange-hint">Drag to reorder</span>
                                <div className="report-group-toggle">
                                    <span className="report-group-label">Group:</span>
                                    <button className={`report-group-btn${groupBy === 'type' ? ' report-group-btn--active' : ''}`} onClick={() => handleGroupByChange('type')}>By Type</button>
                                    <button className={`report-group-btn${groupBy === 'topic' ? ' report-group-btn--active' : ''}`} onClick={() => handleGroupByChange('topic')}>By Topic</button>
                                </div>
                            </div>
                            <div className="report-card-grid">
                                {slideCards.map((card, idx) => (
                                    <div key={card.id} className="report-card" draggable
                                        onDragStart={() => handleDragStart(idx)}
                                        onDragOver={(e) => handleDragOver(e, idx)}
                                        onDrop={handleDrop}
                                        style={{ borderTopColor: card.color }}
                                    >
                                        <button className="report-card-remove" onClick={() => removeCard(idx)}>&times;</button>
                                        <div className="report-card-label">{card.label}</div>
                                        {card.topicName && <div className="report-card-topic">{card.topicName}</div>}
                                    </div>
                                ))}
                            </div>
                            {slideCards.length === 0 && <div className="report-empty-state">No slides selected. Go back to add slides.</div>}
                        </>
                    ) : (
                        /* Step 3: Additional questions */
                        <>
                            <div className="rg-qa-header">
                                <div className="rg-qa-title">Additional Questions</div>
                                <div className="rg-qa-subtitle">
                                    Each question generates its own slide via the research agent.
                                    Leave blank to skip. Max {QA_QUESTION_CAP}.
                                </div>
                            </div>
                            <div className="rg-qa-topic-row">
                                <label className="rg-qa-topic-label" htmlFor="rg-qa-topic">Topic scope</label>
                                <select
                                    id="rg-qa-topic"
                                    className="rg-qa-topic-select"
                                    value={qaTopicId}
                                    onChange={(e) => setQaTopicId(e.target.value)}
                                >
                                    <option value="">Auto-detect (let agent choose)</option>
                                    {primaryTopics.map(t => (
                                        <option key={t.id} value={t.id}>{t.name || t.topic}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="rg-qa-list">
                                {qaQuestions.map((q, idx) => (
                                    <div key={idx} className="rg-qa-row">
                                        <span className="rg-qa-index">{idx + 1}</span>
                                        <textarea
                                            className="rg-qa-input"
                                            value={q}
                                            onChange={(e) => updateQuestion(idx, e.target.value)}
                                            placeholder="e.g. What pricing concerns have emerged this quarter?"
                                            rows={2}
                                        />
                                        <button
                                            className="rg-qa-remove"
                                            onClick={() => removeQuestionRow(idx)}
                                            title="Remove question"
                                            disabled={qaQuestions.length === 1 && !qaQuestions[0]}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <button
                                className="rg-qa-add"
                                onClick={addQuestionRow}
                                disabled={qaQuestions.length >= QA_QUESTION_CAP}
                            >
                                <Plus size={14} /> Add question
                            </button>
                            <div className="rg-qa-count">
                                {activeQuestions.length} question{activeQuestions.length !== 1 ? 's' : ''} queued
                                {qaQuestions.length >= QA_QUESTION_CAP && ' (cap reached)'}
                            </div>
                        </>
                    )}
                </div>

                {/* Non-blocking register-lint findings — the deck already
                    downloaded; these flag copy worth a manual pass. */}
                {lintWarnings.length > 0 && !generating && (
                    <div
                        className="rg-lint-warnings"
                        style={{
                            padding: '8px 16px', background: '#FFFBEB',
                            borderTop: '1px solid #F59E0B', color: '#92400E', fontSize: 12,
                        }}
                    >
                        {lintWarnings.map((w, i) => <div key={i}>{w}</div>)}
                    </div>
                )}

                {/* Footer */}
                <div className="rg-footer">
                    {generating ? (
                        <span className="rg-footer-label">Generating report... you can navigate away.</span>
                    ) : step === 1 ? (
                        <button className="rg-primary-btn" onClick={goToArrange} disabled={totalSlides === 0}>
                            Next: Arrange {totalSlides} Slides &rarr;
                        </button>
                    ) : step === 2 ? (
                        <>
                            <button className="rg-secondary-btn" onClick={() => setStep(1)}>&larr; Back</button>
                            <button className="rg-primary-btn" onClick={() => setStep(3)} disabled={slideCards.length === 0}>
                                Next: Add Questions &rarr;
                            </button>
                        </>
                    ) : (
                        <>
                            <button className="rg-secondary-btn" onClick={() => setStep(2)}>&larr; Back</button>
                            <button className="rg-primary-btn" onClick={generate} disabled={slideCards.length === 0 && activeQuestions.length === 0}>
                                Generate Report ({slideCards.length + activeQuestions.length} slide{(slideCards.length + activeQuestions.length) !== 1 ? 's' : ''})
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ReportGenerator;
